import React, { Suspense, useRef, useMemo, useEffect, useState, useCallback } from 'react';
import { FallbackOnError } from '../components/three/FallbackOnError';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Sky, Environment, PerspectiveCamera, useTexture, TransformControls, Text } from '@react-three/drei';

// Global flag to suppress click-to-move while dragging TransformControls
(window as any).__transformDragging = false;

// Perf overlay (?perf=1) — lazy so it never weighs the normal bundle
const PerfOverlay = React.lazy(() => import('r3f-perf').then((m) => ({ default: m.Perf })));
const SHOW_PERF = new URLSearchParams(window.location.search).has('perf');
// Explicit ?dpr= overrides adaptive resolution (testing knob)
const URL_DPR = parseFloat(new URLSearchParams(window.location.search).get('dpr') || '') || null;

/**
 * AdaptiveDpr — splat worlds are fill-rate-bound (overdraw of large
 * transparent quads scales with pixel count, not splat count), so render at
 * reduced resolution while the camera is moving — motion masks the softness —
 * and restore full resolution ~0.4s after it stops.
 */
const AdaptiveDpr: React.FC<{ idle: number; moving: number }> = ({ idle, moving }) => {
  const setDpr = useThree((s) => s.setDpr);
  const camera = useThree((s) => s.camera);
  const prevMatrix = useRef(new THREE.Matrix4());
  const idleSince = useRef(0);
  const current = useRef(0);

  useFrame(() => {
    const now = performance.now();
    const moved = !prevMatrix.current.equals(camera.matrixWorld);
    prevMatrix.current.copy(camera.matrixWorld);

    if (moved) {
      idleSince.current = now;
      if (current.current !== moving) {
        current.current = moving;
        setDpr(moving);
      }
    } else if (now - idleSince.current > 400 && current.current !== idle) {
      current.current = idle;
      setDpr(idle);
    }
  });

  return null;
};
import { XR, useXR, createXRStore } from '@react-three/xr';
import * as THREE from 'three';
import { WaterSurface, WaterDepthPass, WATER_PALETTES } from '../components/WaterMaterial';
import { pinWorldThumbnail } from '../utils/captureThumbnail';
import { FoliageLayer, scatterFoliage, makeTreeCanopy } from '../components/FoliageLayer';
import { TrunkLayer, TrunkInstance } from '../components/TrunkLayer';
import { FoliageBrushPanel } from '../components/FoliageBrushPanel';
import { instancesFromStrokes, packStroke, unpackStroke } from '../foliageStrokes';
import { DebugInspector } from '../debug/Inspector';

/** Renders every committed foliage stroke. Instances are derived, never stored. */
const PaintedFoliage: React.FC = () => {
  const strokes = useWorldStore((s) => s.foliageStrokes);
  const { foliage, trunks } = useMemo(() => instancesFromStrokes(strokes), [strokes]);
  if (!foliage.length && !trunks.length) return null;
  return (
    <>
      <TrunkLayer instances={trunks} />
      {foliage.length > 0 && <FoliageLayer instances={foliage} />}
    </>
  );
};

// Preview harness for the foliage prototype: /world/<id>?foliage=400
// Not part of any world's data — purely for judging the look before the
// scatter brush and persistence exist.
const FoliagePreview: React.FC = () => {
  const n = parseInt(new URLSearchParams(window.location.search).get('foliage') || '0', 10);
  const { foliage, trunks } = useMemo(() => {
    if (n <= 0) return { foliage: [], trunks: [] as TrunkInstance[] };
    const ground = scatterFoliage(n, 26);
    // A handful of trees, ringed out from the middle so the spawn stays clear.
    const trees: TrunkInstance[] = [];
    const canopies = [];
    const count = Math.max(4, Math.round(n / 90));
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 + 0.7;
      const r = 7 + ((i * 37) % 13);
      const scale = 1.0 + ((i * 17) % 7) / 10;
      const p: [number, number, number] = [Math.cos(a) * r, 0, Math.sin(a) * r];
      trees.push({ position: p, scale, rotation: (i * 1.3) % (Math.PI * 2) });
      canopies.push(...makeTreeCanopy(p, scale, 1000 + i));
    }
    return { foliage: [...ground, ...canopies], trunks: trees };
  }, [n]);
  if (!foliage.length) return null;
  return (
    <>
      <TrunkLayer instances={trunks} />
      <FoliageLayer instances={foliage} />
    </>
  );
};

const isWater = (preset: string) => preset in WATER_PALETTES;
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { initKTX2Loader } from '../utils/loaderConfig';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ChatOverlay from '../components/ChatOverlay';
import S3Upload from '../components/S3Upload';
import ContentBrowser from '../components/ContentBrowser';
import ObjectLibraryBrowser from '../components/ObjectLibraryBrowser';
import CopilotPanel from '../components/CopilotPanel';
import SkyboxManager from '../components/SkyboxManager';
import AvatarWithVoiceState from '../components/AvatarWithVoiceState';
import ObjectGenerator from '../components/ObjectGenerator';
import ScreenShare from '../components/ScreenShare';
import PanelUrlPicker from '../components/PanelUrlPicker';
import PanelPlacement from '../components/PanelPlacement';
import SceneMediaPanel from '../components/SceneMediaPanel';
import MediaPanelDialogs from '../components/MediaPanelDialogs';
import { initRoomScreens } from '../services/roomScreens';
import MediaPanelActions from '../components/MediaPanelActions';
import EnvironmentControls from '../components/EnvironmentControls';
import ObjectControls from '../components/ObjectControls';
import SimpleEnvironmentModel from '../components/SimpleEnvironmentModel';
import SceneObjectsList, { SceneObject } from '../components/SceneObjectsList';
import GroundControls from '../components/GroundControls';
import PrimitiveControls from '../components/PrimitiveControls';
import AtmosphereScene from '../components/AtmosphereScene';
import AtmosphereControls from '../components/AtmosphereControls';
import LightControls from '../components/LightControls';
import ContentDisplay from '../components/ContentDisplay';
import VRButtonComponent from '../components/VR/VRButton';
import VRScene from '../components/VR/VRScene';
import { turnAngle, sizeFactor, proportionalScale } from '../components/VR/build/vrBuildMath';
// VRBloom removed — toggling gl.xr.enabled during XR render loop causes black screen
import MultiplayerSync from '../components/MultiplayerSync';
import SafeR3F from '../components/SafeR3F';
import { PREFABS } from '../prefabs';
import { generateBuilding, BuildingKind } from '../buildings';
import { VENUES, venueGlbUrl } from '../venues';
import EmoteWheel from '../components/EmoteWheel';
import BuilderToolbar from '../components/BuilderToolbar';
import OnlineUsersBar from '../components/OnlineUsersBar';
import KnockNotification from '../components/KnockNotification';
import VRPanel from '../components/VR/VRPanel';
import VRPerformanceManager from '../components/VR/VRPerformanceManager';
import VROptimizer from '../components/VR/VROptimizer';
import VRFrameOptimizer from '../components/VR/VRFrameOptimizer';
import VRPreloader from '../components/VR/VRPreloader';
import { ResourceLogger } from '../components/Performance/ResourceLogger';
import VoiceChatWrapper from '../components/VoiceChatWrapper';
import RoomScreenDock from '../components/RoomScreenDock';
import AuthComponent from '../components/Auth';
import ScreenProp from '../components/ScreenProp';
import BrowserProp from '../components/BrowserProp';
import { getStoredUserName } from '../utils/nameGenerator';
import { supabase } from '../config/supabase';
import { getWorldAccessState } from '../services/worldJoin';
import { MessageSquare, Share2, Upload, Globe, Layers, User, Image, Eye, FolderOpen, Cloud, ZoomIn, ZoomOut, LogOut, Save, AlertCircle, X, ChevronUp, ChevronDown, Lock, GlobeIcon, Plus, Settings, Users, LayoutGrid, Box, Sparkles, Wand2, Mic, MicOff, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Video, UserRound, Volume2, MapPin, Camera, DraftingCompass, CircleDot, Gamepad2, Music, Speaker } from 'lucide-react';
import WorldPreloader from '../components/WorldPreloader';
import VoiceButtonOverlay from '../components/VoiceButtonOverlay';
import AvatarCreateModal from '../components/avatar-create/AvatarCreateModal';
import NpcCreator from '../components/npc/NpcCreator';
import NpcAvatar from '../components/npc/NpcAvatar';
import { watchNpcBuild } from '../services/npcBuild';
import type { NpcConfig } from '../config/npc';
import { useWorldStore, DEFAULT_AVATAR_URL, PrimitiveItem, PrimitiveShape, LightItem, PlacementSpec, ScreenPropItem } from '../stores/worldStore';
import { parseWorldBounds } from '../utils/worldBounds';
import { parseWorldMusic, DEFAULT_SPEAKER } from '../services/music/musicTypes';
import { SpeakerModel3D, SPK_W, SPK_H, SPK_D, SPK_REST_Y } from '../components/music/SpeakerModel3D';
import { BuilderMusicButton } from '../components/music/MusicHudButton';
import { environmentFit } from '../services/libraryCatalog';
import { LockedFlash, LockedNotice } from '../components/LockedFlash';
import { WallDesigner, WallDesignBridge } from '../components/WallDesigner';
import { PortalVisual, PortalTraveler, PORTAL_W, PORTAL_H, newPortalLink, portalActive, portalLabelOf, portalNeedingExit, startPlacePortalExit } from '../components/portals/portals';

/** Arm click-to-place for a primitive (same as the toolbar's Add Shape). `game` = which game a game table plays. */
function startPlacePrimitive(shape: PrimitiveShape, game?: string) {
  const s = useWorldStore.getState();
  if (game) armGamePlacement(game);
  s.setPlacementSpec({ kind: 'primitive', shape, ...(game ? { game } : {}) });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}

/** Arm click-to-place for a speaker (positional music). */
function startPlaceSpeaker() {
  const s = useWorldStore.getState();
  s.setPlacementSpec({ kind: 'primitive', shape: 'speaker' });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}

/** Arm click-to-place for a world-game piece (start / collectible / hunter / finish / scare zone) of the world's game. */
function startPlaceWorldGame(role: WorldGameRole) {
  const s = useWorldStore.getState();
  armWorldGamePlacement(role, defaultGameKey(s.primitives));
  s.setPlacementSpec({ kind: 'primitive', shape: 'worldgame' });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}

/** Arm click-to-place for an arcade scoreboard (default settings: every game, all time, this world, on a stand). */
function startPlaceLeaderboard() {
  const s = useWorldStore.getState();
  armBoardPlacement();
  s.setPlacementSpec({ kind: 'primitive', shape: 'leaderboard', board: armedBoardConfig() });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}

/** Arm click-to-place for an arcade cabinet playing `gameId` (arcade registry id). */
function startPlaceArcade(gameId: string) {
  const s = useWorldStore.getState();
  armArcadePlacement(gameId);
  s.setPlacementSpec({ kind: 'primitive', shape: 'arcade', arcadeGame: gameId });
  s.setPlacementRotY(0);
  s.setBuildTool('place');
}
import { BuilderGameTable } from '../components/games/GameTable3D';
import { GT_W, GT_H, GT_REST_Y } from '../components/games/tableLayout';
import { gameList, getGameDef, armGamePlacement, armedGame, DEFAULT_GAME } from '../components/games/registry';
import { ARC_W, ARC_H, ARC_D, ARC_REST_Y, armArcadePlacement, armedArcade, arcadeCabinetName, artDataUrl, useArcadeGames, armBoardPlacement, armedBoardConfig, boardBox, boardRestY } from '../components/games/arcade/arcadeShared';
import type { LeaderboardConfig } from '../stores/worldStore';
const BuilderLeaderboard = React.lazy(() => import('../components/games/arcade/Leaderboard3D').then((m) => ({ default: m.BuilderLeaderboard })));
const BuilderArcadeCabinet = React.lazy(() => import('../components/games/arcade/ArcadeCabinet3D').then((m) => ({ default: m.BuilderArcadeCabinet })));
// World games (lazy: the piece visuals load when a world has one or the builder places one).
const BuilderWorldGame = React.lazy(() => import('../components/games/worldgame/BuilderWorldGame'));
import { ROLE_BOX, ROLE_SCALE, ROLE_HINT, roleRestY, armWorldGamePlacement, armedWorldGame, defaultGameKey, newWorldGameConfig, worldGamePieceName } from '../components/games/worldgame/worldGameShared';
import type { WorldGameRole } from '../components/games/worldgame/worldGameTypes';
import { Flag as WgFlag, Ghost as WgGhost, Footprints as WgFootprints, Play as WgPlay, Candy as WgCandy, Zap as WgZap, PersonStanding as WgUser } from 'lucide-react';
const WORLD_GAME_MENU: { role: WorldGameRole; label: string; Icon: typeof WgPlay }[] = [
  { role: 'start', label: 'Start', Icon: WgPlay }, { role: 'collectible', label: 'Collectible', Icon: WgCandy }, { role: 'patrol', label: 'Hunter path', Icon: WgFootprints },
  { role: 'goal', label: 'Goal', Icon: WgFlag }, { role: 'scare', label: 'Scare zone', Icon: WgGhost },
  { role: 'powerup', label: 'Power-up', Icon: WgZap }, { role: 'figure', label: 'Cutout figure', Icon: WgUser },
];
// World music (lazy: the audio runtime, popover and playlist editor load on demand).
const MusicRuntime = React.lazy(() => import('../components/music/MusicRuntime'));
const MusicOverlay = React.lazy(() => import('../components/music/MusicOverlay'));
const MusicListenerFrame = React.lazy(() => import('../components/music/MusicListenerFrame'));
const WorldMusicPanel = React.lazy(() => import('../components/music/WorldMusicEditor'));
import { URLShortenerService } from '../services/urlShortener';
import { putObject } from '../utils/uploadToStorage';
import { getChannel, getPresenceKey, onChannelSubscribed } from '../services/realtimeManager';
import { broadcastObjectAdd as _broadcastAdd, broadcastObjectUpdate as _broadcastUpdate, broadcastObjectDelete as _broadcastDelete, broadcastObjectLock, broadcastObjectUnlock, broadcastObjectTransform, broadcastEmote } from '../services/worldBroadcast';
import { clearWorldHistory, redoWorld, undoWorld } from '../services/worldUndo';
import { initWorldSettingsSync } from '../services/worldSync';
import { getActiveAvatar, savePoseAndAnimations } from '../services/avatarService';
import { usePanelOpacity } from '../components/PanelOpacityToggle';
import { MarbleWorldPanel, MarbleWorldRenderer } from '../plugins/marble-worlds';
import type { MarbleWorld, IngestedAssets } from '../plugins/marble-worlds';
import { MARBLE_ENABLED, MARBLE_COMING_SOON_MESSAGE } from '../config/marble';
import MobileContentDisplay from '../components/MobileContentDisplay';
import './WorldBuilder.css';
import '../components/CreateWorldModal.css';

// ---- Error Boundary for mobile Canvas ----
class CanvasErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  componentDidCatch(error: Error) {
    console.error('[CanvasErrorBoundary]', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#0a0e1a', color: 'rgba(255,255,255,0.7)', padding: '32px', textAlign: 'center',
          flexDirection: 'column', gap: '16px',
        }}>
          <AlertCircle size={48} color="#00c2b3" />
          <h3 style={{ color: 'white', margin: 0, fontSize: '18px' }}>3D view unavailable</h3>
          <p style={{ margin: 0, fontSize: '14px' }}>Your device may not support WebGL rendering.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * SafeR3FComponent — wraps any R3F child component so that if it throws during
 * mount (useEffect / initial render), it silently unmounts instead of killing
 * the entire Canvas.  React error boundaries do NOT catch errors that happen
 * inside the R3F reconciler (useFrame, imperative three.js calls, etc.), so
 * this uses a try/render/catch pattern: it renders children only after a
 * successful probe render, and catches constructor/import-level crashes via
 * an inner error boundary that lives *inside* the Canvas fiber tree.
 */
class R3FErrorBoundary extends React.Component<
  { children: React.ReactNode; name?: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; name?: string }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.warn(`[R3FErrorBoundary:${this.props.name || 'unknown'}] Component crashed, hiding it:`, error.message);
  }
  render() {
    if (this.state.hasError) return null; // silently disappear inside Canvas
    return this.props.children;
  }
}

/**
 * KTX2Initializer — placed inside each <Canvas> to initialise the KTX2/Basis
 * transcoder once the WebGLRenderer is available.  Renders nothing.
 */
const KTX2Initializer: React.FC = () => {
  const { gl } = useThree();
  useEffect(() => {
    initKTX2Loader(gl);
  }, [gl]);
  return null;
};

/**
 * MobileSafeEnvironmentModel — wraps SimpleEnvironmentModel but catches the
 * useXR() hook crash on mobile (no XR store/session) by lazy-loading through
 * a dynamic import that avoids pulling in @react-three/xr on mobile.
 * Instead we render the GLB directly with useGLTF (no XR optimizations).
 */
const LazyMobileGLBModel = React.lazy(() => import('../components/MobileGLBModel'));
const MobileSafeEnvironmentModel: React.FC<{
  url: string;
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
}> = ({ url, position, scale, rotation }) => {
  return (
    <Suspense fallback={null}>
      <LazyMobileGLBModel url={url} position={position} scale={scale} rotation={rotation} />
    </Suspense>
  );
};

/** True inside the phone scene, which has no <XR> wrapper (useXR would throw there). */
const NoXRScene = React.createContext(false);
/** In a VR session? Safe in the phone scene too (always false there). */
function usePresenting() {
  const noXR = React.useContext(NoXRScene); // fixed for a component's lifetime
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return noXR ? false : useXR((s) => !!s.session);
}

// ---- Mobile camera modes ----
// Shared ref for mobile movement input (set by D-pad buttons)
const mobileMovementInput = { x: 0, z: 0 };

/** Virtual joystick — outputs analog x/z to mobileMovementInput. */
const MobileJoystick: React.FC = () => {
  const baseRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);
  const touchIdRef = useRef<number | null>(null);
  const RADIUS = 40; // max knob travel in px

  useEffect(() => {
    const base = baseRef.current;
    if (!base) return;

    const getCenter = () => {
      const rect = base.getBoundingClientRect();
      return { cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2 };
    };

    const update = (clientX: number, clientY: number) => {
      const { cx, cy } = getCenter();
      let dx = clientX - cx;
      let dy = clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > RADIUS) { dx = (dx / dist) * RADIUS; dy = (dy / dist) * RADIUS; }
      if (knobRef.current) {
        knobRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
      }
      // Normalize to -1..1 — dx = strafe (x), dy = forward/back (z, inverted)
      mobileMovementInput.x = dx / RADIUS;
      mobileMovementInput.z = -dy / RADIUS;
    };

    const reset = () => {
      touchIdRef.current = null;
      mobileMovementInput.x = 0;
      mobileMovementInput.z = 0;
      if (knobRef.current) knobRef.current.style.transform = 'translate(0px, 0px)';
    };

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      if (touchIdRef.current !== null) return; // already tracking a finger
      const t = e.changedTouches[0];
      touchIdRef.current = t.identifier;
      update(t.clientX, t.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === touchIdRef.current) {
          update(e.changedTouches[i].clientX, e.changedTouches[i].clientY);
          return;
        }
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === touchIdRef.current) {
          reset();
          return;
        }
      }
    };

    base.addEventListener('touchstart', onTouchStart, { passive: false });
    base.addEventListener('touchmove', onTouchMove, { passive: false });
    base.addEventListener('touchend', onTouchEnd);
    base.addEventListener('touchcancel', onTouchEnd);
    base.addEventListener('contextmenu', (e) => e.preventDefault());

    return () => {
      base.removeEventListener('touchstart', onTouchStart);
      base.removeEventListener('touchmove', onTouchMove);
      base.removeEventListener('touchend', onTouchEnd);
      base.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  return (
    <div ref={baseRef} className="mobile-joystick-base">
      <div ref={knobRef} className="mobile-joystick-knob" />
    </div>
  );
};

/** Mobile first-person camera — no avatar, moves the camera directly with D-pad.
 *  OrbitControls handles look-around via touch swipe. */
const MobileFirstPersonCamera: React.FC<{
  orbitRef: React.RefObject<any>;
}> = ({ orbitRef }) => {
  const { camera } = useThree();
  const _forward = useRef(new THREE.Vector3());
  const _right = useRef(new THREE.Vector3());

  useFrame((_, delta) => {
    if (mobileMovementInput.x === 0 && mobileMovementInput.z === 0) return;

    const speed = 5 * delta;
    camera.getWorldDirection(_forward.current);
    _forward.current.y = 0;
    _forward.current.normalize();
    _right.current.crossVectors(_forward.current, camera.up).normalize();

    const moveX = mobileMovementInput.x * speed;
    const moveZ = mobileMovementInput.z * speed;

    const dx = _forward.current.x * moveZ + _right.current.x * moveX;
    const dz = _forward.current.z * moveZ + _right.current.z * moveX;

    camera.position.x += dx;
    camera.position.z += dz;

    // Keep OrbitControls target in sync so swipe-to-look stays centered
    if (orbitRef.current) {
      const target = orbitRef.current.target as THREE.Vector3;
      target.x += dx;
      target.z += dz;
      orbitRef.current.update();
    }
  });

  return null;
};

/** Mobile skybox — loads the uploaded skybox texture directly onto a sphere.
 *  Bypasses drei's Environment which uses heavy HDR IBL setup that crashes mobile GPUs.
 *  Handles jpg/png via TextureLoader and hdr via RGBELoader. */
const MobileSkybox: React.FC<{ url: string }> = ({ url }) => {
  const [texture, setTexture] = useState<THREE.Texture | null>(null);
  const texRef = useRef<THREE.Texture | null>(null);

  useEffect(() => {
    let disposed = false;

    // Extract extension from URL path (strip query params first)
    const pathname = url.split('?')[0];
    const ext = pathname.split('.').pop()?.toLowerCase() || '';
    console.log('[MobileSkybox] Loading skybox, ext:', ext, 'url:', pathname.slice(-40));

    const onLoad = (tex: THREE.Texture) => {
      if (disposed) { tex.dispose(); return; }
      // Use default UVMapping — sphere UVs are already equirectangular.
      // EquirectangularReflectionMapping only works with scene.background, not material.map
      tex.mapping = THREE.UVMapping;
      if (ext !== 'hdr' && ext !== 'exr') tex.colorSpace = THREE.SRGBColorSpace;
      texRef.current = tex;
      setTexture(tex);
      console.log('[MobileSkybox] Texture loaded successfully');
    };

    const onError = (err: any) => {
      console.error('[MobileSkybox] Load FAILED for ext:', ext, 'url:', url, 'error:', err);
    };

    if (ext === 'hdr') {
      const loader = new RGBELoader();
      loader.setCrossOrigin('anonymous');
      loader.load(url, onLoad, undefined, onError);
    } else if (ext === 'exr') {
      // Dynamic import EXR only when needed (rare)
      import('three/examples/jsm/loaders/EXRLoader.js').then(({ EXRLoader }) => {
        if (disposed) return;
        const loader = new EXRLoader();
        loader.setCrossOrigin('anonymous');
        loader.load(url, onLoad, undefined, onError);
      }).catch(onError);
    } else {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');
      loader.load(url, onLoad, undefined, onError);
    }

    return () => {
      disposed = true;
      if (texRef.current) { texRef.current.dispose(); texRef.current = null; }
    };
  }, [url]);

  if (!texture) return null;

  return (
    <mesh renderOrder={-1}>
      <sphereGeometry args={[400, 64, 32]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} depthWrite={false} toneMapped={false} fog={false} />
    </mesh>
  );
};

// Procedural soft particle textures (same as AtmosphereScene — canvas-based radial gradients)
const _mobileTexCache: Record<string, THREE.Texture> = {};
function getMobileParticleTexture(style: 'soft' | 'streak' | 'glow' | 'bubble'): THREE.Texture {
  if (_mobileTexCache[style]) return _mobileTexCache[style];
  const size = 64, canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d')!, half = size / 2;
  if (style === 'soft') {
    const g = ctx.createRadialGradient(half, half, 0, half, half, half);
    g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.4, 'rgba(255,255,255,0.6)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  } else if (style === 'streak') {
    const g = ctx.createLinearGradient(half, 0, half, size);
    g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.3, 'rgba(255,255,255,0.8)'); g.addColorStop(0.7, 'rgba(255,255,255,0.8)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(half - 1, 0, 2, size);
  } else if (style === 'bubble') {
    const g = ctx.createRadialGradient(half, half, 0, half, half, half);
    g.addColorStop(0, 'rgba(255,255,255,0.08)'); g.addColorStop(0.72, 'rgba(255,255,255,0.12)'); g.addColorStop(0.85, 'rgba(255,255,255,0.9)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
    const hl = ctx.createRadialGradient(half * 0.62, half * 0.62, 0, half * 0.62, half * 0.62, size * 0.14);
    hl.addColorStop(0, 'rgba(255,255,255,0.95)'); hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hl; ctx.fillRect(0, 0, size, size);
  } else {
    const g = ctx.createRadialGradient(half, half, 0, half, half, half);
    g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.15, 'rgba(255,255,255,0.9)'); g.addColorStop(0.5, 'rgba(255,255,255,0.3)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas); tex.needsUpdate = true;
  _mobileTexCache[style] = tex;
  return tex;
}

/** Mobile-safe atmosphere: fog + particles + lighting. No useXR, no bloom (too heavy). */
const MobileAtmosphere: React.FC = () => {
  const atmosphere = useWorldStore((s) => s.atmosphere);
  const { scene, camera } = useThree();
  const pointsRef = useRef<THREE.Points>(null);
  const _camWorld = useRef(new THREE.Vector3());

  // Fog management
  useFrame(() => {
    if (atmosphere.fog === 'none') {
      if (scene.fog) scene.fog = null;
      return;
    }
    const c = new THREE.Color(atmosphere.fogColor);
    if (atmosphere.fog === 'linear') {
      if (!(scene.fog instanceof THREE.Fog)) scene.fog = new THREE.Fog(c, atmosphere.fogNear, atmosphere.fogFar);
      else scene.fog.color.set(c);
    } else if (atmosphere.fog === 'exponential') {
      if (!(scene.fog instanceof THREE.FogExp2)) scene.fog = new THREE.FogExp2(c, atmosphere.fogDensity);
      else scene.fog.color.set(c);
    }
  });

  // Particle configs (same as AtmosphereScene but capped lower for mobile)
  const CONFIGS: Record<string, { speed: number; spread: number; height: number; size: number; opacity: number; gravity: boolean; blending: THREE.Blending; texture: 'soft' | 'streak' | 'glow' | 'bubble' }> = {
    rain:      { speed: 8,   spread: 25, height: 18, size: 0.12, opacity: 0.5,  gravity: true,  blending: THREE.NormalBlending,   texture: 'streak' },
    snow:      { speed: 1.5, spread: 25, height: 18, size: 0.25, opacity: 0.75, gravity: true,  blending: THREE.NormalBlending,   texture: 'soft' },
    dust:      { speed: 0.3, spread: 20, height: 7,  size: 0.15, opacity: 0.35, gravity: false, blending: THREE.NormalBlending,   texture: 'soft' },
    fireflies: { speed: 0.5, spread: 15, height: 5,  size: 0.3,  opacity: 0.9,  gravity: false, blending: THREE.AdditiveBlending, texture: 'glow' },
    embers:    { speed: 1.0, spread: 10, height: 8,  size: 0.12, opacity: 0.7,  gravity: false, blending: THREE.AdditiveBlending, texture: 'glow' },
    bubbles:   { speed: 0.9, spread: 16, height: 11, size: 0.2,  opacity: 0.55, gravity: false, blending: THREE.NormalBlending,   texture: 'bubble' },
  };

  const pType = atmosphere.particles;
  const config = CONFIGS[pType];
  const count = config ? Math.min(atmosphere.particleCount, 800) : 0;
  const particleTexture = useMemo(() => config ? getMobileParticleTexture(config.texture) : null, [config?.texture]);

  const [positions, velocities] = useMemo(() => {
    if (!config || count === 0) return [null, null];
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      pos[i3] = (Math.random() - 0.5) * config.spread;
      pos[i3 + 1] = Math.random() * config.height;
      pos[i3 + 2] = (Math.random() - 0.5) * config.spread;
      if (config.gravity) {
        vel[i3] = (Math.random() - 0.5) * 0.3;
        vel[i3 + 1] = -config.speed - Math.random() * (config.speed * 0.25);
        vel[i3 + 2] = (Math.random() - 0.5) * 0.3;
      } else if (pType === 'bubbles') {
        vel[i3] = 0;
        vel[i3 + 1] = config.speed * (0.4 + Math.random() * 0.9); // rise; wobble is per-frame
        vel[i3 + 2] = 0;
      } else {
        vel[i3] = (Math.random() - 0.5) * config.speed;
        vel[i3 + 1] = (Math.random() - 0.5) * config.speed * 0.5;
        vel[i3 + 2] = (Math.random() - 0.5) * config.speed;
      }
    }
    return [pos, vel];
  }, [pType, count]);

  useFrame((_, delta) => {
    if (!pointsRef.current || !positions || !velocities || !config) return;
    const posArr = pointsRef.current.geometry.attributes.position.array as Float32Array;
    camera.getWorldPosition(_camWorld.current);
    const cx = _camWorld.current.x, cz = _camWorld.current.z;
    const half = config.spread / 2;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      if (pType === 'fireflies') {
        const t = Date.now() * 0.001;
        posArr[i3] += Math.sin(t + i * 0.73) * delta * 0.5;
        posArr[i3 + 1] += Math.cos(t * 1.3 + i * 1.1) * delta * 0.3;
        posArr[i3 + 2] += Math.sin(t * 0.7 + i * 1.9) * delta * 0.5;
      } else if (pType === 'bubbles') {
        const t = Date.now() * 0.001;
        posArr[i3] += Math.sin(t * 1.6 + i * 0.91) * delta * 0.35;
        posArr[i3 + 1] += velocities[i3 + 1] * delta;
        posArr[i3 + 2] += Math.cos(t * 1.3 + i * 1.37) * delta * 0.35;
      } else {
        posArr[i3] += velocities[i3] * delta;
        posArr[i3 + 1] += velocities[i3 + 1] * delta;
        posArr[i3 + 2] += velocities[i3 + 2] * delta;
      }
      // Respawn
      if (config.gravity && posArr[i3 + 1] < -0.5) {
        posArr[i3] = cx + (Math.random() - 0.5) * config.spread;
        posArr[i3 + 1] = config.height;
        posArr[i3 + 2] = cz + (Math.random() - 0.5) * config.spread;
      } else if (!config.gravity) {
        if (posArr[i3 + 1] > config.height) posArr[i3 + 1] = 0;
        if (posArr[i3 + 1] < -0.5) posArr[i3 + 1] = config.height;
        if (Math.abs(posArr[i3] - cx) > half) posArr[i3] = cx + (Math.random() - 0.5) * config.spread;
        if (Math.abs(posArr[i3 + 2] - cz) > half) posArr[i3 + 2] = cz + (Math.random() - 0.5) * config.spread;
      }
    }
    pointsRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <>
      <ambientLight intensity={atmosphere.ambientIntensity} />
      <directionalLight
        position={[50, 50, 25]}
        intensity={atmosphere.sunIntensity}
        color={atmosphere.sunColor}
        castShadow={!!atmosphere.sunShadows}
        key={`sun-${atmosphere.shadowRadius ?? 60}`}
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-(atmosphere.shadowRadius ?? 60)}
        shadow-camera-right={atmosphere.shadowRadius ?? 60}
        shadow-camera-top={atmosphere.shadowRadius ?? 60}
        shadow-camera-bottom={-(atmosphere.shadowRadius ?? 60)}
        shadow-camera-near={1}
        shadow-camera-far={200}
        shadow-bias={-0.0004}
      />
      {config && positions && count > 0 && (
        <points ref={pointsRef} frustumCulled={false}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" args={[positions, 3]} count={count} />
          </bufferGeometry>
          <pointsMaterial
            map={particleTexture}
            color={atmosphere.particleColor}
            size={config.size}
            sizeAttenuation
            transparent
            opacity={config.opacity}
            depthWrite={false}
            blending={config.blending}
            alphaTest={0.01}
          />
        </points>
      )}
    </>
  );
};

// ---- Ground ----
// Presets that include an AO map
// Presets that animate. Water is sold by motion — a still ripple map reads as
// crumpled foil. Two layers drift at different speeds and angles so the surface
// never repeats visibly.
const ANIMATED_PRESETS = new Set(['water', 'water2']);

const WATER_DRIFT: [number, number][] = [[0.013, 0.007], [-0.008, 0.011]];

/** Scrolls a preset's maps every frame. No-op for static presets. */
function useTextureDrift(texMap: Record<string, any>, preset: string) {
  const animated = ANIMATED_PRESETS.has(preset);
  useFrame((_, delta) => {
    if (!animated) return;
    const t = Math.min(delta, 0.1);   // ignore huge steps after a tab stall
    const color = texMap.map, normal = texMap.normalMap;
    if (color?.offset) {
      color.offset.x += WATER_DRIFT[0][0] * t;
      color.offset.y += WATER_DRIFT[0][1] * t;
    }
    // Opposing drift on the normals is what makes it read as moving water
    // rather than a sliding photograph.
    if (normal?.offset) {
      normal.offset.x += WATER_DRIFT[1][0] * t;
      normal.offset.y += WATER_DRIFT[1][1] * t;
    }
  });
}

const PRESETS_WITH_AO = new Set(['grass','dirt','sand','brick','cobblestone','marble','paving1','paving2','wood1','wood2']);

// Inner component that loads PBR textures via useTexture (suspends until loaded)
// Transparency props shared by every ground material. depthWrite is dropped
// below full opacity so the plane stops occluding whatever is under it — with
// it left on, a half-faded ground still hides everything behind, which looks
// like the slider doing nothing.
const groundFade = (opacity: number) => ({
  transparent: opacity < 1,
  opacity,
  depthWrite: opacity >= 1,
});

const PBRGround: React.FC<{ size: number; preset: string; textureScale: number; opacity: number }> = ({ size, preset, textureScale, opacity }) => {
  const base = `/textures/${preset}`;
  const hasAO = PRESETS_WITH_AO.has(preset);

  const paths: Record<string, string> = {
    map: `${base}/color.jpg`,
    normalMap: `${base}/normal.jpg`,
    roughnessMap: `${base}/roughness.jpg`,
  };
  if (hasAO) paths.aoMap = `${base}/ao.jpg`;

  const texMap = useTexture(paths);

  const repeat = size / textureScale;
  useEffect(() => {
    Object.values(texMap).forEach((tex) => {
      if (tex instanceof THREE.Texture) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(repeat, repeat);
        tex.needsUpdate = true;
      }
    });
  }, [texMap, repeat]);
  useTextureDrift(texMap, preset);

  const meshRef = useRef<THREE.Mesh>(null);
  useEffect(() => {
    if (meshRef.current && hasAO) {
      const geo = meshRef.current.geometry;
      const uv = geo.attributes.uv;
      if (uv) geo.setAttribute('uv2', uv);
    }
  }, [hasAO]);

  return (
    <mesh ref={meshRef} name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[size, size]} />
      {isWater(preset) ? <WaterSurface texMap={texMap} preset={preset} opacity={opacity} /> : <meshStandardMaterial
        color="#ffffff"
        map={texMap.map}
        normalMap={texMap.normalMap}
        roughnessMap={texMap.roughnessMap}
        aoMap={texMap.aoMap || null}
        roughness={1}
        metalness={0}
        {...groundFade(opacity)}
      />}
    </mesh>
  );
};

// Custom PBR ground — same as PBRGround but from library map URLs (ambientCG)
const CustomPBRGround: React.FC<{ size: number; maps: PbrMaps; textureScale: number; opacity: number }> = ({ size, maps, textureScale, opacity }) => {
  const paths: Record<string, string> = { map: maps.colorUrl };
  if (maps.normalUrl) paths.normalMap = maps.normalUrl;
  if (maps.roughnessUrl) paths.roughnessMap = maps.roughnessUrl;
  if (maps.aoUrl) paths.aoMap = maps.aoUrl;
  const texMap = useTexture(paths) as Record<string, THREE.Texture>;

  const repeat = size / textureScale;
  useEffect(() => {
    Object.values(texMap).forEach((tex) => {
      if (tex instanceof THREE.Texture) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(repeat, repeat);
        tex.needsUpdate = true;
      }
    });
  }, [texMap, repeat]);

  const meshRef = useRef<THREE.Mesh>(null);
  useEffect(() => {
    if (meshRef.current && maps.aoUrl) {
      const geo = meshRef.current.geometry;
      const uv = geo.attributes.uv;
      if (uv) geo.setAttribute('uv2', uv);
    }
  }, [maps.aoUrl]);

  return (
    <mesh ref={meshRef} name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial
        color="#ffffff"
        map={texMap.map}
        normalMap={texMap.normalMap || null}
        roughnessMap={texMap.roughnessMap || null}
        aoMap={texMap.aoMap || null}
        roughness={1}
        metalness={0}
        {...groundFade(opacity)}
      />
    </mesh>
  );
};

export const Ground: React.FC<{
  visible: boolean;
  size: number;
  color: string;
  textureUrl: string | null;
  texturePreset: string | null;
  pbrMaps?: PbrMaps | null;
  textureScale: number;
  opacity?: number;
}> = ({ visible, size, color, textureUrl, texturePreset, pbrMaps, textureScale, opacity = 1 }) => {
  if (!visible) return null;

  // Custom library material takes precedence
  if (pbrMaps?.colorUrl) {
    return (
      <Suspense fallback={
        <mesh name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <planeGeometry args={[size, size]} />
          <meshStandardMaterial color={color} {...groundFade(opacity)} />
        </mesh>
      }>
        <CustomPBRGround size={size} maps={pbrMaps} textureScale={textureScale} opacity={opacity} />
      </Suspense>
    );
  }

  // PBR preset — loads via Suspense
  if (texturePreset) {
    return (
      <Suspense fallback={
        <mesh name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <planeGeometry args={[size, size]} />
          <meshStandardMaterial color={color} {...groundFade(opacity)} />
        </mesh>
      }>
        <PBRGround size={size} preset={texturePreset} textureScale={textureScale} opacity={opacity} />
      </Suspense>
    );
  }

  // Custom single texture or plain color
  return (
    <mesh name="ground" receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[size, size]} />
      {textureUrl ? (
        <FallbackOnError fallback={<meshStandardMaterial color={color} {...groundFade(opacity)} />}><Suspense fallback={<meshStandardMaterial color={color} {...groundFade(opacity)} />}>
          <SingleTextureGround textureUrl={textureUrl} size={size} textureScale={textureScale} opacity={opacity} />
        </Suspense></FallbackOnError>
      ) : (
        <meshStandardMaterial color={color} {...groundFade(opacity)} />
      )}
    </mesh>
  );
};

const SingleTextureGround: React.FC<{ textureUrl: string; size: number; textureScale: number; opacity: number }> = ({ textureUrl, size, textureScale, opacity }) => {
  const tex = useTexture(textureUrl);
  useEffect(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    const repeat = size / textureScale;
    tex.repeat.set(repeat, repeat);
    tex.needsUpdate = true;
  }, [tex, size, textureScale]);
  return <meshStandardMaterial map={tex} {...groundFade(opacity)} />;
};

// ---- Resource Meter (bottom-left HUD) ----
const ResourceMeter: React.FC<{
  multiplayerInfo: { shardIndex: number | null; playerCount: number; socketId: string | null; connected: boolean };
  primitiveCount: number;
  contentCount: number;
  lightCount: number;
}> = ({ multiplayerInfo, primitiveCount, contentCount, lightCount }) => {
  const [fps, setFps] = useState(0);
  const [memory, setMemory] = useState<number | null>(null);
  const frameRef = useRef<number[]>([]);

  useEffect(() => {
    let rafId: number;
    const tick = () => {
      const now = performance.now();
      frameRef.current.push(now);
      // Keep last 30 frames
      while (frameRef.current.length > 30) frameRef.current.shift();
      if (frameRef.current.length > 1) {
        const elapsed = now - frameRef.current[0];
        setFps(Math.round((frameRef.current.length - 1) / (elapsed / 1000)));
      }
      // Memory (Chrome only)
      const perf = (performance as any);
      if (perf.memory) {
        setMemory(Math.round(perf.memory.usedJSHeapSize / (1024 * 1024)));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const totalObjects = primitiveCount + contentCount + lightCount;
  const fpsColor = fps >= 50 ? '#4ade80' : fps >= 30 ? '#facc15' : '#f87171';

  return (
    <div style={{
      position: 'absolute', bottom: 12, left: 12, padding: '8px 12px',
      background: 'rgba(5, 14, 24, 0.75)', backdropFilter: 'blur(10px)',
      borderRadius: 8, border: '1px solid rgba(0, 194, 179, 0.15)',
      color: 'rgba(255,255,255,0.7)', fontSize: 11, fontFamily: 'monospace',
      zIndex: 90, display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.5,
    }}>
      <div>
        <span style={{ color: fpsColor }}>{fps}</span> fps
        {memory !== null && <span style={{ marginLeft: 8 }}>{memory} MB</span>}
        <span style={{ marginLeft: 8 }}>objects: <span style={{ color: 'var(--gx-accent)' }}>{totalObjects}</span></span>
      </div>
    </div>
  );
};

// ---- Presence Tracker (mobile only — broadcast-based) ----
// Mobile doesn't render MultiplayerSync (no Canvas XR context),
// so this component handles channel subscribe + broadcast join/heartbeat.
const PresenceTracker: React.FC<{ worldId: string }> = ({ worldId }) => {
  useEffect(() => {
    if (!worldId) return;
    const channel = getChannel(worldId);
    const presenceKey = getPresenceKey() || crypto.randomUUID();
    const remotePlayersRef: Record<string, { userName: string; avatarUrl: string; userId?: string | null; lastSeen: number }> = {};

    const rebuildOnlinePlayers = () => {
      const now = Date.now();
      const state = useWorldStore.getState();
      const allPlayers: { id: string; userName: string; avatarUrl: string; userId?: string | null }[] = [
        { id: presenceKey, userName: state.userName || 'Player', avatarUrl: state.avatarUrl || '', userId: state.user?.id || null },
      ];
      let remoteCount = 0;
      for (const [key, info] of Object.entries(remotePlayersRef)) {
        if (now - info.lastSeen > 8000) { delete remotePlayersRef[key]; continue; }
        allPlayers.push({ id: key, userName: info.userName, avatarUrl: info.avatarUrl, userId: info.userId });
        remoteCount++;
      }
      state.setOnlinePlayers(allPlayers);
      state.setMultiplayerInfo({ playerCount: remoteCount, connected: true });
    };

    // Listen for player join/here/heartbeat/leave broadcasts
    const registerRemote = (key: string, data: any) => {
      remotePlayersRef[key] = { userName: data.userName || 'Player', avatarUrl: data.avatarUrl || '', userId: data.userId, lastSeen: Date.now() };
    };

    channel.on('broadcast', { event: 'player:join' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKey) return;
      registerRemote(data.key, data);
      rebuildOnlinePlayers();
      // Reply with player:here (NOT player:join) to avoid infinite loop
      const state = useWorldStore.getState();
      channel.send({ type: 'broadcast', event: 'player:here', payload: { key: presenceKey, userName: state.userName || 'Player', avatarUrl: state.avatarUrl || '', userId: state.user?.id || null } });
    });

    channel.on('broadcast', { event: 'player:here' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKey) return;
      registerRemote(data.key, data);
      rebuildOnlinePlayers();
    });

    channel.on('broadcast', { event: 'player:heartbeat' }, (payload: any) => {
      const data = payload.payload;
      if (!data?.key || data.key === presenceKey) return;
      registerRemote(data.key, data);
    });

    channel.on('broadcast', { event: 'player:leave' }, (payload: any) => {
      const data = payload.payload;
      if (data?.key) { delete remotePlayersRef[data.key]; rebuildOnlinePlayers(); }
    });

    // Subscribe and announce join
    const stopStatus = onChannelSubscribed(worldId, (status: string) => {
      if (status === 'SUBSCRIBED') {
        useWorldStore.getState().setMultiplayerInfo({ connected: true });
        const state = useWorldStore.getState();
        channel.send({ type: 'broadcast', event: 'player:join', payload: { key: presenceKey, userName: state.userName || 'Player', avatarUrl: state.avatarUrl || '', userId: state.user?.id || null } });
      }
    });

    // Heartbeat every 3s
    const heartbeatInterval = setInterval(() => {
      try {
        const state = useWorldStore.getState();
        channel.send({ type: 'broadcast', event: 'player:heartbeat', payload: { key: presenceKey, userName: state.userName || 'Player', avatarUrl: state.avatarUrl || '', userId: state.user?.id || null } });
      } catch (e) { /* not ready */ }
    }, 3000);

    // Cleanup stale players every 5s
    const cleanupInterval = setInterval(rebuildOnlinePlayers, 5000);

    return () => {
      try { channel.send({ type: 'broadcast', event: 'player:leave', payload: { key: presenceKey } }); } catch (e) {}
      stopStatus();
      clearInterval(heartbeatInterval);
      clearInterval(cleanupInterval);
    };
  }, [worldId]);

  return null;
};

// ---- Scene Primitive (renders a shape with optional PBR texture) ----

const PrimitiveGeometry: React.FC<{ shape: PrimitiveShape; game?: string; board?: LeaderboardConfig }> = ({ shape, game, board }) => {
  switch (shape) {
    case 'box': return <boxGeometry args={[1, 1, 1]} />;
    case 'sphere': return <sphereGeometry args={[0.5, 32, 32]} />;
    case 'cylinder': return <cylinderGeometry args={[0.5, 0.5, 1, 32]} />;
    case 'cone': return <coneGeometry args={[0.5, 1, 32]} />;
    case 'plane': return <planeGeometry args={[1, 1]} />;
    case 'torus': return <torusGeometry args={[0.4, 0.15, 16, 32]} />;
    case 'portal': return <boxGeometry args={[PORTAL_W, PORTAL_H, PORTAL_W]} />;
    case 'gametable': { const fp = getGameDef(game ?? armedGame())?.footprint; return <boxGeometry args={[fp?.w ?? GT_W, GT_H, fp?.d ?? GT_W]} />; }
    case 'arcade': return <boxGeometry args={[ARC_W, ARC_H, ARC_D]} />;
    case 'leaderboard': return <boxGeometry args={boardBox({ ...armedBoardConfig(), ...(board ?? {}) })} />;
    case 'npc': return <boxGeometry args={[NPC_PICK_W, NPC_PICK_H, NPC_PICK_W]} />;
    case 'speaker': return <boxGeometry args={[SPK_W, SPK_H, SPK_D]} />;
    case 'worldgame': { const r = armedWorldGame().role, b = ROLE_BOX[r], k = ROLE_SCALE[r]; return <boxGeometry args={[b[0] * k[0], b[1] * k[1], b[2] * k[2]]} />; }
    default: return <boxGeometry args={[1, 1, 1]} />;
  }
};

const PBRPrimitiveMaterial: React.FC<{ preset: string; textureScale: number; textureScaleY?: number }> = ({ preset, textureScale, textureScaleY }) => {
  const base = `/textures/${preset}`;
  const hasAO = PRESETS_WITH_AO.has(preset);
  const paths: Record<string, string> = {
    map: `${base}/color.jpg`,
    normalMap: `${base}/normal.jpg`,
    roughnessMap: `${base}/roughness.jpg`,
  };
  if (hasAO) paths.aoMap = `${base}/ao.jpg`;
  const texMap = useTexture(paths);

  useEffect(() => {
    Object.values(texMap).forEach((tex) => {
      if (tex instanceof THREE.Texture) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(textureScale, textureScaleY ?? textureScale);
        tex.needsUpdate = true;
      }
    });
  }, [texMap, textureScale, textureScaleY]);
  useTextureDrift(texMap, preset);

  if (isWater(preset)) {
    // Pools are shallower than an ocean, so the tint saturates sooner and the
    // foam band is tighter — the same numbers as the ground read as a void.
    return <WaterSurface texMap={texMap} preset={preset} depthFade={1.6} foamWidth={0.22} />;
  }

  return (
    <meshStandardMaterial
      color="#ffffff"
      map={texMap.map}
      normalMap={texMap.normalMap}
      roughnessMap={texMap.roughnessMap}
      aoMap={texMap.aoMap || null}
      roughness={1}
      metalness={0}
    />
  );
};

const CustomTextureMaterial: React.FC<{ url: string; textureScale: number; textureScaleY?: number }> = ({ url, textureScale, textureScaleY }) => {
  const mapUrl = url.includes('?') ? url : `${url}?v=2`;
  const texMap = useTexture({ map: mapUrl });

  useEffect(() => {
    if (texMap.map instanceof THREE.Texture) {
      texMap.map.wrapS = texMap.map.wrapT = THREE.RepeatWrapping;
      texMap.map.repeat.set(textureScale, textureScaleY ?? textureScale);
      texMap.map.needsUpdate = true;
    }
  }, [texMap, textureScale, textureScaleY]);

  return (
    <meshStandardMaterial
      color="#ffffff"
      map={texMap.map}
      roughness={0.8}
      metalness={0}
    />
  );
};

const CustomPBRMaterial: React.FC<{ pbrMaps: import('../stores/worldStore').PbrMaps; textureScale: number; textureScaleY?: number }> = ({ pbrMaps, textureScale, textureScaleY }) => {
  const { scene } = useThree();
  // Append cache-buster to S3 URLs to avoid stale non-CORS cached responses
  const bust = (url: string) => url.includes('?') ? url : `${url}?v=2`;
  const paths: Record<string, string> = { map: bust(pbrMaps.colorUrl) };
  if (pbrMaps.normalUrl) paths.normalMap = bust(pbrMaps.normalUrl);
  if (pbrMaps.roughnessUrl) paths.roughnessMap = bust(pbrMaps.roughnessUrl);
  if (pbrMaps.aoUrl) paths.aoMap = bust(pbrMaps.aoUrl);
  if (pbrMaps.metallicUrl) paths.metalnessMap = bust(pbrMaps.metallicUrl);

  const texMap = useTexture(paths);

  useEffect(() => {
    Object.values(texMap).forEach((tex) => {
      if (tex instanceof THREE.Texture) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(textureScale, textureScaleY ?? textureScale);
        tex.needsUpdate = true;
      }
    });
  }, [texMap, textureScale, textureScaleY]);

  // meshStandardMaterial uses scene.environment for reflections.
  // When roughnessMap is provided, it drives per-pixel roughness (0=mirror, 1=matte).
  // Without roughnessMap, use a moderate default so metal surfaces still reflect.
  const hasRoughnessMap = !!pbrMaps.roughnessUrl;
  const hasMetalnessMap = !!pbrMaps.metallicUrl;

  return (
    <meshStandardMaterial
      color="#ffffff"
      map={texMap.map}
      normalMap={texMap.normalMap || null}
      roughnessMap={texMap.roughnessMap || null}
      aoMap={texMap.aoMap || null}
      metalnessMap={texMap.metalnessMap || null}
      roughness={hasRoughnessMap ? 1 : 0.3}
      metalness={hasMetalnessMap ? 1 : 0.8}
      envMap={scene.environment}
      envMapIntensity={1.5}
    />
  );
};

const ScenePrimitive: React.FC<{
  id: string;
  primitive: PrimitiveItem;
  isSelected: boolean;
  transformMode: 'translate' | 'rotate' | 'scale';
  onSelect: () => void;
  onTransformChange: (updates: Partial<PrimitiveItem>) => void;
  orbitRef: React.RefObject<any>;
}> = ({ id, primitive, isSelected, transformMode, onSelect, onTransformChange, orbitRef }) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const isPresenting = usePresenting();
  const snapSettings = useWorldStore((s) => s.snapSettings);
  const editLock = useWorldStore((s) => s.editLocks[id]);
  const lastStreamRef = useRef(0);

  // Selection handler — switches selection between objects. A locked object
  // can't be picked by clicking: it flashes red, and is unlocked from the
  // Scene Objects list.
  const handleSelect = useCallback(() => {
    // Mid-grab in VR (one hand or two), the other hand's pinch is a select too —
    // it must not select whatever its ray happens to hit.
    if ((window as any).__vrGrabbing) return;
    if (primitive.isLocked) { useWorldStore.getState().flashLocked(id); return; }
    onSelect();
  }, [onSelect, primitive.isLocked, id]);

  // VR grab state — uses squeeze/grip, not trigger
  const isGrabbing = useRef(false);
  const grabStartPoint = useRef(new THREE.Vector3());
  const grabStartPos = useRef(new THREE.Vector3());
  const grabStartScale = useRef(new THREE.Vector3());
  const grabStartRotY = useRef(0);
  const grabOffset = useRef(new THREE.Vector3());
  const grabCenter = useRef(new THREE.Vector3());
  const lastTurn = useRef(0);

  const handlePointerDown = useCallback((e: any) => {
    if (!isPresenting) return;
    // First click = select. Grab only if already selected.
    if (!isSelected) {
      handleSelect();
      return;
    }
    // VRGrab (grip / pinch, near or far) owns Move, Turn and Size in VR, for
    // shapes and models alike — this ray-drag would fight it for hand pinches.
    if ((window as any).__vrGrabOn) return;
    e.stopPropagation();
    try { (e.target as any).setPointerCapture(e.pointerId); } catch (_) {}
    isGrabbing.current = true;
    grabStartPoint.current.copy(e.point);
    if (meshRef.current) {
      grabStartPos.current.copy(meshRef.current.position);
      grabStartScale.current.copy(meshRef.current.scale);
      grabStartRotY.current = meshRef.current.rotation.y;
      grabOffset.current.copy(meshRef.current.position).sub(e.point);
      meshRef.current.getWorldPosition(grabCenter.current);
      lastTurn.current = 0;
    }
  }, [isPresenting, isSelected, handleSelect]);

  const handlePointerMove = useCallback((e: any) => {
    if (!isGrabbing.current || !meshRef.current) return;
    e.stopPropagation();

    const mode = (window as any).__vrTransformMode || transformMode;

    if (mode === 'rotate') {
      // The angle the pointer has gone round the shape's vertical axis — the
      // same whichever way you face (world-X drag barely turned it facing ±X).
      const a = turnAngle(grabCenter.current, grabStartPoint.current, e.point);
      if (a !== null) lastTurn.current = a;
      const newRotY = grabStartRotY.current + lastTurn.current;
      meshRef.current.rotation.y = snapSettings.rotation
        ? Math.round(newRotY / (snapSettings.rotationAngle * Math.PI / 180)) * (snapSettings.rotationAngle * Math.PI / 180)
        : newRotY;
    } else if (mode === 'scale-uniform' || mode === 'scale') {
      // Proportional: every axis by the same factor from the start scale, so a
      // wall or plank keeps its shape instead of becoming a cube.
      proportionalScale(grabStartScale.current, sizeFactor(grabCenter.current, grabStartPoint.current, e.point), meshRef.current.scale);
    } else {
      const newPos = e.point.clone().add(grabOffset.current);
      if (snapSettings.grid) {
        newPos.x = Math.round(newPos.x / snapSettings.gridSize) * snapSettings.gridSize;
        newPos.y = Math.round(newPos.y / snapSettings.gridSize) * snapSettings.gridSize;
        newPos.z = Math.round(newPos.z / snapSettings.gridSize) * snapSettings.gridSize;
      }
      meshRef.current.position.copy(newPos);
    }
  }, [snapSettings, transformMode]);

  const handlePointerUp = useCallback((e: any) => {
    if (!isGrabbing.current || !meshRef.current) return;
    isGrabbing.current = false;
    const m = meshRef.current;
    const mode = (window as any).__vrTransformMode || transformMode;

    if (mode === 'rotate') {
      onTransformChange({ rotation: [m.rotation.x, m.rotation.y, m.rotation.z] });
    } else if (mode === 'scale' || mode === 'scale-uniform') {
      onTransformChange({ scale: [m.scale.x, m.scale.y, m.scale.z] });
    } else {
      onTransformChange({ position: [m.position.x, m.position.y, m.position.z] });
    }
  }, [onTransformChange, transformMode]);

  // Ensure userData stays in sync
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.userData.collidable = primitive.collidable;
      meshRef.current.userData.seat = !!primitive.seat;
      meshRef.current.userData.seatFit = primitive.seatFit;
      meshRef.current.userData.objectId = id;
    }
  }, [primitive.collidable, primitive.seat, primitive.seatFit, id]);

  const portalLinked = useWorldStore((s) => primitive.shape === 'portal' && portalActive(id, s.primitives));

  if (!primitive.visible) return null;

  const meshEl = primitive.shape === 'npc' && primitive.npc ? (
    // Invisible pick box the size of a person; the character stands inside it
    // and loops its move in place (it only walks around in the viewer).
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[NPC_PICK_W, NPC_PICK_H, NPC_PICK_W]} />
      <meshBasicMaterial visible={false} />
      <group position={[0, -NPC_PICK_H / 2, 0]}>
        <NpcAvatar id={id} npc={primitive.npc} editor selected={isSelected} />
        {isSelected && (primitive.npc.anim === 'wander' || primitive.npc.anim === 'jog') && (
          // How far it may walk from here
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
            <ringGeometry args={[Math.max(0.05, primitive.npc.radius - 0.04), primitive.npc.radius, 64]} />
            <meshBasicMaterial color="#ffd166" transparent opacity={0.6} depthWrite={false} side={THREE.DoubleSide} />
          </mesh>
        )}
      </group>
    </mesh>
  ) : primitive.shape === 'gametable' ? (
    // Invisible pick box around the table and chairs; the furniture is drawn
    // inside it, standing on the floor.
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[getGameDef(primitive.game)?.footprint?.w ?? GT_W, GT_H, getGameDef(primitive.game)?.footprint?.d ?? GT_W]} />
      <meshBasicMaterial visible={false} />
      <BuilderGameTable primitive={primitive} />
    </mesh>
  ) : primitive.shape === 'worldgame' && primitive.worldGame ? (
    // Invisible pick box; the world-game piece is drawn inside it (zones are unit boxes sized by scale).
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={ROLE_BOX[primitive.worldGame.role]} />
      <meshBasicMaterial visible={false} />
      <Suspense fallback={null}><BuilderWorldGame primitive={primitive} selected={isSelected} /></Suspense>
    </mesh>
  ) : primitive.shape === 'speaker' ? (
    // Invisible pick box the size of the speaker; the speaker is drawn inside it.
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[SPK_W, SPK_H, SPK_D]} />
      <meshBasicMaterial visible={false} />
      <SpeakerModel3D id={id} color={primitive.color} selected={isSelected} editor />
    </mesh>
  ) : primitive.shape === 'arcade' ? (
    // Invisible pick box the size of the cabinet; the cabinet is drawn inside it.
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[ARC_W, ARC_H, ARC_D]} />
      <meshBasicMaterial visible={false} />
      <Suspense fallback={null}><BuilderArcadeCabinet primitive={primitive} /></Suspense>
    </mesh>
  ) : primitive.shape === 'leaderboard' ? (
    // Invisible pick box around the scoreboard (and its stand); the board is drawn inside it.
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <PrimitiveGeometry shape="leaderboard" board={primitive.board} />
      <meshBasicMaterial visible={false} />
      <Suspense fallback={null}><BuilderLeaderboard primitive={primitive} /></Suspense>
    </mesh>
  ) : primitive.shape === 'portal' ? (
    // Invisible doorway-sized box = what you click, select and drag; the
    // swirling ring is drawn inside it.
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      userData={{ portal: id }}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <boxGeometry args={[PORTAL_W, PORTAL_H, PORTAL_W]} />
      <meshBasicMaterial visible={false} />
      <PortalVisual color={primitive.color} active={portalLinked} label={portalLabelOf(primitive)} highlight={isSelected} />
    </mesh>
  ) : primitive.shape === 'text' ? (
    <Text
      ref={meshRef as any}
      name={`primitive-${id}`}
      userData={{ collidable: primitive.collidable, seat: !!primitive.seat, ...(primitive.seatFit ? { seatFit: primitive.seatFit } : {}) }}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      fontSize={primitive.fontSize || 1}
      color={primitive.color}
      anchorX="center"
      anchorY="middle"
      onClick={(e: any) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {primitive.text || 'Text'}
    </Text>
  ) : (
    <mesh
      ref={meshRef}
      name={`primitive-${id}`}
      castShadow={primitive.castShadow !== false}
      receiveShadow
      userData={{ collidable: primitive.collidable, seat: !!primitive.seat, ...(primitive.seatFit ? { seatFit: primitive.seatFit } : {}) }}
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale}
      onClick={(e) => { e.stopPropagation(); handleSelect(); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <PrimitiveGeometry shape={primitive.shape} />
      {primitive.colliderOnly ? (
        <meshBasicMaterial color="#00c2b3" wireframe transparent opacity={isSelected ? 0.5 : 0.15} depthWrite={false} />
      ) : primitive.texturePreset ? (
        <Suspense fallback={<meshStandardMaterial color={primitive.color} />}>
          <PBRPrimitiveMaterial preset={primitive.texturePreset} textureScale={primitive.textureScale} textureScaleY={primitive.textureScaleY} />
        </Suspense>
      ) : primitive.pbrMaps ? (
        <FallbackOnError fallback={<meshStandardMaterial color={primitive.color} />}><Suspense fallback={<meshStandardMaterial color={primitive.color} />}>
          <CustomPBRMaterial pbrMaps={primitive.pbrMaps} textureScale={primitive.textureScale} textureScaleY={primitive.textureScaleY} />
        </Suspense></FallbackOnError>
      ) : primitive.textureUrl ? (
        <FallbackOnError fallback={<meshStandardMaterial color={primitive.color} />}><Suspense fallback={<meshStandardMaterial color={primitive.color} />}>
          <CustomTextureMaterial url={primitive.textureUrl} textureScale={primitive.textureScale} textureScaleY={primitive.textureScaleY} />
        </Suspense></FallbackOnError>
      ) : (primitive.opacity ?? 1) < 1 ? (
        isPresenting ? (
          <meshBasicMaterial
            color={primitive.color}
            transparent
            opacity={primitive.opacity ?? 1}
          />
        ) : (
          <meshPhysicalMaterial
            color={primitive.color}
            transparent
            opacity={primitive.opacity ?? 1}
            metalness={primitive.metalness ?? 0}
            roughness={primitive.roughness ?? 0.5}
            transmission={1 - (primitive.opacity ?? 1)}
            ior={1.5}
            thickness={0.5}
            emissive={primitive.color}
            emissiveIntensity={primitive.emissive ?? 0}
          />
        )
      ) : isPresenting ? (
        <meshLambertMaterial
          color={primitive.color}
          emissive={primitive.color}
          emissiveIntensity={primitive.emissive ?? 0}
        />
      ) : (
        <meshStandardMaterial
          color={primitive.color}
          metalness={primitive.metalness ?? 0}
          roughness={primitive.roughness ?? 0.5}
          emissive={primitive.color}
          emissiveIntensity={primitive.emissive ?? 0}
        />
      )}
    </mesh>
  );

  // VR selection highlight — glowing wireframe outline
  const selectionHighlight = isSelected && isPresenting ? (
    <mesh
      position={primitive.position}
      rotation={primitive.rotation}
      scale={primitive.scale.map((s: number) => s * 1.05) as [number, number, number]}
    >
      <PrimitiveGeometry shape={primitive.shape} game={primitive.game} board={primitive.board} />
      <meshBasicMaterial color="#00c2b3" wireframe transparent opacity={0.6} depthWrite={false} />
    </mesh>
  ) : null;

  if (isSelected && !primitive.isLocked && !editLock && !isPresenting) {
    // The mesh sits beside the gizmo, not inside it: drei re-attaches the
    // gizmo whenever its children change, and meshEl is a new element on every
    // render, so any re-render mid-drag dropped the drag ("arrows don't move").
    return (
      <>
      {meshEl}
      <TransformControls
        object={meshRef}
        mode={transformMode}
        translationSnap={snapSettings.grid ? snapSettings.gridSize : null}
        rotationSnap={snapSettings.rotation ? (snapSettings.rotationAngle * Math.PI / 180) : null}
        onMouseDown={() => {
          (window as any).__transformDragging = true;
          if (orbitRef.current) orbitRef.current.enabled = false;
          const wid = useWorldStore.getState().actualWorldId;
          if (wid) broadcastObjectLock(wid, id);
        }}
        onChange={() => {
          if (!(window as any).__transformDragging || !meshRef.current) return;
          const now = performance.now();
          if (now - lastStreamRef.current < 66) return;
          lastStreamRef.current = now;
          const wid = useWorldStore.getState().actualWorldId;
          if (!wid) return;
          const m = meshRef.current;
          broadcastObjectTransform(wid, id, {
            position: [m.position.x, m.position.y, m.position.z],
            rotation: [m.rotation.x, m.rotation.y, m.rotation.z],
            scale: [m.scale.x, m.scale.y, m.scale.z],
          });
        }}
        onMouseUp={() => {
          setTimeout(() => { (window as any).__transformDragging = false; }, 50);
          if (orbitRef.current) orbitRef.current.enabled = true;
          if (meshRef.current) {
            const p = meshRef.current.position;
            const r = meshRef.current.rotation;
            const s = meshRef.current.scale;
            onTransformChange({
              position: [p.x, p.y, p.z],
              rotation: [r.x, r.y, r.z],
              scale: [s.x, s.y, s.z],
            });
          }
          const wid = useWorldStore.getState().actualWorldId;
          if (wid) broadcastObjectUnlock(wid, id);
        }}
      />
      </>
    );
  }

  return <>{meshEl}{selectionHighlight}</>;
};

// ---- Scene Light (renders a light with a clickable helper gizmo + TransformControls) ----
const SceneLight: React.FC<{
  id: string;
  light: LightItem;
  isSelected: boolean;
  onSelect: () => void;
  onTransformChange: (updates: Partial<LightItem>) => void;
  orbitRef: React.RefObject<any>;
}> = ({ id, light, isSelected, onSelect, onTransformChange, orbitRef }) => {
  const groupRef = useRef<THREE.Group>(null!);
  const spotLightRef = useRef<THREE.SpotLight>(null);
  const targetRef = useRef<THREE.Object3D>(null);
  const snapSettings = useWorldStore((s) => s.snapSettings);
  const editLock = useWorldStore((s) => s.editLocks[id]);
  const lastStreamRef = useRef(0);
  const isPresenting = usePresenting();

  const handleSelect = useCallback(() => {
    if (light.isLocked) { useWorldStore.getState().flashLocked(id); return; }
    if (isPresenting) {
      requestAnimationFrame(() => onSelect());
    } else {
      onSelect();
    }
  }, [isPresenting, onSelect, light.isLocked, id]);

  // Keep spotlight target in sync + set objectId
  useEffect(() => {
    if (spotLightRef.current && targetRef.current) {
      spotLightRef.current.target = targetRef.current;
    }
    if (groupRef.current) {
      groupRef.current.userData.objectId = id;
    }
  });

  if (!light.visible) return null;

  const helperColor = isSelected ? '#00c2b3' : light.color;

  // Spotlight target: offset below the light position along its rotation
  const targetPos: [number, number, number] = [
    light.position[0],
    light.position[1] - light.distance * 0.5,
    light.position[2],
  ];

  const inner = (
    <group
      ref={groupRef}
      position={light.position}
      rotation={light.type !== 'pointlight' ? light.rotation : undefined}
    >
      {/* Clickable helper sphere */}
      <mesh onClick={(e) => { e.stopPropagation(); handleSelect(); }}>
        <sphereGeometry args={[0.25, 12, 12]} />
        <meshBasicMaterial color={helperColor} transparent opacity={0.85} wireframe={!isSelected} />
      </mesh>

      {/* Rays indicator lines */}
      {[0, 1, 2, 3].map((i) => {
        const a = (i / 4) * Math.PI * 2;
        const spread = light.type === 'spotlight' ? Math.tan(light.angle) * 2 : 0.8;
        return (
          <line key={i}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                args={[new Float32Array([0, 0, 0, Math.sin(a) * spread, -2, Math.cos(a) * spread]), 3]}
                count={2}
              />
            </bufferGeometry>
            <lineBasicMaterial color={helperColor} transparent opacity={0.4} />
          </line>
        );
      })}

      {/* The actual light — positioned at origin since the group handles position */}
      {light.type === 'spotlight' && (
        <spotLight
          ref={spotLightRef}
          color={light.color}
          intensity={light.intensity}
          distance={light.distance}
          angle={light.angle}
          penumbra={light.penumbra}
          castShadow={light.castShadow}
        />
      )}
      {light.type === 'pointlight' && (
        <pointLight
          color={light.color}
          intensity={light.intensity}
          distance={light.distance}
          castShadow={light.castShadow}
        />
      )}
      {light.type === 'arealight' && (
        <rectAreaLight
          color={light.color}
          intensity={light.intensity}
          width={light.width}
          height={light.height}
        />
      )}

      {/* Visual range indicator when selected */}
      {isSelected && light.type === 'spotlight' && (
        <mesh rotation={[0, 0, 0]}>
          <coneGeometry args={[Math.tan(light.angle) * light.distance * 0.4, light.distance * 0.4, 16, 1, true]} />
          <meshBasicMaterial color={light.color} transparent opacity={0.06} side={2} wireframe />
        </mesh>
      )}
      {isSelected && light.type === 'pointlight' && (
        <mesh>
          <sphereGeometry args={[light.distance * 0.15, 16, 8]} />
          <meshBasicMaterial color={light.color} transparent opacity={0.05} wireframe />
        </mesh>
      )}
      {isSelected && light.type === 'arealight' && (
        <mesh>
          <planeGeometry args={[light.width, light.height]} />
          <meshBasicMaterial color={light.color} transparent opacity={0.12} side={2} />
        </mesh>
      )}
    </group>
  );

  // Spotlight target object (lives outside the group so it doesn't move with it)
  const targetEl = light.type === 'spotlight' ? (
    <object3D ref={targetRef} position={targetPos} />
  ) : null;

  if (isSelected && !light.isLocked && !editLock && !isPresenting) {
    return (
      <>
        {targetEl}
        {inner}
        <TransformControls
          object={groupRef}
          mode="translate"
          translationSnap={snapSettings.grid ? snapSettings.gridSize : null}
          onMouseDown={() => {
            (window as any).__transformDragging = true;
            if (orbitRef.current) orbitRef.current.enabled = false;
            const wid = useWorldStore.getState().actualWorldId;
            if (wid) broadcastObjectLock(wid, id);
          }}
          onChange={() => {
            if (!(window as any).__transformDragging || !groupRef.current) return;
            const now = performance.now();
            if (now - lastStreamRef.current < 66) return;
            lastStreamRef.current = now;
            const wid = useWorldStore.getState().actualWorldId;
            if (!wid) return;
            const g = groupRef.current;
            broadcastObjectTransform(wid, id, {
              position: [g.position.x, g.position.y, g.position.z],
              rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
            });
          }}
          onMouseUp={() => {
            setTimeout(() => { (window as any).__transformDragging = false; }, 50);
            if (orbitRef.current) orbitRef.current.enabled = true;
            if (groupRef.current) {
              const p = groupRef.current.position;
              const r = groupRef.current.rotation;
              onTransformChange({
                position: [p.x, p.y, p.z],
                rotation: [r.x, r.y, r.z],
              });
            }
            const wid = useWorldStore.getState().actualWorldId;
            if (wid) broadcastObjectUnlock(wid, id);
          }}
        />
      </>
    );
  }

  return <>{targetEl}{inner}</>;
};

// ---- Spawn Point Marker ----
const SpawnPointMarker: React.FC<{
  position: [number, number, number];
  isSelected: boolean;
  onSelect: () => void;
  onTransformChange: (pos: [number, number, number]) => void;
  orbitRef: React.RefObject<any>;
  canBuild: boolean;
}> = ({ position, isSelected, onSelect, onTransformChange, orbitRef, canBuild }) => {
  const meshRef = React.useRef<THREE.Mesh>(null);
  const ringRef = React.useRef<THREE.Mesh>(null);
  const markerRef = React.useRef<THREE.Group>(null);
  const snapSettings = useWorldStore((s) => s.snapSettings);

  useFrame((_, delta) => {
    if (ringRef.current) {
      ringRef.current.rotation.y += delta * 0.5;
    }
  });

  const marker = (
    <group ref={markerRef} position={position} onClick={(e) => { e.stopPropagation(); if (canBuild) onSelect(); }}>
      {/* Base pad */}
      <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[0.6, 32]} />
        <meshStandardMaterial color="#00c2b3" transparent opacity={0.4} emissive="#00c2b3" emissiveIntensity={0.3} />
      </mesh>
      {/* Rotating ring */}
      <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]}>
        <ringGeometry args={[0.5, 0.6, 32]} />
        <meshStandardMaterial color="#00c2b3" transparent opacity={0.6} emissive="#00c2b3" emissiveIntensity={0.5} />
      </mesh>
      {/* Arrow pointing up */}
      <mesh position={[0, 0.6, 0]}>
        <coneGeometry args={[0.15, 0.4, 8]} />
        <meshStandardMaterial color="#00c2b3" emissive="#00c2b3" emissiveIntensity={0.4} transparent opacity={0.8} />
      </mesh>
      {/* Vertical beam */}
      <mesh position={[0, 0.3, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.5, 8]} />
        <meshStandardMaterial color="#00c2b3" emissive="#00c2b3" emissiveIntensity={0.3} transparent opacity={0.5} />
      </mesh>
    </group>
  );

  if (isSelected && canBuild) {
    // Same pattern as the primitive gizmo: attach to the object by ref and
    // commit the position ON RELEASE. Committing during the drag
    // (onObjectChange -> store -> re-render) reset the gizmo every frame and
    // made the handles impossible to pull.
    return (
      <>
      {marker}
      <TransformControls
        object={markerRef as any}
        mode="translate"
        translationSnap={snapSettings.grid ? snapSettings.gridSize : null}
        onMouseDown={() => {
          (window as any).__transformDragging = true;
          if (orbitRef.current) orbitRef.current.enabled = false;
        }}
        onMouseUp={() => {
          setTimeout(() => { (window as any).__transformDragging = false; }, 50);
          if (orbitRef.current) orbitRef.current.enabled = true;
          if (markerRef.current) {
            const p = markerRef.current.position;
            onTransformChange([p.x, p.y, p.z]);
          }
        }}
      />
      </>
    );
  }

  return marker;
};

// ============================================================================
// Build Mode v2 — grid snap, ghost placement, wall/room drawing, paint
// ----------------------------------------------------------------------------
// Everything below is inert while `buildTool === 'none'` (the default), so the
// classic select + transform-gizmo flow is completely untouched.
// ============================================================================

const WALL_THICKNESS = 0.2;
const WALL_HEIGHT = 3;
const WALL_COLOR = '#b8b0a4';
const FLOOR_COLOR = '#8a8378';
const FLOOR_THICKNESS = 0.1;
const GHOST_COLOR = '#00c2b3';
// NPCs: an invisible person-sized pick box, centred half its height above the floor.
const NPC_PICK_W = 0.7;
const NPC_PICK_H = 1.8;

const newPrimId = () => `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
const newContentId = () => `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;

const snapAxis = (v: number, on: boolean, size: number) => (on && size > 0 ? Math.round(v / size) * size : v);

// Y offset that makes a unit-scaled primitive rest on the ground plane.
const shapeRestY = (shape: PrimitiveShape): number => {
  switch (shape) {
    case 'plane': return 0.01;
    case 'text': return 1.5;
    case 'torus': return 0.55;
    case 'portal': return PORTAL_H / 2;
    case 'gametable': return GT_REST_Y;
    case 'arcade': return ARC_REST_Y;
    case 'leaderboard': return boardRestY(armedBoardConfig());
    case 'npc': return NPC_PICK_H / 2;
    case 'speaker': return SPK_REST_Y;
    case 'worldgame': return roleRestY(armedWorldGame().role);
    default: return 0.5;
  }
};

// Full PrimitiveItem with the same defaults the existing "Add Shape" flow uses.
const makePrimitive = (
  shape: PrimitiveShape,
  position: [number, number, number],
  rotationY: number,
  overrides: Partial<PrimitiveItem> = {},
): PrimitiveItem => ({
  shape,
  name: shape === 'text' ? 'Text' : shape === 'gametable' ? `${getGameDef(overrides.game)?.label ?? 'Game'} Table` : shape === 'arcade' ? arcadeCabinetName(overrides.arcadeGame) : shape === 'leaderboard' ? 'Leaderboard' : shape === 'worldgame' ? worldGamePieceName(overrides.worldGame) : shape.charAt(0).toUpperCase() + shape.slice(1),
  position,
  rotation: [0, rotationY, 0],
  scale: [1, 1, 1],
  color: shape === 'text' ? '#ffffff' : shape === 'portal' ? '#8b5cf6' : shape === 'gametable' ? '#0f5a3c' : shape === 'speaker' ? '#22272f' : '#888888',
  texturePreset: null,
  textureUrl: null,
  pbrMaps: null,
  textureScale: 2,
  visible: true,
  isLocked: false,
  collidable: false,
  colliderOnly: false,
  opacity: 1,
  metalness: 0,
  roughness: 0.5,
  ...(shape === 'text' ? { text: 'Hello World', fontSize: 1 } : {}),
  ...(shape === 'gametable' ? { game: DEFAULT_GAME } : {}),
  ...(shape === 'speaker' ? { speaker: { ...DEFAULT_SPEAKER, tracks: [] } } : {}),
  ...overrides,
});

// A wall is just a stretched box: local +X runs along the segment, so the yaw
// that maps (1,0,0) onto (dx,0,dz) is atan2(-dz, dx).
const makeWall = (sx: number, sz: number, ex: number, ez: number, name = 'Wall'): PrimitiveItem | null => {
  const dx = ex - sx;
  const dz = ez - sz;
  const len = Math.hypot(dx, dz);
  if (len < 0.05) return null;
  return makePrimitive('box', [sx + dx / 2, WALL_HEIGHT / 2, sz + dz / 2], Math.atan2(-dz, dx), {
    name,
    scale: [len, WALL_HEIGHT, WALL_THICKNESS],
    color: WALL_COLOR,
    collidable: true,
  });
};

// --- Commit helpers: store mutation (zundo-tracked) + multiplayer broadcast ---
const commitPrimitive = (worldId: string | undefined, item: PrimitiveItem): string => {
  const id = newPrimId();
  useWorldStore.getState().addPrimitive(id, item);
  if (worldId) _broadcastAdd(worldId, 'primitive', id, item);
  return id;
};

// Spawn a parts list as a GROUP of primitives: every piece is individually
// editable (color/glow/size), the group moves as one, Ungroup breaks it apart.
const spawnPartsGroup = (
  worldId: string | undefined,
  title: string,
  parts: any[],
  at: [number, number],
  rotY: number,
): void => {
  const def = { title, parts };
  const groupId = `prefab-${Date.now().toString(36)}`;
  const qy = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);
  let firstId: string | null = null;
  for (const part of def.parts) {
    const off = new THREE.Vector3(...part.position).applyQuaternion(qy);
    const qp = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(part.rotation ?? [0, 0, 0])));
    const e = new THREE.Euler().setFromQuaternion(qy.clone().multiply(qp), 'XYZ');
    const id = commitPrimitive(worldId, makePrimitive(part.shape, [at[0] + off.x, part.position[1], at[1] + off.z], 0, {
      name: `${def.title} ${part.name}`,
      rotation: [e.x, e.y, e.z],
      scale: part.scale,
      color: part.color,
      collidable: true,
      opacity: part.opacity ?? 1,
      metalness: part.metalness ?? 0,
      roughness: part.roughness ?? 0.5,
      emissive: part.emissive ?? 0,
      ...(part.seat ? { seat: true } : {}),
      groupId,
    }));
    firstId ??= id;
  }
  if (firstId) useWorldStore.getState().setSelectedObjectId(firstId);
};

const commitPrefab = (
  worldId: string | undefined,
  prefabId: string,
  at: [number, number],
  rotY: number,
): void => {
  const def = PREFABS[prefabId];
  if (def) spawnPartsGroup(worldId, def.title, def.parts, at, rotY);
};

const commitVenue = (
  worldId: string | undefined,
  venueId: string,
  at: [number, number],
  rotY: number,
): void => {
  const def = VENUES.find((v) => v.id === venueId);
  if (!def) return;
  const groupId = `venue-${Date.now().toString(36)}`;
  const s = useWorldStore.getState();

  const shellId = newContentId();
  const shell = {
    url: venueGlbUrl(venueId),
    type: 'model' as const,
    position: [at[0], 0, at[1]] as [number, number, number],
    scale: 1,
    rotation: [0, rotY, 0] as [number, number, number],
    visible: true,
    isLocked: false,
    collidable: true,
    filename: def.title,
    uploadedAt: Date.now(),
    groupId,
  };
  s.addContentItem(shellId, shell);
  if (worldId) _broadcastAdd(worldId, 'content', shellId, shell);

  // Invisible sit markers over the modelled chairs: a GLB can't carry the seat
  // flag per piece, so each clickable seat is its own primitive. Opacity 0 and
  // non-colliding, so they're visible only to the raycast.
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  for (const [sx, sy, sz, syaw] of def.seats) {
    const item = makePrimitive('box', [
      at[0] + sx * cos + sz * sin,
      sy + 0.28,
      at[1] - sx * sin + sz * cos,
    ], rotY + syaw, {
      name: `${def.title} Seat`,
      scale: [0.5, 0.5, 0.5],
      opacity: 0,
      collidable: false,
      seat: true,
      groupId,
    });
    commitPrimitive(worldId, item);
  }
  s.setSelectedObjectId(shellId);
};

// Direct-modelled box: the drawn footprint and pulled height become a plain
// primitive, so it saves, syncs, collides and undoes like anything else.
const commitBoxVolume = (
  worldId: string | undefined,
  a: [number, number],
  b: [number, number],
  height: number,
): void => {
  const w = Math.abs(b[0] - a[0]);
  const d = Math.abs(b[1] - a[1]);
  const h = Math.max(0.05, height);
  if (w < 0.05 || d < 0.05) return;
  const cx = (a[0] + b[0]) / 2;
  const cz = (a[1] + b[1]) / 2;
  const id = commitPrimitive(worldId, makePrimitive('box', [cx, h / 2, cz], 0, {
    name: 'Box',
    scale: [w, h, d],
    color: WALL_COLOR,
    collidable: true,
  }));
  useWorldStore.getState().setSelectedObjectId(id);
};

const commitContent = (
  worldId: string | undefined,
  spec: PlacementSpec,
  position: [number, number, number],
  rotationY: number,
): string | null => {
  if (!spec.url) return null;
  const id = newContentId();
  const type = (spec.contentType || 'model') as 'image' | 'audio' | 'model' | 'splat' | 'ply';
  const item = {
    url: spec.url,
    type,
    position,
    scale: type === 'image' ? 3 : 1,
    rotation: [0, rotationY, 0] as [number, number, number],
    visible: true,
    isLocked: false,
    ...(type === 'model' ? { autoFit: spec.autoFit ?? true } : {}),
    ...(spec.seat ? { seat: true } : {}),
    ...(spec.collidable ? { collidable: true } : {}),
    filename: spec.filename,
    uploadedAt: Date.now(),
  };
  useWorldStore.getState().addContentItem(id, item);
  if (worldId) _broadcastAdd(worldId, 'content', id, item);
  return id;
};

// 4 walls + a floor slab from a dragged rectangle.
const commitRoom = (worldId: string | undefined, a: [number, number], b: [number, number]) => {
  const minX = Math.min(a[0], b[0]);
  const maxX = Math.max(a[0], b[0]);
  const minZ = Math.min(a[1], b[1]);
  const maxZ = Math.max(a[1], b[1]);
  const w = maxX - minX;
  const d = maxZ - minZ;
  if (w < 0.2 || d < 0.2) return;

  // One shared groupId per room: selecting/moving/scaling any wall then acts
  // on the whole room (group behavior lives in the primitive onTransformChange).
  const groupId = `room-${Date.now().toString(36)}`;

  commitPrimitive(worldId, makePrimitive('box', [minX + w / 2, FLOOR_THICKNESS / 2, minZ + d / 2], 0, {
    name: 'Floor',
    scale: [w, FLOOR_THICKNESS, d],
    color: FLOOR_COLOR,
    collidable: true,
    groupId,
  }));

  // Butt-jointed corners: the X-parallel walls extend half a thickness past
  // each corner, and the Z-parallel walls are inset by the same amount to meet
  // them flush — otherwise every outside corner shows a t/2 × t/2 notch.
  const h = WALL_THICKNESS / 2;
  const segments: Array<[number, number, number, number]> = [
    [minX - h, minZ, maxX + h, minZ],
    [maxX, minZ + h, maxX, maxZ - h],
    [maxX + h, maxZ, minX - h, maxZ],
    [minX, maxZ - h, minX, minZ + h],
  ];
  for (const [sx, sz, ex, ez] of segments) {
    const wall = makeWall(sx, sz, ex, ez);
    if (wall) commitPrimitive(worldId, { ...wall, groupId });
  }
};

// Move every other member of a group (shapes AND content models) by the same
// delta — called when one member is dragged, so mixed groups move as one.
const moveGroupSiblings = (
  worldId: string | undefined,
  gid: string,
  excludeId: string,
  dx: number,
  dy: number,
  dz: number,
) => {
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001 && Math.abs(dz) < 0.001) return;
  const s = useWorldStore.getState();
  for (const [pid, p] of Object.entries(s.primitives)) {
    if (pid === excludeId || p.groupId !== gid) continue;
    const np: [number, number, number] = [p.position[0] + dx, p.position[1] + dy, p.position[2] + dz];
    s.updatePrimitive(pid, { position: np });
    if (worldId) _broadcastUpdate(worldId, 'primitive', pid, { position: np });
  }
  for (const [cid, c] of Object.entries(s.contentItems)) {
    if (cid === excludeId || c.groupId !== gid) continue;
    const np: [number, number, number] = [c.position[0] + dx, c.position[1] + dy, c.position[2] + dz];
    s.updateContentItem(cid, { position: np });
    if (worldId) _broadcastUpdate(worldId, 'content', cid, { position: np });
  }
};

// XZ bounds of a set of objects (positions padded by rough footprint extents),
// used by the marquee-select tool for its highlight box and inside-hit test.
const selectionBounds = (ids: string[]): { minX: number; maxX: number; minZ: number; maxZ: number } | null => {
  const s = useWorldStore.getState();
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const id of ids) {
    const p = (s.primitives[id] || s.contentItems[id]) as { position: [number, number, number]; scale: number | [number, number, number] } | undefined;
    if (!p) continue;
    const pad = Array.isArray(p.scale) ? Math.max(p.scale[0], p.scale[2]) / 2 : Math.max(0.5, p.scale || 1);
    minX = Math.min(minX, p.position[0] - pad);
    maxX = Math.max(maxX, p.position[0] + pad);
    minZ = Math.min(minZ, p.position[2] - pad);
    maxZ = Math.max(maxZ, p.position[2] + pad);
  }
  return isFinite(minX) ? { minX, maxX, minZ, maxZ } : null;
};

// Shift a marquee selection (shapes + models) by an XZ delta, with broadcasts.
const moveSelectionBy = (worldId: string | undefined, ids: string[], dx: number, dz: number) => {
  if (Math.abs(dx) < 0.001 && Math.abs(dz) < 0.001) return;
  const s = useWorldStore.getState();
  for (const id of ids) {
    const p = s.primitives[id];
    if (p) {
      const np: [number, number, number] = [p.position[0] + dx, p.position[1], p.position[2] + dz];
      s.updatePrimitive(id, { position: np });
      if (worldId) _broadcastUpdate(worldId, 'primitive', id, { position: np });
      continue;
    }
    const c = s.contentItems[id];
    if (c) {
      const np: [number, number, number] = [c.position[0] + dx, c.position[1], c.position[2] + dz];
      s.updateContentItem(id, { position: np });
      if (worldId) _broadcastUpdate(worldId, 'content', id, { position: np });
    }
  }
};

// ---- Stairs tool ----
// Drag on the ground → a rising run of solid steps along the drag direction.
// Each step is a full-height box column (no floating treads), so the profile
// reads as a staircase from any angle and collision stays simple. All steps
// share a groupId — move/duplicate/scale act on the whole staircase.
const STAIR_RISE = 0.25;      // height gained per step
const STAIR_TREAD = 0.3;      // ideal depth per step (recomputed to fit drag)
const STAIR_WIDTH = 1.5;
const STAIR_COLOR = '#9a8f80';
const STAIR_MAX_STEPS = 40;

// Uniform step layout for a drag of length `len`: returns count + exact tread.
const stairLayout = (len: number) => {
  const steps = Math.max(2, Math.min(STAIR_MAX_STEPS, Math.round(len / STAIR_TREAD)));
  return { steps, tread: len / steps };
};

// Curved stairs: `center` is the pivot, `start` sets radius + first step; the
// run sweeps `sweepDeg` around the pivot, rising per step. Returns step
// descriptors so the drag preview and the commit share the exact same layout.
const curvedStairSteps = (
  center: [number, number],
  start: [number, number],
  sweepDeg: number,
): Array<{ x: number; z: number; rotY: number; h: number; tread: number }> => {
  const rx = start[0] - center[0];
  const rz = start[1] - center[1];
  const r = Math.hypot(rx, rz);
  if (r < 0.8) return [];
  const a0 = Math.atan2(rz, rx);
  const sweep = (sweepDeg * Math.PI) / 180;
  const arc = r * sweep;
  const steps = Math.max(3, Math.min(STAIR_MAX_STEPS, Math.round(arc / STAIR_TREAD)));
  const dA = sweep / steps;
  // Size each tread for the OUTER radius so adjacent steps overlap slightly
  // on the inside rather than gapping on the outside.
  const tread = dA * (r + STAIR_WIDTH / 2) + 0.01;
  const out: Array<{ x: number; z: number; rotY: number; h: number; tread: number }> = [];
  for (let i = 0; i < steps; i++) {
    const a = a0 + (i + 0.5) * dA;
    const tx = -Math.sin(a), tz = Math.cos(a); // tangent (direction of travel)
    out.push({
      x: center[0] + Math.cos(a) * r,
      z: center[1] + Math.sin(a) * r,
      rotY: Math.atan2(-tz, tx),
      h: STAIR_RISE * (i + 1),
      tread,
    });
  }
  return out;
};

const commitCurvedStairs = (
  worldId: string | undefined,
  center: [number, number],
  start: [number, number],
  sweepDeg: number,
) => {
  const steps = curvedStairSteps(center, start, sweepDeg);
  if (steps.length === 0) return;
  const groupId = `stairs-${Date.now().toString(36)}`;
  steps.forEach((st, i) => {
    commitPrimitive(worldId, makePrimitive('box', [st.x, st.h / 2, st.z], st.rotY, {
      name: `Step ${i + 1}`,
      scale: [st.tread, st.h, STAIR_WIDTH],
      color: STAIR_COLOR,
      collidable: true,
      groupId,
    }));
  });
};

const commitStairs = (worldId: string | undefined, a: [number, number], b: [number, number]) => {
  const dx = b[0] - a[0];
  const dz = b[1] - a[1];
  const len = Math.hypot(dx, dz);
  if (len < STAIR_TREAD * 2) return;
  const { steps, tread } = stairLayout(len);
  const ux = dx / len, uz = dz / len;
  const rotY = Math.atan2(-dz, dx);
  const groupId = `stairs-${Date.now().toString(36)}`;
  for (let i = 0; i < steps; i++) {
    const along = (i + 0.5) * tread;
    const h = STAIR_RISE * (i + 1);
    commitPrimitive(worldId, makePrimitive('box', [a[0] + ux * along, h / 2, a[1] + uz * along], rotY, {
      name: `Step ${i + 1}`,
      scale: [tread, h, STAIR_WIDTH],
      color: STAIR_COLOR,
      collidable: true,
      groupId,
    }));
  }
};

// ---- Cut tool (door / window openings) ----
// Openings are cut by REPLACING the wall with segments around the hole — every
// piece stays a plain box primitive, so persistence, multiplayer, collision,
// grouping and undo all work unchanged (no CSG geometry involved).
const OPENINGS: Record<string, { w: number; h: number; b: number; arch?: boolean }> = {
  door: { w: 1.0, h: 2.1, b: 0 },      // floor-based doorway
  window: { w: 1.2, h: 1.2, b: 0.9 },  // sill at 0.9m
  arch: { w: 1.1, h: 2.1, b: 0, arch: true },        // door-sized arched opening
  'arch-wide': { w: 2.4, h: 2.6, b: 0, arch: true }, // wide arched opening
};

// Cut an opening into primitive `primId` at local-X offset `u` (metres from the
// wall's center along its length). Replaces the wall with up to 4 segments.
const commitCutOpening = (
  worldId: string | undefined,
  primId: string,
  u: number,
  opening: { w: number; h: number; b: number; arch?: boolean },
) => {
  const s = useWorldStore.getState();
  const prim = s.primitives[primId];
  if (!prim || prim.shape !== 'box') return;
  const [L, H, T] = prim.scale;
  const theta = prim.rotation[1];
  const dirX = Math.cos(theta), dirZ = -Math.sin(theta); // world dir of local +X
  const bottomY = prim.position[1] - H / 2;

  const w = Math.min(opening.w, L - 0.02);
  const h = Math.min(opening.h, H - 0.02);
  const b = Math.min(opening.b, Math.max(0, H - h - 0.02));
  const uc = Math.max(-L / 2 + w / 2, Math.min(L / 2 - w / 2, u));

  // Segments in wall-local terms: cx = center along local X, y0 = bottom, hh = height
  const segs: Array<{ name: string; cx: number; len: number; y0: number; hh: number }> = [];
  const leftLen = (uc - w / 2) - (-L / 2);
  if (leftLen > 0.04) segs.push({ name: `${prim.name}`, cx: (-L / 2 + (uc - w / 2)) / 2, len: leftLen, y0: 0, hh: H });
  const rightLen = L / 2 - (uc + w / 2);
  if (rightLen > 0.04) segs.push({ name: `${prim.name}`, cx: ((uc + w / 2) + L / 2) / 2, len: rightLen, y0: 0, hh: H });
  if (opening.arch) {
    // Smooth arch: boxes laid TANGENT along the curve (like voussoir stones),
    // so the opening's underside is a clean tangent polygon instead of steps.
    // Each box is thick enough to bury its outer corners in the side walls /
    // header, which hide the overlaps. radius w/2, flattened if wall is short.
    const R = w / 2;
    const spring = Math.max(0.2, h - R); // where the curve starts
    const k = (h - spring) / R;          // vertical squash (1 = circular)
    const N = 9;
    const t = Math.max(0.08, 0.45 * R);  // radial thickness (covers corners at 45°)
    const len = 2 * (R + t) * Math.tan(Math.PI / (2 * N)) + 0.02;
    const rMid = R + t / 2;
    for (let j = 0; j < N; j++) {
      const phi = ((j + 0.5) * Math.PI) / N; // 0 = left springline → π = right
      const cx = uc - rMid * Math.cos(phi);
      const yc = b + spring + k * rMid * Math.sin(phi);
      const rz = Math.atan2(k * Math.cos(phi), Math.sin(phi)); // tangent tilt
      const id = newPrimId();
      const item: PrimitiveItem = {
        ...prim,
        name: `${prim.name} Arch`,
        position: [prim.position[0] + dirX * cx, bottomY + yc, prim.position[2] + dirZ * cx],
        rotation: [0, theta, rz], // XYZ euler: wall yaw, then in-plane tilt
        scale: [len, t, T],
      };
      s.addPrimitive(id, item);
      if (worldId) _broadcastAdd(worldId, 'primitive', id, item);
    }
  }
  const headerH = H - (b + h);
  if (headerH > 0.04) segs.push({ name: `${prim.name} Header`, cx: uc, len: w, y0: b + h, hh: headerH });
  if (b > 0.04) segs.push({ name: `${prim.name} Sill`, cx: uc, len: w, y0: 0, hh: b });
  if (segs.length === 0) return;

  for (const seg of segs) {
    const id = newPrimId();
    const item: PrimitiveItem = {
      ...prim,
      name: seg.name,
      position: [prim.position[0] + dirX * seg.cx, bottomY + seg.y0 + seg.hh / 2, prim.position[2] + dirZ * seg.cx],
      scale: [seg.len, seg.hh, T],
    };
    s.addPrimitive(id, item);
    if (worldId) _broadcastAdd(worldId, 'primitive', id, item);
  }
  s.deletePrimitive(primId);
  if (worldId) _broadcastDelete(worldId, 'primitive', primId);
};

// ---- Ghost preview + ground-plane pointer handling ----
const BuildToolsLayer: React.FC<{ worldId?: string; canBuild: boolean }> = ({ worldId, canBuild }) => {
  const isPresenting = usePresenting();
  const { camera, gl, scene } = useThree();
  const buildTool = useWorldStore((s) => s.buildTool);
  const placementSpec = useWorldStore((s) => s.placementSpec);
  const placementRotY = useWorldStore((s) => s.placementRotY);
  const stairSweep = useWorldStore((s) => s.stairSweep);
  const snapSettings = useWorldStore((s) => s.snapSettings);
  const primsAll = useWorldStore((s) => s.primitives);

  const [cursor, setCursor] = useState<[number, number]>([0, 0]);
  // Height of the surface under the cursor while placing a shape (a balcony,
  // a stage, a mezzanine) — 0 on open ground.
  const [cursorY, setCursorY] = useState(0);
  // Placing shapes raycasts the whole scene on every pointer move; give big
  // venue meshes a BVH once so that stays smooth.
  const sceneForBvh = useThree((st) => st.scene);
  const placingPrimitive = buildTool === 'place' && placementSpec?.kind === 'primitive';
  useEffect(() => {
    if (!placingPrimitive) return;
    sceneForBvh.traverse((o: any) => {
      const g = o.isMesh && !o.isSkinnedMesh ? o.geometry : null;
      if (g?.computeBoundsTree && !g.boundsTree && (g.index?.count ?? g.attributes?.position?.count ?? 0) > 3000) {
        try { g.computeBoundsTree(); } catch { /* non-indexable */ }
      }
    });
  }, [placingPrimitive, sceneForBvh]);
  const [dragStart, setDragStart] = useState<[number, number] | null>(null);
  // Box tool is a two-stage gesture: drag a footprint on the grid, release,
  // then move the pointer to pull the height and click to commit. `boxBase`
  // holds the locked footprint while stage 2 runs.
  const [boxBase, setBoxBase] = useState<{ a: [number, number]; b: [number, number] } | null>(null);
  // Live stroke being painted. Held in a ref because it changes on every
  // pointer move; paintTick exists purely to re-render the preview.
  const painting = useRef<[number, number][] | null>(null);
  const [, setPaintTick] = useState(0);
  const [boxHeight, setBoxHeight] = useState(1);
  // Cut tool hover: which wall + where along it the opening would land.
  const [cutHover, setCutHover] = useState<{ id: string; u: number } | null>(null);
  // Marquee-select: 'marquee' drags a selection box, 'move' drags the selected
  // set (started inside the selection's bounds).
  const selectMode = useRef<'marquee' | 'move'>('marquee');
  const selectedIds = useWorldStore((s) => s.selectedObjectIds);
  const dragging = useRef(false);
  const chainEnd = useRef<[number, number] | null>(null);
  // Where the left button went down, in refs so it survives the listener
  // re-registration that a cursor/dragStart state change triggers. Without this
  // an orbit drag would be misread as a click and stamp an object.
  const downAt = useRef<{ x: number; y: number } | null>(null);

  // Only the ground-drawing tools need pointer capture; paint works through the
  // normal object click path.
  const active = canBuild && !isPresenting &&
    (buildTool === 'place' || buildTool === 'wall' || buildTool === 'room' ||
     buildTool === 'door' || buildTool === 'window' || buildTool === 'arch' || buildTool === 'arch-wide' || buildTool === 'stairs' || buildTool === 'box' ||
     buildTool === 'select' || buildTool === 'foliage');

  const snapGrid = snapSettings.grid;
  const snapSize = snapSettings.gridSize;

  useEffect(() => {
    // Reset transient drawing state whenever the tool changes.
    setDragStart(null);
    dragging.current = false;
    chainEnd.current = null;
    setCutHover(null);
  }, [buildTool]);

  useEffect(() => {
    if (!active) return;
    const el = gl.domElement;
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();

    // Raycast the pointer onto the y=0 plane. Using a math plane (rather than a
    // catcher mesh) means the ghost keeps tracking even when the pointer is over
    // an existing object, and no R3F propagation can swallow it.
    // Height stage: project the ray onto a VERTICAL plane through the footprint
    // centre whose normal faces the camera. Mapping screen-Y straight to world-Y
    // feels sticky at oblique angles; this tracks the pointer wherever you orbit.
    const toHeight = (clientX: number, clientY: number, cx: number, cz: number): number | null => {
      const rect = el.getBoundingClientRect();
      ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const n = new THREE.Vector3();
      camera.getWorldDirection(n);
      n.y = 0;
      if (n.lengthSq() < 1e-6) return null;
      n.normalize();
      const vplane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, new THREE.Vector3(cx, 0, cz));
      const hitV = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(vplane, hitV)) return null;
      return Math.max(snapSize > 0 && snapGrid ? snapSize : 0.05, snapAxis(hitV.y, snapGrid, snapSize));
    };

    const toGround = (clientX: number, clientY: number): [number, number] | null => {
      const rect = el.getBoundingClientRect();
      ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      if (!raycaster.ray.intersectPlane(plane, hit)) return null;
      return [snapAxis(hit.x, snapGrid, snapSize), snapAxis(hit.z, snapGrid, snapSize)];
    };

    // Shapes (and portals) stand on whatever you point at — a balcony, a
    // stage, an upper floor — not just the y=0 ground: the first upward-facing
    // surface under the cursor, ignoring the ghost, hidden colliders, avatars
    // and the transform gizmo.
    const _n = new THREE.Vector3();
    const _nm = new THREE.Matrix3();
    const surfaceAt = (clientX: number, clientY: number): { x: number; z: number; y: number } | null => {
      const rect = el.getBoundingClientRect();
      ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      for (const h of raycaster.intersectObjects(scene.children, true)) {
        const o = h.object as any;
        if (!o.isMesh || o.isSkinnedMesh || o.isTroikaText || !h.face) continue;
        const mat = Array.isArray(o.material) ? o.material[0] : o.material;
        if (mat && mat.visible === false) continue;
        let skip = false;
        for (let a: any = o; a; a = a.parent) {
          if (!a.visible || a.name === 'build-ghost' || String(a.type).startsWith('TransformControls')) { skip = true; break; }
        }
        if (skip) continue;
        _n.copy(h.face.normal).applyMatrix3(_nm.getNormalMatrix(o.matrixWorld)).normalize();
        if (_n.y < 0.5) continue;
        return { x: snapAxis(h.point.x, snapGrid, snapSize), z: snapAxis(h.point.z, snapGrid, snapSize), y: h.point.y };
      }
      return null;
    };
    const placingShape = buildTool === 'place' && placementSpec?.kind === 'primitive';

    // Cut tools: raycast against wall meshes (box primitives) instead of the
    // ground, and compute where along the wall's length the opening would sit.
    const toWall = (clientX: number, clientY: number): { id: string; u: number } | null => {
      const rect = el.getBoundingClientRect();
      ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      const prims = useWorldStore.getState().primitives;
      const spec = OPENINGS[buildTool];
      for (const hh of hits) {
        let o: THREE.Object3D | null = hh.object;
        let pid: string | null = null;
        // Identify the primitive by mesh NAME (`primitive-<id>`): userData gets
        // replaced by R3F on re-render (the mesh declares a userData prop), so
        // userData.objectId is unreliable — the name prop is stable.
        while (o) {
          if (o.name && o.name.startsWith('primitive-')) {
            const candidate = o.name.slice('primitive-'.length);
            if (prims[candidate]) { pid = candidate; break; }
          }
          o = o.parent;
        }
        if (!pid) continue;
        const pr = prims[pid];
        if (pr.shape !== 'box' || pr.isLocked) continue;
        if (pr.scale[1] < spec.b + 0.3) continue; // too short to cut (e.g. floors)
        const theta = pr.rotation[1];
        let u = (hh.point.x - pr.position[0]) * Math.cos(theta) + (hh.point.z - pr.position[2]) * -Math.sin(theta);
        if (snapGrid) u = Math.round(u / snapSize) * snapSize;
        const wHalf = Math.min(spec.w, pr.scale[0] - 0.02) / 2;
        if (wHalf <= 0.02) continue; // wall shorter than the opening
        u = Math.max(-pr.scale[0] / 2 + wHalf, Math.min(pr.scale[0] / 2 - wHalf, u));
        return { id: pid, u };
      }
      return null;
    };

    const onMove = (ev: PointerEvent) => {
      // Foliage brush: append to the stroke while the button is held. Points are
      // only added past a minimum spacing — sampling every pointer event would
      // store hundreds of near-identical points and bloat the saved stroke
      // without changing where a single plant lands.
      if (buildTool === 'foliage') {
        const p = toGround(ev.clientX, ev.clientY);
        if (!p) return;
        setCursor(p);
        if (painting.current) {
          const pts = painting.current;
          const last = pts[pts.length - 1];
          if (Math.hypot(p[0] - last[0], p[1] - last[1]) > 0.4) pts.push(p);
          setPaintTick((t) => t + 1);
        }
        return;
      }
      // Height stage owns the pointer: track it and skip the ground cursor.
      if (buildTool === 'box' && boxBase) {
        const cx = (boxBase.a[0] + boxBase.b[0]) / 2;
        const cz = (boxBase.a[1] + boxBase.b[1]) / 2;
        const h = toHeight(ev.clientX, ev.clientY, cx, cz);
        if (h != null) setBoxHeight(h);
        return;
      }
      if (buildTool === 'door' || buildTool === 'window' || buildTool === 'arch' || buildTool === 'arch-wide') {
        const hv = toWall(ev.clientX, ev.clientY);
        setCutHover((c) => (c?.id === hv?.id && c?.u === hv?.u ? c : hv));
        return;
      }
      if (placingShape) {
        const sf = surfaceAt(ev.clientX, ev.clientY);
        if (sf) {
          setCursor((c) => (c[0] === sf.x && c[1] === sf.z ? c : [sf.x, sf.z]));
          setCursorY(sf.y);
          return;
        }
        setCursorY(0);
      }
      const p = toGround(ev.clientX, ev.clientY);
      if (!p) return;
      setCursor((c) => (c[0] === p[0] && c[1] === p[1] ? c : p));
    };

    const onDown = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      downAt.current = { x: ev.clientX, y: ev.clientY };
      // Box stage 2 must be handled BEFORE the ground raycast: pulling a tall
      // box points the cursor above the horizon, where the ground plane isn't
      // hit at all, and the commit click would silently do nothing.
      if (buildTool === 'box' && boxBase) {
        commitBoxVolume(worldId, boxBase.a, boxBase.b, boxHeight);
        setBoxBase(null);
        return;
      }
      const p = toGround(ev.clientX, ev.clientY);
      if (!p) return;
      setCursor(p);
      if (buildTool === 'foliage') {
        painting.current = [p];
        setPaintTick((t) => t + 1);
        return;
      }
      if (buildTool === 'select') {
        // Press inside the current selection's bounds drags it; elsewhere
        // starts a fresh marquee.
        const b = selectedIds.length ? selectionBounds(selectedIds) : null;
        selectMode.current = b && p[0] >= b.minX && p[0] <= b.maxX && p[1] >= b.minZ && p[1] <= b.maxZ
          ? 'move' : 'marquee';
        dragging.current = true;
        setDragStart(p);
        return;
      }
      if (buildTool !== 'wall' && buildTool !== 'room' && buildTool !== 'stairs' && buildTool !== 'box') return;
      let start = p;
      // Wall chaining: starting near the previous endpoint snaps onto it exactly.
      if (buildTool === 'wall' && chainEnd.current) {
        const d = Math.hypot(p[0] - chainEnd.current[0], p[1] - chainEnd.current[1]);
        if (d <= Math.max(0.75, snapSize * 2)) start = chainEnd.current;
      }
      dragging.current = true;
      setDragStart(start);
    };

    const onUp = (ev: PointerEvent) => {
      if (ev.button !== 0) return;
      let p = toGround(ev.clientX, ev.clientY);
      const down = downAt.current;
      downAt.current = null;

      // Commit the painted stroke. What gets stored is the path and the brush
      // settings, never the plants themselves.
      if (buildTool === 'foliage' && painting.current) {
        const pts = painting.current;
        painting.current = null;
        setPaintTick((t) => t + 1);
        const b = useWorldStore.getState().foliageBrush;
        useWorldStore.getState().addFoliageStroke({
          id: `fol-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
          points: pts,
          erase: b.erase,
          radius: b.radius,
          // Trees need a fraction of the ground-cover density. Sharing one
          // slider value plants a solid wall of trunks: a tree occupies about
          // 2m across, so a density tuned for grass tufts overlaps them ~20x.
          density: b.trees ? b.density * 0.045 : b.density,
          species: b.trees ? [1] : b.species,
          scaleMin: b.trees ? 0.85 : 0.75,
          scaleMax: b.trees ? 1.5 : 1.35,
          y: 0,
          // The seed is drawn once, here, and saved. Re-rolling it at render
          // time would rearrange every world on every load.
          seed: Math.floor(Math.random() * 1e9) || 1,
          trees: b.trees,
        });
        return;
      }

      if (buildTool === 'select') {
        const wasDragging = dragging.current;
        dragging.current = false;
        if (wasDragging && dragStart && p) {
          const s = useWorldStore.getState();
          if (selectMode.current === 'move') {
            moveSelectionBy(worldId, s.selectedObjectIds, p[0] - dragStart[0], p[1] - dragStart[1]);
          } else {
            const minX = Math.min(dragStart[0], p[0]), maxX = Math.max(dragStart[0], p[0]);
            const minZ = Math.min(dragStart[1], p[1]), maxZ = Math.max(dragStart[1], p[1]);
            if (maxX - minX > 0.2 || maxZ - minZ > 0.2) {
              const ids: string[] = [];
              for (const [pid, pr] of Object.entries(s.primitives)) {
                if (pr.visible && pr.position[0] >= minX && pr.position[0] <= maxX && pr.position[2] >= minZ && pr.position[2] <= maxZ) ids.push(pid);
              }
              for (const [cid, c] of Object.entries(s.contentItems)) {
                if (c.visible && c.position[0] >= minX && c.position[0] <= maxX && c.position[2] >= minZ && c.position[2] <= maxZ) ids.push(cid);
              }
              s.setMultiSelect(ids);
            } else {
              s.clearMultiSelect(); // stray click on empty ground deselects
            }
          }
        }
        setDragStart(null);
      setBoxBase(null);
        return;
      }

      if (buildTool === 'box') {
        // Stage 1 ended: lock the footprint and hand over to the height stage.
        const wasDragging = dragging.current;
        dragging.current = false;
        if (wasDragging && dragStart && p) {
          const w = Math.abs(p[0] - dragStart[0]), d = Math.abs(p[1] - dragStart[1]);
          if (w > 0.05 && d > 0.05) { setBoxBase({ a: dragStart, b: p }); setBoxHeight(Math.max(snapSize, 1)); }
        }
        setDragStart(null);
        return;
      }
      if (buildTool === 'wall' || buildTool === 'room' || buildTool === 'stairs') {
        const wasDragging = dragging.current;
        dragging.current = false;
        // dragStart can still be null here if the press and release landed in
        // the same frame (a stray click) — commit nothing, just clear.
        if (wasDragging && dragStart) {
          const end = p || dragStart;
          if (buildTool === 'wall') {
            const wall = makeWall(dragStart[0], dragStart[1], end[0], end[1]);
            if (wall) {
              commitPrimitive(worldId, wall);
              chainEnd.current = end;
            }
          } else if (buildTool === 'room') {
            commitRoom(worldId, dragStart, end);
          } else if (stairSweep > 0) {
            commitCurvedStairs(worldId, dragStart, end, stairSweep);
          } else {
            commitStairs(worldId, dragStart, end);
          }
        }
        setDragStart(null);
        return;
      }

      // Cut tools: a click (not an orbit drag) on a hovered wall cuts the
      // opening. The tool stays armed for more cuts.
      if ((buildTool === 'door' || buildTool === 'window' || buildTool === 'arch' || buildTool === 'arch-wide') && down &&
          Math.hypot(ev.clientX - down.x, ev.clientY - down.y) < 5) {
        const hv = toWall(ev.clientX, ev.clientY);
        if (hv) {
          commitCutOpening(worldId, hv.id, hv.u, OPENINGS[buildTool]);
          setCutHover(null);
        }
        return;
      }

      // Place mode: a click that started on the canvas and barely moved (i.e.
      // not an orbit drag) stamps the ghost — and the tool stays armed so you
      // can keep clicking to repeat-place.
      if (buildTool === 'place' && placementSpec && p && down &&
          Math.hypot(ev.clientX - down.x, ev.clientY - down.y) < 5) {
        const sf = placementSpec.kind === 'primitive' ? surfaceAt(ev.clientX, ev.clientY) : null;
        const baseY = sf ? sf.y : 0;
        if (sf) p = [sf.x, sf.z];
        if (placementSpec.kind === 'primitive' && placementSpec.shape === 'portal') {
          // One portal end per click; the tool then switches off and selects
          // it, so you can look around freely. The exit is placed separately
          // (Add Shape ▸ Portal exit, or "Place exit" in the portal's panel).
          const at: [number, number, number] = [p[0], baseY + shapeRestY('portal'), p[1]];
          const exitFor = placementSpec.portalExitFor;
          const id = commitPrimitive(worldId, makePrimitive('portal', at, placementRotY, exitFor
            ? { name: 'Portal (exit)', portalLink: exitFor }
            : { name: 'Portal (entrance)', portalLink: newPortalLink() }));
          useWorldStore.getState().setBuildTool('none');
          // Select it once this click has finished — the canvas's "clicked
          // empty space" handler runs after us and would clear it.
          // Select it but leave the camera where it is: you just clicked the
          // spot, and flying to an exit out on a terrace can park the camera
          // inside the building's walls.
          setTimeout(() => { skipFocusFor = id; useWorldStore.getState().setSelectedObjectId(id); }, 80);
        } else if (placementSpec.kind === 'primitive' && placementSpec.shape === 'npc' && placementSpec.npc) {
          // One NPC per click (each is its own character), then select it.
          const npc = placementSpec.npc;
          const id = commitPrimitive(worldId, makePrimitive('npc', [p[0], baseY + shapeRestY('npc'), p[1]], placementRotY, { name: npc.name, npc }));
          if (worldId && npc.status === 'building' && npc.buildId) watchNpcBuild(worldId, id, npc.buildId);
          useWorldStore.getState().setBuildTool('none');
          setTimeout(() => { skipFocusFor = id; useWorldStore.getState().setSelectedObjectId(id); }, 80);
        } else if (placementSpec.kind === 'primitive') {
          const shape = placementSpec.shape || 'box';
          commitPrimitive(worldId, makePrimitive(shape, [p[0], baseY + shapeRestY(shape), p[1]], placementRotY,
            shape === 'gametable' ? { game: placementSpec.game || DEFAULT_GAME }
              : shape === 'arcade' ? { arcadeGame: placementSpec.arcadeGame || armedArcade() || undefined }
              : shape === 'leaderboard' ? { board: { ...armedBoardConfig(), ...(placementSpec.board ?? {}) } }
              : shape === 'worldgame' ? { worldGame: newWorldGameConfig(armedWorldGame().role, armedWorldGame().game), scale: [...ROLE_SCALE[armedWorldGame().role]] as [number, number, number] }
              : {}));
        } else if (placementSpec.kind === 'prefab' && placementSpec.prefabId) {
          commitPrefab(worldId, placementSpec.prefabId, [p[0], p[1]], placementRotY);
        } else if (placementSpec.kind === 'venue' && placementSpec.venueId) {
          commitVenue(worldId, placementSpec.venueId, [p[0], p[1]], placementRotY);
        } else if (placementSpec.kind === 'generated' && placementSpec.parts) {
          spawnPartsGroup(worldId, placementSpec.label || 'Building', placementSpec.parts, [p[0], p[1]], placementRotY);
          // Every stamp is unique: regenerate so the NEXT ghost is a new variation
          if (placementSpec.buildingKind) {
            const next = generateBuilding(placementSpec.buildingKind as BuildingKind);
            useWorldStore.getState().setPlacementSpec({ ...placementSpec, parts: next.parts });
          }
        } else {
          commitContent(worldId, placementSpec, [p[0], 0, p[1]], placementRotY);
        }
      }
    };

    const onLeave = () => {
      dragging.current = false;
      setDragStart(null);
    };

    const onContextMenu = (ev: MouseEvent) => {
      // Right-click always exits the armed tool.
      ev.preventDefault();
      ev.stopPropagation();
      useWorldStore.getState().setBuildTool('none');
    };

    const prevCursor = el.style.cursor;
    el.style.cursor = 'crosshair';
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('pointerup', onUp);
    return () => {
      el.style.cursor = prevCursor;
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('contextmenu', onContextMenu, true);
      window.removeEventListener('pointerup', onUp);
    };
  }, [active, buildTool, placementSpec, placementRotY, stairSweep, snapGrid, snapSize, dragStart, boxBase, boxHeight, worldId, camera, gl, scene, selectedIds]);

  if (!active) return null;

  const ghostMat = (opacity: number) => (
    <meshBasicMaterial color={GHOST_COLOR} transparent opacity={opacity} depthWrite={false} />
  );

  // Snapped-cursor puck so you always know exactly where the click will land.
  const cursorMarker = (
    <mesh position={[cursor[0], 0.02, cursor[1]]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={4}>
      <ringGeometry args={[0.12, 0.2, 24]} />
      <meshBasicMaterial color={GHOST_COLOR} transparent opacity={0.85} depthWrite={false} side={THREE.DoubleSide} />
    </mesh>
  );

  let preview: React.ReactNode = null;

  // Foliage brush: a ring at the cursor showing the exact area that will be
  // planted, plus a trail of the stroke so far. Painting blind and only seeing
  // the result on release makes density impossible to judge.
  if (buildTool === 'foliage') {
    const br = useWorldStore.getState().foliageBrush;
    const trail = painting.current || [];
    // Erasing is destructive, so it gets its own colour rather than sharing the
    // paint cursor — the mode has to be obvious before the drag, not after.
    const ringColor = br.erase ? '#ff6b6b' : GHOST_COLOR;
    preview = (
      <group>
        <mesh position={[cursor[0], 0.02, cursor[1]]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={6}>
          <ringGeometry args={[Math.max(0.05, br.radius - 0.06), br.radius, 40]} />
          <meshBasicMaterial color={ringColor} transparent opacity={0.9} depthWrite={false} depthTest={false} />
        </mesh>
        {trail.map((pt, i) => (
          <mesh key={i} position={[pt[0], 0.015, pt[1]]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={5}>
            <circleGeometry args={[br.radius, 20]} />
            <meshBasicMaterial color={ringColor} transparent opacity={0.10} depthWrite={false} />
          </mesh>
        ))}
      </group>
    );
  }

  // Box tool ghost. Stage 1 shows the footprint growing under the cursor;
  // stage 2 shows the volume rising. A live readout sits above it throughout —
  // seeing the number as you drag is what removes the guess-then-correct loop.
  if (buildTool === 'box' && (dragStart || boxBase)) {
    const a = boxBase ? boxBase.a : dragStart!;
    const b = boxBase ? boxBase.b : cursor;
    const w = Math.abs(b[0] - a[0]);
    const d = Math.abs(b[1] - a[1]);
    const h = boxBase ? boxHeight : 0.02;
    const cx = (a[0] + b[0]) / 2;
    const cz = (a[1] + b[1]) / 2;
    const label = boxBase
      ? `${w.toFixed(2)} x ${d.toFixed(2)} x ${h.toFixed(2)}`
      : `${w.toFixed(2)} x ${d.toFixed(2)}`;
    preview = (
      <group>
        <mesh position={[cx, h / 2, cz]} scale={[Math.max(w, 0.02), Math.max(h, 0.02), Math.max(d, 0.02)]} renderOrder={5}>
          <boxGeometry args={[1, 1, 1]} />
          {ghostMat(boxBase ? 0.35 : 0.5)}
        </mesh>
        <mesh position={[cx, h / 2, cz]} scale={[Math.max(w, 0.02) + 0.004, Math.max(h, 0.02) + 0.004, Math.max(d, 0.02) + 0.004]} renderOrder={6}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color={GHOST_COLOR} wireframe transparent opacity={0.85} depthWrite={false} />
        </mesh>
        {(w > 0.05 || d > 0.05) && (
          <Text
            position={[cx, h + 0.55, cz]}
            fontSize={0.42}
            color="#ffffff"
            outlineWidth={0.045}
            outlineColor="#06131a"
            anchorX="center"
            anchorY="middle"
            renderOrder={20}
            material-depthTest={false}
          >
            {label}
          </Text>
        )}
      </group>
    );
  }

  if (buildTool === 'place' && placementSpec) {
    if (placementSpec.kind === 'primitive') {
      const shape = placementSpec.shape || 'box';
      preview = (
        <mesh
          name="build-ghost"
          position={[cursor[0], cursorY + shapeRestY(shape), cursor[1]]}
          rotation={[shape === 'plane' ? -Math.PI / 2 : 0, placementRotY, 0]}
          renderOrder={5}
        >
          <PrimitiveGeometry shape={shape === 'text' ? 'box' : shape} game={placementSpec.game} board={placementSpec.board} />
          {ghostMat(0.4)}
        </mesh>
      );
    } else if ((placementSpec.kind === 'prefab' && placementSpec.prefabId && PREFABS[placementSpec.prefabId]) || (placementSpec.kind === 'generated' && placementSpec.parts)) {
      // Prefab ghost: every part rendered translucent at its real offset,
      // so what you see is exactly what stamps down.
      preview = (
        <group position={[cursor[0], 0, cursor[1]]} rotation={[0, placementRotY, 0]}>
          {(placementSpec.kind === 'generated' ? placementSpec.parts! : PREFABS[placementSpec.prefabId!].parts).map((pt: any, i: number) => (
            <mesh key={i} position={pt.position} rotation={pt.rotation ?? [0, 0, 0]} scale={pt.scale} renderOrder={5}>
              <PrimitiveGeometry shape={pt.shape} />
              {ghostMat(0.35)}
            </mesh>
          ))}
        </group>
      );
    } else {
      // Content models are normalized by ContentDisplay's autoFit, so a unit
      // ghost box is an honest stand-in and costs nothing to render.
      preview = (
        <group position={[cursor[0], 0, cursor[1]]} rotation={[0, placementRotY, 0]}>
          <mesh position={[0, 0.5, 0]} renderOrder={5}>
            <boxGeometry args={[1, 1, 1]} />
            {ghostMat(0.25)}
          </mesh>
          <mesh position={[0, 0.5, 0]} renderOrder={6}>
            <boxGeometry args={[1.001, 1.001, 1.001]} />
            <meshBasicMaterial color={GHOST_COLOR} wireframe transparent opacity={0.8} depthWrite={false} />
          </mesh>
        </group>
      );
    }
  } else if (buildTool === 'wall' && dragStart) {
    const dx = cursor[0] - dragStart[0];
    const dz = cursor[1] - dragStart[1];
    const len = Math.hypot(dx, dz);
    if (len >= 0.05) {
      preview = (
        <mesh
          position={[dragStart[0] + dx / 2, WALL_HEIGHT / 2, dragStart[1] + dz / 2]}
          rotation={[0, Math.atan2(-dz, dx), 0]}
          scale={[len, WALL_HEIGHT, WALL_THICKNESS]}
          renderOrder={5}
        >
          <boxGeometry args={[1, 1, 1]} />
          {ghostMat(0.4)}
        </mesh>
      );
    }
  } else if (buildTool === 'room' && dragStart) {
    const minX = Math.min(dragStart[0], cursor[0]);
    const maxX = Math.max(dragStart[0], cursor[0]);
    const minZ = Math.min(dragStart[1], cursor[1]);
    const maxZ = Math.max(dragStart[1], cursor[1]);
    const w = Math.max(maxX - minX, 0.001);
    const d = Math.max(maxZ - minZ, 0.001);
    // Same butt-jointed corner layout as commitRoom, so the preview matches.
    const h = WALL_THICKNESS / 2;
    const segs: Array<[number, number, number, number]> = [
      [minX - h, minZ, maxX + h, minZ],
      [maxX, minZ + h, maxX, maxZ - h],
      [maxX + h, maxZ, minX - h, maxZ],
      [minX, maxZ - h, minX, minZ + h],
    ];
    preview = (
      <group>
        <mesh position={[minX + w / 2, FLOOR_THICKNESS / 2, minZ + d / 2]} scale={[w, FLOOR_THICKNESS, d]} renderOrder={5}>
          <boxGeometry args={[1, 1, 1]} />
          {ghostMat(0.3)}
        </mesh>
        {segs.map(([sx, sz, ex, ez], i) => {
          const sdx = ex - sx;
          const sdz = ez - sz;
          const slen = Math.hypot(sdx, sdz);
          if (slen < 0.05) return null;
          return (
            <mesh
              key={i}
              position={[sx + sdx / 2, WALL_HEIGHT / 2, sz + sdz / 2]}
              rotation={[0, Math.atan2(-sdz, sdx), 0]}
              scale={[slen, WALL_HEIGHT, WALL_THICKNESS]}
              renderOrder={5}
            >
              <boxGeometry args={[1, 1, 1]} />
              {ghostMat(0.4)}
            </mesh>
          );
        })}
      </group>
    );
  } else if (buildTool === 'stairs' && dragStart) {
    if (stairSweep > 0) {
      const steps = curvedStairSteps(dragStart, cursor, stairSweep);
      preview = (
        <group>
          {/* pivot marker at the drag start (curve center) */}
          <mesh position={[dragStart[0], 0.03, dragStart[1]]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={5}>
            <ringGeometry args={[0.1, 0.18, 20]} />
            <meshBasicMaterial color={GHOST_COLOR} transparent opacity={0.9} depthWrite={false} side={THREE.DoubleSide} />
          </mesh>
          {steps.map((st, i) => (
            <mesh key={i} position={[st.x, st.h / 2, st.z]} rotation={[0, st.rotY, 0]} scale={[st.tread, st.h, STAIR_WIDTH]} renderOrder={5}>
              <boxGeometry args={[1, 1, 1]} />
              {ghostMat(0.4)}
            </mesh>
          ))}
        </group>
      );
    } else {
      const sdx = cursor[0] - dragStart[0];
      const sdz = cursor[1] - dragStart[1];
      const slen = Math.hypot(sdx, sdz);
      if (slen >= STAIR_TREAD * 2) {
        const { steps, tread } = stairLayout(slen);
        const ux = sdx / slen, uz = sdz / slen;
        const rotY = Math.atan2(-sdz, sdx);
        preview = (
          <group>
            {Array.from({ length: steps }, (_, i) => {
              const along = (i + 0.5) * tread;
              const h = STAIR_RISE * (i + 1);
              return (
                <mesh key={i} position={[dragStart[0] + ux * along, h / 2, dragStart[1] + uz * along]} rotation={[0, rotY, 0]} scale={[tread, h, STAIR_WIDTH]} renderOrder={5}>
                  <boxGeometry args={[1, 1, 1]} />
                  {ghostMat(0.4)}
                </mesh>
              );
            })}
          </group>
        );
      }
    }
  } else if (buildTool === 'select') {
    const parts: React.ReactNode[] = [];
    // Live marquee rectangle while dragging one out.
    if (dragging.current && dragStart && selectMode.current === 'marquee') {
      const w = Math.max(Math.abs(cursor[0] - dragStart[0]), 0.01);
      const d = Math.max(Math.abs(cursor[1] - dragStart[1]), 0.01);
      parts.push(
        <mesh key="marquee" position={[(dragStart[0] + cursor[0]) / 2, 0.04, (dragStart[1] + cursor[1]) / 2]} scale={[w, 0.02, d]} renderOrder={5}>
          <boxGeometry args={[1, 1, 1]} />
          {ghostMat(0.18)}
        </mesh>
      );
    }
    // Selection bounds highlight — follows the cursor while move-dragging.
    if (selectedIds.length) {
      const b = selectionBounds(selectedIds);
      if (b) {
        const ox = dragging.current && dragStart && selectMode.current === 'move' ? cursor[0] - dragStart[0] : 0;
        const oz = dragging.current && dragStart && selectMode.current === 'move' ? cursor[1] - dragStart[1] : 0;
        parts.push(
          <mesh key="selbox" position={[(b.minX + b.maxX) / 2 + ox, 0.05, (b.minZ + b.maxZ) / 2 + oz]} scale={[b.maxX - b.minX, 0.02, b.maxZ - b.minZ]} renderOrder={6}>
            <boxGeometry args={[1, 1, 1]} />
            {ghostMat(0.28)}
          </mesh>
        );
      }
    }
    if (parts.length) preview = <group>{parts}</group>;
  } else if ((buildTool === 'door' || buildTool === 'window' || buildTool === 'arch' || buildTool === 'arch-wide') && cutHover) {
    const pr = primsAll[cutHover.id];
    if (pr) {
      const spec = OPENINGS[buildTool];
      const [L, H, T] = pr.scale;
      const theta = pr.rotation[1];
      const w = Math.min(spec.w, L - 0.02);
      const h = Math.min(spec.h, H - 0.02);
      const b = Math.min(spec.b, Math.max(0, H - h - 0.02));
      const dirX = Math.cos(theta), dirZ = -Math.sin(theta);
      const spring = spec.arch ? Math.max(0.2, h - w / 2) : h; // curve start height
      preview = (
        <group
          position={[pr.position[0] + dirX * cutHover.u, pr.position[1] - H / 2 + b, pr.position[2] + dirZ * cutHover.u]}
          rotation={[0, theta, 0]}
        >
          <mesh position={[0, spring / 2, 0]} scale={[w, spring, T + 0.08]} renderOrder={5}>
            <boxGeometry args={[1, 1, 1]} />
            {ghostMat(0.55)}
          </mesh>
          {spec.arch && (
            <mesh position={[0, spring, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[w, T + 0.08, 2 * (h - spring)]} renderOrder={5}>
              <cylinderGeometry args={[0.5, 0.5, 1, 32]} />
              {ghostMat(0.55)}
            </mesh>
          )}
        </group>
      );
    }
  }

  const isCutTool = buildTool === 'door' || buildTool === 'window' || buildTool === 'arch' || buildTool === 'arch-wide';
  return <>{!isCutTool && cursorMarker}{preview}</>;
};

// ---- Top-down architect camera ----
// Saves the free-look camera pose on entry and restores it on exit. The orbit
// constraints themselves are driven declaratively from NonVRControls.
const TopDownCamera: React.FC<{ orbitRef: React.RefObject<any> }> = ({ orbitRef }) => {
  const topDown = useWorldStore((s) => s.topDownView);
  const { camera } = useThree();
  const saved = useRef<{ pos: THREE.Vector3; target: THREE.Vector3 } | null>(null);

  useEffect(() => {
    const oc = orbitRef.current;
    if (!oc) return;
    if (topDown) {
      saved.current = { pos: camera.position.clone(), target: oc.target.clone() };
      const tx = oc.target.x;
      const tz = oc.target.z;
      oc.target.set(tx, 0, tz);
      // Tiny z offset keeps the up-vector well-defined when looking straight down.
      camera.position.set(tx, 40, tz + 0.001);
      oc.update();
    } else if (saved.current) {
      camera.position.copy(saved.current.pos);
      oc.target.copy(saved.current.target);
      oc.update();
      saved.current = null;
    }
  }, [topDown, camera, orbitRef]);

  return null;
};

// ---- Avatar Manager ----
const AvatarManager = React.memo<{
  avatarUrl: string | null;
  isEditingEnvironment: boolean;
  cameraMode: 'normal' | 'zoom' | 'birdseye';
  userName: string;
  spawnPosition: [number, number, number];
  onGroupRef?: (ref: THREE.Group | null) => void;
}>(({ avatarUrl, isEditingEnvironment, cameraMode, userName, spawnPosition, onGroupRef }) => {
  const isPresenting = usePresenting();
  if (!avatarUrl || avatarUrl === 'skip') return null;
  if (isPresenting) return null;
  return (
    <AvatarWithVoiceState
      key={avatarUrl}
      avatarUrl={avatarUrl}
      position={spawnPosition}
      cameraMode={isEditingEnvironment ? 'normal' : cameraMode}
      userName={userName}
      onGroupRef={onGroupRef}
      disableCameraControl={isEditingEnvironment}
    />
  );
});

// ---- Ensure opaque background during VR to prevent Quest 3 passthrough ----
const VRBackgroundEnforcer: React.FC = () => {
  const isPresenting = usePresenting();
  const { scene } = useThree();

  useEffect(() => {
    if (isPresenting && !scene.background) {
      // Quest 3 uses RGBA framebuffers for XR. If scene.background is null,
      // Three.js clears with clearColor (black) + clearAlpha. But Quest 3's
      // compositor may still show passthrough through any transparent pixels.
      // Setting an explicit scene.background ensures fully opaque rendering.
      scene.background = new THREE.Color(0x000000);
      return () => {
        scene.background = null;
      };
    }
  }, [isPresenting, scene]);

  return null;
};

// ---- Non-VR Controls (disabled during VR session) ----
const NonVRControls: React.FC<{ avatarUrl: string | null; environmentModels: Record<string, any>; orbitRef: React.RefObject<any> }> = ({ avatarUrl, environmentModels, orbitRef }) => {
  const isPresenting = usePresenting();
  const isEditing = useWorldStore((s) => s.isEditingEnvironment);
  const topDown = useWorldStore((s) => s.topDownView);
  const buildTool = useWorldStore((s) => s.buildTool);
  // Wall/room drawing owns the left button, so orbit must not steal the drag.
  const dragTool = buildTool === 'wall' || buildTool === 'room' || buildTool === 'stairs' || buildTool === 'select';
  const mouseButtons = useMemo(() => ({
    LEFT: (dragTool ? -1 : topDown ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE) as any,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN,
  }), [dragTool, topDown]);
  if (isPresenting) return null;
  // Always show OrbitControls — allows orbiting even with avatar present
  const hasAvatar = !!avatarUrl && avatarUrl !== 'skip';
  const showOrbit = true;
  return (
    <>
      {!hasAvatar && (
        <PerspectiveCamera makeDefault position={Object.keys(environmentModels).length > 0 ? [15, 10, 15] : [0, 5, 10]} fov={60} />
      )}
      {showOrbit && (
        <OrbitControls
          ref={orbitRef}
          enablePan
          enableZoom
          enableRotate={!topDown}
          minPolarAngle={0}
          maxPolarAngle={topDown ? 0.0001 : Math.PI}
          mouseButtons={mouseButtons}
        />
      )}
    </>
  );
};

// ---- Roof cutaway: top-down view looks INTO venues ----
// Venues ship their roof as a mesh named *roof* / *ceiling* (GathrXR Studio
// rooms: `solid_roof`). From above it hides every floor, so the top-down view
// hides it — you can then place on the dance floor, a mezzanine or a deck.
const RoofCutaway: React.FC = () => {
  const topDown = useWorldStore((s) => s.topDownView);
  const scene = useThree((s) => s.scene);
  useEffect(() => {
    if (!topDown) return;
    const hidden: THREE.Object3D[] = [];
    scene.traverse((o: any) => {
      if (o.isMesh && o.visible && /roof|ceiling/i.test(o.name || '') && !/^(collider|ucx|collision)[_.]/i.test(o.name || '')) { o.visible = false; hidden.push(o); }
    });
    return () => { hidden.forEach((o) => { o.visible = true; }); };
  }, [topDown, scene]);
  return null;
};

// ---- Camera Focuser: moves camera to selected object when entering edit mode ----
// Helper: get object position from store
function getObjectPosition(id: string): [number, number, number] | null {
  const s = useWorldStore.getState();
  if (id.startsWith('prim-') && s.primitives[id]) return s.primitives[id].position;
  if (id.startsWith('content-') && s.contentItems[id]) return s.contentItems[id].position || [0, 0, 0];
  if (id.startsWith('light-') && s.lights[id]) return s.lights[id].position;
  if (id.startsWith('env-') && s.environmentModels[id]) return s.environmentModels[id].transform?.position || [0, 0, 0];
  if (id.startsWith('screen-') && s.screenProps[id]) return s.screenProps[id].position;
  return null;
}

// Helper: rough bounding radius of an object so the camera can frame it
function getObjectRadius(id: string): number {
  const s = useWorldStore.getState();
  if (id.startsWith('prim-') && s.primitives[id]) {
    const sc = s.primitives[id].scale;
    return Math.max(sc[0], sc[1], sc[2]) * 0.87;
  }
  if (id.startsWith('content-') && s.contentItems[id]) {
    const sc: any = s.contentItems[id].scale;
    return Array.isArray(sc) ? Math.max(...sc) : (sc || 1);
  }
  if (id.startsWith('env-')) return 6;
  return 1;
}

/** A selection made by placing something: don't fly the camera to it. */
let skipFocusFor: string | null = null;
const CameraFocuser: React.FC<{ orbitRef: React.RefObject<any> }> = ({ orbitRef }) => {
  const { camera } = useThree();
  const selectedId = useWorldStore((s) => s.selectedObjectId);
  const isEditing = useWorldStore((s) => s.isEditingEnvironment);
  const topDown = useWorldStore((s) => s.topDownView);
  const goal = useRef<{ target: THREE.Vector3; camPos: THREE.Vector3 } | null>(null);

  useEffect(() => {
    if (!isEditing || !selectedId || topDown || selectedId === skipFocusFor) {
      if (selectedId === skipFocusFor) skipFocusFor = null;
      goal.current = null;
      return;
    }
    const oc = orbitRef.current;
    const pos = getObjectPosition(selectedId);
    if (!pos || !oc) return;

    const targetPos = new THREE.Vector3(pos[0], pos[1], pos[2]);

    // Frame the object: close enough to edit comfortably, scaled to its size.
    // Never zoom OUT — if the user is already closer, just re-center on it.
    const radius = getObjectRadius(selectedId);
    const fitDistance = Math.min(14, Math.max(2.5, radius * 3));
    const currentDistance = camera.position.distanceTo(oc.target);
    const distance = Math.min(currentDistance, fitDistance);

    const direction = camera.position.clone().sub(oc.target).normalize();
    if (direction.lengthSq() < 1e-6) direction.set(0, 0.5, 1).normalize();

    goal.current = {
      target: targetPos,
      camPos: targetPos.clone().add(direction.multiplyScalar(distance)),
    };

    // User grabbing the orbit cancels the fly-in
    const cancel = () => { goal.current = null; };
    oc.addEventListener('start', cancel);
    return () => oc.removeEventListener('start', cancel);
  }, [isEditing, selectedId, topDown, camera, orbitRef]);

  useFrame((_, delta) => {
    const g = goal.current;
    const oc = orbitRef.current;
    if (!g || !oc) return;
    const k = 1 - Math.exp(-8 * delta);
    camera.position.lerp(g.camPos, k);
    oc.target.lerp(g.target, k);
    oc.update();
    if (camera.position.distanceTo(g.camPos) < 0.02 && oc.target.distanceTo(g.target) < 0.02) {
      goal.current = null;
    }
  });

  return null;
};

// Create XR store - offer immersive-vr so Quest browser shows VR button in address bar
const xrStore = createXRStore({
  offerSession: 'immersive-vr',
  enterGrantedSession: ['immersive-vr'],
  // No controller grab pointer: its sphere test uses each mesh's bounding BOX,
  // so standing inside a big merged environment mesh reads as a 0 m "grab hit",
  // the combined pointer prefers it, and the laser ray is switched off. The grip
  // (squeeze) is owned by VRGrab, which reads the gamepad and does its own tests.
  controller: { grabPointer: false },
  customSessionInit: {
    requiredFeatures: ['local-floor'],
    optionalFeatures: ['hand-tracking'],
  },
});

// ---- Marble world wrapper with TransformControls ----
const MarbleWorldWithControls: React.FC<{
  mw: { id: string; name: string; splatUrl: string; radUrl?: string | null; colliderUrl: string | null; position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number]; groundPlaneOffset: number | null; showCollider: boolean };
  isSelected: boolean;
  transformMode: 'translate' | 'rotate' | 'scale';
  orbitRef: React.RefObject<any>;
  onSelect: () => void;
  onTransformChange: (updates: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: [number, number, number] }) => void;
}> = ({ mw, isSelected, transformMode, orbitRef, onSelect, onTransformChange }) => {
  const groupRef = React.useRef<THREE.Group>(null!);
  const snapSettings = useWorldStore((s) => s.snapSettings);
  const isPresenting = usePresenting();

  // Mark marble world for distance culling
  React.useEffect(() => {
    if (groupRef.current) {
      groupRef.current.userData.isMarbleWorld = true;
    }
  }, []);

  const inner = (
    <group
      ref={groupRef}
      position={mw.position}
      rotation={mw.rotation}
      scale={mw.scale}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <MarbleWorldRenderer
        splatUrl={mw.splatUrl}
        radUrl={mw.radUrl}
        colliderUrl={mw.colliderUrl}
        groundPlaneOffset={mw.groundPlaneOffset}
        showCollider={mw.showCollider}
      />
      {/* Always-present clickable bounding box — transparent when not selected */}
      <mesh>
        <boxGeometry args={[5, 5, 5]} />
        <meshBasicMaterial color="#00c2b3" transparent opacity={isSelected ? 0.05 : 0} wireframe={isSelected} />
      </mesh>
    </group>
  );

  // TransformControls renders as a SIBLING targeting groupRef — wrapping
  // `inner` as a child would remount the whole subtree on select/deselect,
  // which reloads (re-streams) the splat from scratch every click.
  return (
    <>
      {inner}
      {isSelected && !mw.isLocked && !isPresenting && (
        <TransformControls
          object={groupRef}
          mode={transformMode}
          translationSnap={snapSettings.grid ? snapSettings.gridSize : null}
          rotationSnap={snapSettings.rotation ? (snapSettings.rotationAngle * Math.PI / 180) : null}
          onMouseDown={() => { (window as any).__transformDragging = true; if (orbitRef?.current) orbitRef.current.enabled = false; }}
          onMouseUp={() => {
            setTimeout(() => { (window as any).__transformDragging = false; }, 50);
            if (orbitRef?.current) orbitRef.current.enabled = true;
            if (groupRef.current) {
              const p = groupRef.current.position;
              const r = groupRef.current.rotation;
              const sc = groupRef.current.scale;
              onTransformChange({
                position: [p.x, p.y, p.z],
                rotation: [r.x, r.y, r.z],
                scale: [sc.x, sc.y, sc.z],
              });
            }
          }}
        />
      )}
    </>
  );
};

// ---- WorldBuilder ----
const WorldBuilder: React.FC = () => {
  const { worldId } = useParams();
  const [searchParams] = useSearchParams();
  const worldName = searchParams.get('name') || 'Untitled World';
  const isTemp = searchParams.get('temp') === 'true';
  const navigate = useNavigate();

  // Ref for local avatar group (used by MultiplayerSync to read position)
  const localAvatarRef = useRef<THREE.Group | null>(null);

  // Early store selectors needed before the main destructure
  const isOwner = useWorldStore((s) => s.isOwner);

  // Panel opacity (solid vs glass)
  const { solid: panelsSolid, toggle: togglePanelOpacity } = usePanelOpacity();

  // Track VR presentation state outside Canvas (xrStore is a zustand store)
  const [isInVR, setIsInVR] = useState(false);
  useEffect(() => {
    const unsub = xrStore.subscribe((state: any) => {
      setIsInVR(!!state.session);
    });
    return unsub;
  }, []);

  // Menu dropdown state
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  // Arcade games for the Games menu (the registry loads the first time it opens).
  const arcadeMenu = useArcadeGames(openMenu === 'games');
  const [showWorldSettings, setShowWorldSettings] = useState(false);
  const [maxPlayersInput, setMaxPlayersInput] = useState(20);
  const [shardInfo, setShardInfo] = useState<{ shards: any[]; totalPlayers: number; hostUserId?: string | null } | null>(null);
  const [connectedPlayers, setConnectedPlayers] = useState<{ socketId: string; userId: string; userName: string; role: string }[]>([]);
  // Material slots reported by loaded GLBs, keyed by content id. Runtime only —
  // it's derived from the model, so there's nothing to persist.
  const [modelSlots, setModelSlots] = useState<Record<string, { name: string; color: string }[]>>({});
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate' | 'scale'>('translate');
  const activeTool = useWorldStore((s) => s.activeTool);

  // Sync activeTool from store → local transformMode
  useEffect(() => {
    const toolMap: Record<string, 'translate' | 'rotate' | 'scale'> = {
      move: 'translate', rotate: 'rotate', scale: 'scale',
    };
    if (toolMap[activeTool]) setTransformMode(toolMap[activeTool]);
  }, [activeTool]);

  const orbitControlsRef = useRef<any>(null);

  // ---- Build Mode v2 state (see BuildToolsLayer / TopDownCamera above) ----
  const buildTool = useWorldStore((s) => s.buildTool);
  const stairSweep = useWorldStore((s) => s.stairSweep);
  const topDownView = useWorldStore((s) => s.topDownView);
  const snapSettings = useWorldStore((s) => s.snapSettings);

  // Arm the ghost-placement tool with a primitive shape or a content model.
  const startPlacement = useCallback((spec: PlacementSpec) => {
    const s = useWorldStore.getState();
    s.setPlacementSpec(spec);
    s.setPlacementRotY(0);
    s.setBuildTool('place');
  }, []);

  // Global flag so the avatar's click-to-move / right-click-teleport stays out
  // of the way while a build tool is armed (same idea as __transformDragging).
  // The avatar's click-to-move skips placed objects while in the editor.
  useEffect(() => {
    (window as any).__gxEditor = true;
    return () => { (window as any).__gxEditor = false; };
  }, []);

  useEffect(() => {
    (window as any).__buildToolActive = buildTool !== 'none';
    return () => { (window as any).__buildToolActive = false; };
  }, [buildTool]);

  // Paint mode gets a crosshair too (the ground-drawing tools set their own
  // cursor from inside BuildToolsLayer, which owns their pointer listeners).
  useEffect(() => {
    if (buildTool !== 'paint') return;
    const el = document.querySelector('.world-canvas canvas') as HTMLCanvasElement | null;
    if (!el) return;
    const prev = el.style.cursor;
    el.style.cursor = 'crosshair';
    return () => { el.style.cursor = prev; };
  }, [buildTool]);

  // Duplicate the selected primitive / content item, offset one snap step on X.
  // Mirrors the existing ObjectControls onDuplicate handlers.
  const duplicateSelected = useCallback(() => {
    const s = useWorldStore.getState();
    if (!s.canBuild) return;
    const step = s.snapSettings.grid ? s.snapSettings.gridSize : 1.5;

    // Marquee multi-selection: duplicate EVERYTHING selected as one block,
    // offset beside the original. Groups inside the selection map to fresh
    // groupIds so the clones stay internally grouped but independent. The
    // clones become the new selection, so repeated Cmd+D chain-stamps.
    if (s.selectedObjectIds.length >= 2) {
      const ids = s.selectedObjectIds;
      let minX = Infinity, maxX = -Infinity;
      for (const oid of ids) {
        const o = s.primitives[oid] || s.contentItems[oid];
        if (!o) continue;
        minX = Math.min(minX, o.position[0]);
        maxX = Math.max(maxX, o.position[0]);
      }
      const offset = (isFinite(maxX - minX) ? maxX - minX : 0) + step;
      const groupMap = new Map<string, string>();
      const freshGroup = (old?: string) => {
        if (!old) return undefined;
        let g = groupMap.get(old);
        if (!g) { g = `group-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 5)}`; groupMap.set(old, g); }
        return g;
      };
      const newIds: string[] = [];
      for (const oid of ids) {
        const p = s.primitives[oid];
        if (p) {
          const newId = `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
          const dup = { ...p, groupId: freshGroup(p.groupId), position: [p.position[0] + offset, p.position[1], p.position[2]] as [number, number, number] };
          s.addPrimitive(newId, dup);
          if (worldId) _broadcastAdd(worldId, 'primitive', newId, dup);
          newIds.push(newId);
          continue;
        }
        const c = s.contentItems[oid];
        if (c) {
          const newId = `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
          const dup = { ...c, groupId: freshGroup(c.groupId), position: [c.position[0] + offset, c.position[1], c.position[2]] as [number, number, number] };
          s.addContentItem(newId, dup);
          if (worldId) _broadcastAdd(worldId, 'content', newId, dup);
          newIds.push(newId);
        }
      }
      if (newIds.length) s.setMultiSelect(newIds);
      return;
    }

    const id = s.selectedObjectId;
    if (!id) return;
    const src = s.primitives[id];
    // Group-aware: duplicating any member of a group (a wall of a room, one
    // model of a furniture set) clones the WHOLE group — shapes and content
    // models — under a fresh groupId, offset to land beside the original.
    const gid = src?.groupId || s.contentItems[id]?.groupId;
    if (gid) {
      const members = s.getGroupMembers(gid);
      let minX = Infinity, maxX = -Infinity;
      for (const mid of members) {
        const mp = s.primitives[mid] || s.contentItems[mid];
        if (!mp) continue;
        minX = Math.min(minX, mp.position[0]);
        maxX = Math.max(maxX, mp.position[0]);
      }
      const offset = (isFinite(maxX - minX) ? maxX - minX : 0) + step;
      const newGroupId = `group-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 5)}`;
      let selectId: string | null = null;
      for (const mid of members) {
        const mp = s.primitives[mid];
        if (mp) {
          const newId = `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
          const dup = { ...mp, groupId: newGroupId, position: [mp.position[0] + offset, mp.position[1], mp.position[2]] as [number, number, number] };
          s.addPrimitive(newId, dup);
          if (worldId) _broadcastAdd(worldId, 'primitive', newId, dup);
          if (mid === id) selectId = newId;
          continue;
        }
        const mc = s.contentItems[mid];
        if (mc) {
          const newId = `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
          const dup = { ...mc, groupId: newGroupId, position: [mc.position[0] + offset, mc.position[1], mc.position[2]] as [number, number, number] };
          s.addContentItem(newId, dup);
          if (worldId) _broadcastAdd(worldId, 'content', newId, dup);
          if (mid === id) selectId = newId;
        }
      }
      if (selectId) s.setSelectedObjectId(selectId);
      return;
    }
    if (src) {
      const newId = `prim-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      const dupItem = { ...src, position: [src.position[0] + step, src.position[1], src.position[2]] as [number, number, number], name: `${src.name} Copy` };
      s.addPrimitive(newId, dupItem);
      if (worldId) _broadcastAdd(worldId, 'primitive', newId, dupItem);
      s.setSelectedObjectId(newId);
      return;
    }
    const csrc = s.contentItems[id];
    if (csrc) {
      const newId = `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
      const dupItem = { ...csrc, position: [csrc.position[0] + step, csrc.position[1], csrc.position[2]] as [number, number, number] };
      s.addContentItem(newId, dupItem);
      if (worldId) _broadcastAdd(worldId, 'content', newId, dupItem);
      s.setSelectedObjectId(newId);
    }
  }, [worldId]);
  // Keep the mount-once keyboard handler pointed at the current closure.
  const duplicateSelectedRef = useRef(duplicateSelected);
  duplicateSelectedRef.current = duplicateSelected;

  // Marble AI worlds
  const [showMarblePanel, setShowMarblePanel] = useState(false);
  // Marble is "coming soon" (config/marble.ts): the Add menu entry shows this notice instead.
  const [marbleSoonAt, setMarbleSoonAt] = useState(0);
  useEffect(() => {
    if (!marbleSoonAt) return;
    const t = setTimeout(() => setMarbleSoonAt(0), 5200);
    return () => clearTimeout(t);
  }, [marbleSoonAt]);
  const [showObjectGen, setShowObjectGen] = useState(false);
  const [showNpcCreator, setShowNpcCreator] = useState(false);
  const [showWallDesigner, setShowWallDesigner] = useState(false);
  const [showMusicPanel, setShowMusicPanel] = useState(false);
  // A click on a locked object opens Scene Objects so it can be unlocked there.
  const lockFlash = useWorldStore((s) => s.lockFlash);
  useEffect(() => { if (lockFlash) useWorldStore.getState().setPanel('showObjectsList', true); }, [lockFlash]);
  const [showObjectLibrary, setShowObjectLibrary] = useState(false);
  const [pendingPanel, setPendingPanel] = useState<Pick<ScreenPropItem, 'type' | 'browserUrl' | 'videoUrl'> | null>(null);
  const beginPanelPlacement = (panel: Pick<ScreenPropItem, 'type' | 'browserUrl' | 'videoUrl'>) => {
    const s = useWorldStore.getState();
    if (!s.canBuild) return;
    s.setBuildTool('none');
    s.setSelectedObjectId(null);
    s.setPanel('showScreenShare', false);
    s.setPanel('showScreenPropPicker', false);
    s.setPanel('showBrowserPropPicker', false);
    setPendingPanel(panel);
  };
  const [showCopilot, setShowCopilot] = useState(false);
  // Studio is admin-only; only admins see its link in the Add menu.
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  useEffect(() => { supabase.rpc('is_app_admin').then(({ data }) => setIsAppAdmin(!!data), () => {}); }, []);
  const [marbleWorlds, setMarbleWorlds] = useState<Array<{
    id: string;
    name: string;
    splatUrl: string;
    radUrl?: string | null;
    colliderUrl: string | null;
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
    groundPlaneOffset: number | null;
    showCollider: boolean;
    isLocked: boolean;
  }>>([]);

  // Keyboard shortcuts for transform mode + undo/redo
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;

      // ---- Build Mode v2 shortcuts (take priority while a tool is armed) ----
      const bs = useWorldStore.getState();
      if (e.key === 'Escape' && bs.buildTool !== 'none') {
        e.preventDefault();
        if (bs.buildTool === 'select') bs.clearMultiSelect();
        bs.setBuildTool('none');
        return;
      }
      // Foliage brush shortcuts. Adjusting the brush without leaving the canvas
      // is the difference between painting and form-filling.
      if (bs.buildTool === 'foliage') {
        if (e.key === '[' || e.key === ']') {
          e.preventDefault();
          const step = e.key === ']' ? 0.5 : -0.5;
          bs.setFoliageBrush({ radius: Math.max(0.5, Math.min(15, bs.foliageBrush.radius + step)) });
          return;
        }
        if (e.key === 't' || e.key === 'T') {
          e.preventDefault();
          bs.setFoliageBrush({ trees: !bs.foliageBrush.trees, erase: false });
          return;
        }
        if (e.key === 'e' || e.key === 'E') {
          e.preventDefault();
          bs.setFoliageBrush({ erase: !bs.foliageBrush.erase });
          return;
        }
        if ((e.key === 'z' || e.key === 'Z') && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          bs.undoFoliageStroke();
          return;
        }
      }

      // G while marquee-selecting groups the selection.
      if ((e.key === 'g' || e.key === 'G') && bs.buildTool === 'select' && bs.selectedObjectIds.length >= 2 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        bs.groupSelected();
        return;
      }
      // R while the stairs tool is armed cycles the curve sweep.
      if ((e.key === 'r' || e.key === 'R') && bs.buildTool === 'stairs' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const order = [0, 90, 180, 360];
        bs.setStairSweep(order[(order.indexOf(bs.stairSweep) + 1) % order.length]);
        return;
      }
      if ((e.key === 'r' || e.key === 'R') && bs.buildTool === 'place' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        bs.setPlacementRotY(bs.placementRotY + Math.PI / 2);
        return;
      }
      if ((e.key === 't' || e.key === 'T') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        bs.setTopDownView(!bs.topDownView);
        return;
      }
      // Duplicate selected: Cmd/Ctrl+D (offset one snap step on X)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        duplicateSelectedRef.current();
        return;
      }

      // Undo: Ctrl+Z / Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoWorld(worldId);
        return;
      }
      // Redo: Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y
      if ((e.ctrlKey || e.metaKey) && (e.key === 'Z' || e.key === 'y')) {
        e.preventDefault();
        redoWorld(worldId);
        return;
      }
      if (e.key === 'q' || e.key === 'Q') setTransformMode('translate');
      else if (e.key === 'e' || e.key === 'E') setTransformMode('rotate');
      else if (e.key === 'r' || e.key === 'R') setTransformMode('scale');
      else if (e.key === 'Escape') {
        const s = useWorldStore.getState();
        if (s.selectedObjectId?.startsWith('prim-')) {
          s.setSelectedObjectId(null);
          s.setIsEditingEnvironment(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Listen for VR transform mode changes (from VRBuildMenu buttons)
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent).detail;
      if (mode === 'translate' || mode === 'rotate' || mode === 'scale') {
        setTransformMode(mode);
      }
    };
    window.addEventListener('vr-transform-mode', handler);
    return () => window.removeEventListener('vr-transform-mode', handler);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handleClick = () => setOpenMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [openMenu]);

  // Fetch connected players from Supabase Presence when settings panel is open
  useEffect(() => {
    if (!showWorldSettings || !worldId) return;
    const refreshPlayers = () => {
      try {
        const channel = getChannel(worldId);
        const state = channel.presenceState();
        const players: any[] = [];
        for (const [key, presences] of Object.entries(state)) {
          const p = (presences as any[])[0];
          if (p) {
            // Preserve manually-set roles (builder/host) from previous state
            const existing = connectedPlayers.find(cp => cp.userId === p.userId);
            const role = existing?.role || (p.userId && isOwner ? 'owner' : 'viewer');
            players.push({ socketId: key, userId: p.userId, userName: p.userName, role });
          }
        }
        setConnectedPlayers(players);
      } catch (e) { /* channel not ready */ }
    };
    refreshPlayers();
    const interval = setInterval(refreshPlayers, 3000);
    return () => clearInterval(interval);
  }, [showWorldSettings, worldId, isOwner]);

  const updateMaxPlayers = useCallback(async () => {
    if (!worldId) return;
    // Max players is informational — stored in world settings via Supabase
    try {
      await supabase.from('worlds').update({ settings: { max_players: maxPlayersInput } }).eq('id', worldId);
    } catch (e) { console.error('Failed to update max players:', e); }
  }, [worldId, maxPlayersInput]);

  const designateAsHost = useCallback(async () => {
    const currentUser = useWorldStore.getState().user;
    if (!worldId || !currentUser) return;
    // With Supabase, host/owner is determined by world ownership in DB
    // Just set the local role
    useWorldStore.getState().setRole('owner');
  }, [worldId]);

  // Pull everything from store
  const store = useWorldStore();
  const {
    showChat, showScreenShare, showEnvironmentControls, showObjectsList,
    showControlsPanel, showEnvironmentPicker, showSkyboxPicker, showContentPicker,
    showAvatarSelector, showGroundControls, showContentBrowser, showSkyboxManager,
    showScreenPropPicker, showBrowserPropPicker, showAuthPrompt, showTimeWarning,
    showVoiceChatPrompt, showShareModal, showAtmosphereControls,
    avatarUrl, userName, cameraMode,
    environmentModels, skyboxes, currentSkyboxId, mobileSkyboxUrl, contentItems, screenProps, primitives, lights,
    groundVisible, groundSettings,
    selectedObjectId, selectedObjectIds, isEditingEnvironment,
    user, isPublic, accessCode, canBuild, role,
    isSaving, lastSaved, actualWorldId, saveNotification,
    shortenedURL, isLoadingShortURL, activeScreenShare,
    multiplayerInfo,
  } = store;

  // Portal tool hint: placing an entrance, or the exit of an existing portal.
  const placingShape = useWorldStore((s) => (s.buildTool === 'place' ? s.placementSpec?.shape : undefined));
  const placingPortalExit = useWorldStore((s) => !!s.placementSpec?.portalExitFor);

  // ---- Multiplayer object sync helpers (extracted to worldBroadcast service) ----
  const broadcastObjectAdd = useCallback((objectType: string, id: string, item: any) => {
    if (worldId) _broadcastAdd(worldId, objectType, id, item);
  }, [worldId]);
  const broadcastObjectDelete = useCallback((objectType: string, id: string) => {
    if (worldId) _broadcastDelete(worldId, objectType, id);
  }, [worldId]);
  const broadcastObjectUpdate = useCallback((objectType: string, id: string, updates: any) => {
    if (worldId) _broadcastUpdate(worldId, objectType, id, updates);
  }, [worldId]);

  // Automatic sync for environments/skyboxes/screen props and shared world
  // settings — diff-based store subscription, so every panel's mutations
  // broadcast without per-call-site wiring. Starts after load completes so
  // bulk-sets (loadWorldAssets, resetWorldData) never flood the channel.
  const worldLoadingNow = store.worldLoading;
  useEffect(() => {
    if (!worldId || worldLoadingNow) return;
    return initWorldSettingsSync(worldId);
  }, [worldId, worldLoadingNow]);

  // Shared room screens (cloud browser / synced video), same as the play route.
  useEffect(() => {
    if (!worldId) return;
    return initRoomScreens(worldId);
  }, [worldId]);

  // ---- Add Primitive ----
  // ---- Capture canvas thumbnail and upload to S3 ----
  const captureAndUploadThumbnail = async (worldId: string): Promise<string | null> => {
    try {
      const canvas = document.querySelector('.world-canvas canvas') as HTMLCanvasElement;
      if (!canvas) return null;

      // Create a smaller thumbnail (400px wide)
      const thumbCanvas = document.createElement('canvas');
      const aspect = canvas.height / canvas.width;
      thumbCanvas.width = 400;
      thumbCanvas.height = Math.round(400 * aspect);
      const ctx = thumbCanvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);

      const blob = await new Promise<Blob | null>((resolve) =>
        thumbCanvas.toBlob(resolve, 'image/jpeg', 0.8)
      );
      if (!blob) return null;

      const key = `thumbnails/${worldId}.jpg`;
      return (await putObject(key, blob, 'image/jpeg', { cacheControl: 'max-age=300' })) + `?t=${Date.now()}`;
    } catch (err) {
      console.warn('[Thumbnail] Capture failed:', err);
      return null;
    }
  };

  // ---- Capture the current view as this world's card thumbnail (on demand) ----
  // Every save used to re-shoot the thumbnail from wherever the camera happened
  // to be, so a deliberately framed card was silently replaced by the next save.
  // Once the user picks a thumbnail it is pinned: saves leave it alone until
  // they capture again.
  const thumbRef = useRef<{ pinned: boolean; url: string | null }>({ pinned: false, url: null });
  const [capturingThumb, setCapturingThumb] = useState(false);
  const captureThumbnailNow = async () => {
    if (!actualWorldId) {
      store.setSaveNotification({ show: true, type: 'error', message: 'Save the world once first, then capture a thumbnail.' });
      setTimeout(() => store.setSaveNotification({ show: false, type: 'success', message: '' }), 3000);
      return;
    }
    setCapturingThumb(true);
    const url = await captureAndUploadThumbnail(actualWorldId);
    if (url) {
      const { error } = await supabase.from('worlds').update({ thumbnail_url: url }).eq('id', actualWorldId);
      if (!error) {
        thumbRef.current = { pinned: true, url };
        await pinWorldThumbnail(actualWorldId);
      }
      store.setSaveNotification(error
        ? { show: true, type: 'error', message: 'Couldn\'t save the thumbnail. Try again.' }
        : { show: true, type: 'success', message: 'Thumbnail set — saving won\'t change it.' });
    } else {
      store.setSaveNotification({ show: true, type: 'error', message: 'Capture failed — try again.' });
    }
    setCapturingThumb(false);
    setTimeout(() => store.setSaveNotification({ show: false, type: 'success', message: '' }), 3500);
  };

  // ---- Save World ----
  const saveWorld = async () => {
    if (!user || isTemp) {
      store.setPanel('showAuthPrompt', true);
      return;
    }
    store.setIsSaving(true);
    try {
      // Every asset's metadata carries its store id so rows keep a stable
      // identity across saves — required for per-object write-through and
      // consistent object keys between clients.
      const assets: any[] = [];
      Object.entries(environmentModels).forEach(([id, model]) => {
        assets.push({
          type: 'environment', url: model.url,
          metadata: { id, position: model.transform.position, scale: model.transform.scale, rotation: model.transform.rotation, visible: model.visible, isLocked: model.transform.isLocked }
        });
      });
      Object.entries(skyboxes).forEach(([id, skybox]) => {
        assets.push({
          type: 'skybox', url: skybox.url,
          metadata: { id, filename: skybox.filename, uploadedAt: skybox.uploadedAt, thumbnail: skybox.thumbnail, isCurrent: id === currentSkyboxId }
        });
      });
      Object.entries(contentItems).forEach(([id, item]) => {
        assets.push({
          type: 'content', url: item.url,
          metadata: { id, position: item.position, scale: item.scale, rotation: item.rotation, visible: item.visible, isLocked: item.isLocked || false, filename: item.filename, thumbnailUrl: item.thumbnailUrl, uploadedAt: item.uploadedAt, contentType: item.type, collidable: item.collidable || false, seat: item.seat || false, environmentMesh: item.environmentMesh || false, mobileUrl: item.mobileUrl, materialColors: item.materialColors, materialTextures: item.materialTextures, groupId: item.groupId }
        });
      });
      Object.entries(primitives).forEach(([id, prim]) => {
        assets.push({
          type: 'primitive', url: '',
          metadata: { id, shape: prim.shape, name: prim.name, position: prim.position, rotation: prim.rotation, scale: prim.scale, color: prim.color, texturePreset: prim.texturePreset, textureUrl: prim.textureUrl, pbrMaps: prim.pbrMaps, textureScale: prim.textureScale, textureScaleY: prim.textureScaleY, visible: prim.visible, isLocked: prim.isLocked, collidable: prim.collidable, colliderOnly: prim.colliderOnly, opacity: prim.opacity, metalness: prim.metalness, roughness: prim.roughness, emissive: prim.emissive ?? 0, seat: prim.seat ?? false, ...(prim.seatFit ? { seatFit: prim.seatFit } : {}), portalLink: prim.portalLink, portalLabel: prim.portalLabel, portalWorldId: prim.portalWorldId, portalWorldName: prim.portalWorldName, game: prim.game, gameStyle: prim.gameStyle, ...(prim.arcadeGame ? { arcadeGame: prim.arcadeGame } : {}), ...(prim.board ? { board: prim.board } : {}), ...(prim.speaker ? { speaker: prim.speaker } : {}), ...(prim.worldGame ? { worldGame: prim.worldGame } : {}), npc: prim.npc, groupId: prim.groupId, text: prim.text, fontSize: prim.fontSize, ...(prim.castShadow === false ? { castShadow: false } : {}) }
        });
      });
      Object.entries(lights).forEach(([id, light]) => {
        assets.push({
          type: 'light', url: '',
          metadata: { id, lightType: light.type, name: light.name, position: light.position, rotation: light.rotation, color: light.color, intensity: light.intensity, distance: light.distance, angle: light.angle, penumbra: light.penumbra, castShadow: light.castShadow, visible: light.visible, isLocked: light.isLocked, width: light.width, height: light.height }
        });
      });
      Object.entries(screenProps).forEach(([id, prop]) => {
        // Save the display surface; live capture tracks stay session-only.
        assets.push({
          type: 'screenprop', url: prop.browserUrl || prop.videoUrl || '',
          metadata: { id, screenType: prop.type, position: prop.position, scale: prop.scale, rotation: prop.rotation, visible: prop.visible, isLocked: prop.isLocked ?? false, browserUrl: prop.browserUrl, videoUrl: prop.videoUrl, filename: prop.filename, uploadedAt: prop.uploadedAt, ...(prop.roomScreen ? { roomScreen: true, width: prop.width, height: prop.height, name: prop.name } : {}) }
        });
      });
      // Every stroke in a single row. Saving the generated plants instead would
      // be thousands of rows for one painted hillside.
      const strokes = useWorldStore.getState().foliageStrokes;
      if (strokes.length) {
        assets.push({
          type: 'foliage', url: '',
          metadata: { id: 'foliage-strokes', strokes: strokes.map(packStroke) },
        });
      }
      marbleWorlds.forEach((mw) => {
        assets.push({
          type: 'marble_world', url: mw.splatUrl,
          metadata: { id: mw.id, name: mw.name, radUrl: mw.radUrl || null, colliderUrl: mw.colliderUrl, position: mw.position, rotation: mw.rotation, scale: mw.scale, groundPlaneOffset: mw.groundPlaneOffset }
        });
      });

      // Keep a pinned thumbnail; only auto-capture when the user hasn't chosen one.
      const thumbWorldId = actualWorldId || crypto.randomUUID();
      const pinnedThumb = thumbRef.current.pinned && thumbRef.current.url;
      const thumbnailUrl = pinnedThumb
        ? thumbRef.current.url
        : await captureAndUploadThumbnail(thumbWorldId);
      if (!pinnedThumb && thumbnailUrl) thumbRef.current.url = thumbnailUrl;
      console.log('[SaveWorld] Thumbnail:', pinnedThumb ? 'pinned' : (thumbnailUrl ? 'captured' : 'skipped'));

      console.log('[SaveWorld] Saving:', { worldId: actualWorldId, assetCount: assets.length, assetTypes: assets.map(a => a.type) });
      const { data, error } = await supabase.rpc('save_world', {
        p_world_id: actualWorldId,
        p_world_name: worldName,
        p_owner_id: user.id,
        p_settings: {
          max_users: 20, is_public: isPublic,
          // Keep a starter experience an experience across edits.
          ...(templateMetaRef.current.is_template ? { is_template: true } : {}),
          ...(templateMetaRef.current.experience_type ? { experience_type: templateMetaRef.current.experience_type } : {}),
          // Headset/phone rendition (utils/qualityTier): not edited here, carried through saves.
          // Keyed by original asset URL, so an asset swapped in the editor just loads its original on lite.
          ...(templateMetaRef.current.lite ? { lite: templateMetaRef.current.lite } : {}),
          access_code: !isPublic ? (accessCode || store.generateAccessCode()) : null,
          thumbnail_url: thumbnailUrl,
          thumbnail_pinned: thumbRef.current.pinned,
          ground: {
            size: groundSettings.size,
            color: groundSettings.color,
            texturePreset: groundSettings.texturePreset,
            textureUrl: groundSettings.textureUrl || null,
            pbrMaps: groundSettings.pbrMaps || null,
            textureScale: groundSettings.textureScale,
            opacity: groundSettings.opacity,
            showGrid: groundSettings.showGrid,
            visible: groundVisible,
          },
          atmosphere: store.atmosphere,
          spawnPosition: store.spawnPosition,
          mobileSkyboxUrl: store.mobileSkyboxUrl || null,
          ...(store.worldBounds ? { bounds: store.worldBounds } : {}),
          ...(store.worldMusic ? { music: store.worldMusic } : {}),
        },
        p_assets: assets
      });
      if (error) {
        console.error('[SaveWorld] RPC error:', error.code, error.message, error.details, error.hint);
        throw error;
      }

      const worldUuid = data;
      if (!actualWorldId && worldUuid) store.setActualWorldId(worldUuid);
      store.setLastSaved(new Date());
      // Save stamps ids on every row, so write-through is safe from here on
      store.setPersistenceReady(true);
      store.setSaveNotification({ show: true, type: 'success', message: 'World saved successfully!' });
      setTimeout(() => store.setSaveNotification({ show: false, type: 'success', message: '' }), 3000);
      if (!actualWorldId && worldUuid) {
        window.history.replaceState({}, '', `/world/${worldUuid}?name=${encodeURIComponent(worldName)}`);
      }
    } catch (error: any) {
      let errorMessage = 'Failed to save world';
      if (error.code === '42501') errorMessage = 'Permission denied. Please make sure you are logged in.';
      else if (error.code === '23505') errorMessage = 'A world with this ID already exists.';
      else if (error.message) errorMessage = `Failed to save: ${error.message}`;
      store.setSaveNotification({ show: true, type: 'error', message: errorMessage });
      setTimeout(() => store.setSaveNotification({ show: false, type: 'error', message: '' }), 5000);
    } finally {
      store.setIsSaving(false);
    }
  };

  // ---- Desktop bridge (GathrXR Studio) ----
  // Expose the builder's save/capture handlers so the Electron shell's native
  // menu (⌘S / ⇧⌘T) can invoke them. No-op in a normal browser: the globals
  // are only ever read by src/desktopBridge.ts, which itself is gated behind
  // `window.gathrxrDesktop`. Cleaned up on unmount so stale handlers don't linger.
  useEffect(() => {
    (window as any).__gathrxrSaveWorld = saveWorld;
    (window as any).__gathrxrCaptureThumbnail = captureThumbnailNow;
    return () => {
      delete (window as any).__gathrxrSaveWorld;
      delete (window as any).__gathrxrCaptureThumbnail;
    };
  });

  // ---- Load World ----
  // Preserve template flags (is_template / experience_type) across an edit —
  // the builder rebuilds settings on save and would otherwise strip them,
  // dropping a starter experience out of the gallery.
  const templateMetaRef = useRef<{ is_template?: boolean; experience_type?: string; lite?: unknown }>({});

  const loadWorld = async (worldUuid: string) => {
    try {
      store.setWorldLoading(true);
      store.setLoadingMessage('Loading world data');

      // Clear previous world data before loading new world
      store.resetWorldData();

      const { data: world, error: worldError } = await supabase
        .from('worlds').select('*').eq('id', worldUuid).single();
      if (worldError) {
        // A private world this user isn't a member of (e.g. an old /world/ share
        // link): the viewer asks for the access code, so send them there.
        if (worldError.code === 'PGRST116' && (await getWorldAccessState(worldUuid)) === 'private') {
          store.setWorldLoading(false);
          navigate(`/vr/${worldUuid}${window.location.search}`, { replace: true });
          return;
        }
        throw worldError;
      }

      if (world) {
        templateMetaRef.current = {
          is_template: world.settings?.is_template,
          experience_type: world.settings?.experience_type,
          lite: world.settings?.lite,
        };
        store.setIsPublic(world.settings?.is_public ?? true);
        store.setAccessCode(world.settings?.access_code || null);
        const currentUser = (await supabase.auth.getUser()).data.user;
        const ownerMatch = currentUser?.id === world.owner_id;
        store.setIsOwner(ownerMatch);

        // Set role from DB: owner > invited builder > viewer.
        // Guests must never keep the store's default build permissions.
        if (ownerMatch) {
          store.setRole('owner');
        } else {
          store.setRole('viewer');
          if (currentUser) {
            const { data: access } = await supabase
              .from('world_access')
              .select('role')
              .eq('world_id', worldUuid)
              .eq('user_id', currentUser.id)
              .maybeSingle();
            if (access?.role === 'owner' || access?.role === 'invited') {
              store.setRole('builder');
            }
          }
        }

        // Block non-owners from locked worlds (prevents direct URL bypass)
        if (world.settings?.is_locked && !ownerMatch) {
          store.setWorldLoading(false);
          alert('This world is currently locked by the owner.');
          navigate(currentUser ? '/dashboard' : '/');
          return;
        }

        thumbRef.current = {
          pinned: !!world.settings?.thumbnail_pinned,
          url: world.thumbnail_url ?? world.settings?.thumbnail_url ?? null,
        };

        // Restore ground settings
        const g = world.settings?.ground;
        if (g) {
          store.setGroundSettings({
            size: g.size ?? 100,
            color: g.color ?? '#1a1a1a',
            texturePreset: g.texturePreset ?? null,
            textureUrl: g.textureUrl ?? null,
            pbrMaps: g.pbrMaps ?? null,
            textureScale: g.textureScale ?? 10,
            opacity: g.opacity ?? 1,
            showGrid: g.showGrid ?? true,
            isLocked: false,
          });
          store.setGroundVisible(g.visible ?? true);
        }

        // Restore atmosphere settings
        const atm = world.settings?.atmosphere;
        // areaParticles is optional: clear it so one world's never leaks into the next.
        store.setAtmosphere({ areaParticles: null, shadowRadius: undefined, ...(atm || {}) });
        // Restore spawn position
        if (world.settings?.spawnPosition) {
          store.setSpawnPosition(world.settings.spawnPosition);
        }
        // Walkable bounds (always set, so one world's never leaks into the next)
        store.setWorldBounds(parseWorldBounds(world.settings?.bounds));
        // World music (always set, so one world's never plays in the next)
        store.setWorldMusic(parseWorldMusic(world.settings?.music));
        // Restore mobile skybox URL
        if (world.settings?.mobileSkyboxUrl) {
          store.setMobileSkyboxUrl(world.settings.mobileSkyboxUrl);
        }
      }

      store.setLoadingMessage('Loading assets');
      const { data: assets, error: assetsError } = await supabase
        .from('world_assets').select('*').eq('world_id', worldUuid).order('created_at', { ascending: true });
      if (assetsError) throw assetsError;
      console.log(`[LoadWorld] ${worldUuid}: ${assets?.length || 0} assets loaded`);
      if (assets && assets.length > 0) {
        store.loadWorldAssets(assets);
        // NPCs still building (tab closed mid-build): pick their polls back up.
        if (worldId) {
          for (const [pid, prim] of Object.entries(useWorldStore.getState().primitives)) {
            if (prim.shape === 'npc' && prim.npc?.status === 'building' && prim.npc.buildId) watchNpcBuild(worldId, pid, prim.npc.buildId);
          }
        }
        const foliageRow = assets.find((a: any) => a.type === 'foliage');
        store.setFoliageStrokes(
          (foliageRow?.metadata?.strokes || []).map(unpackStroke).filter(Boolean) as any,
        );

        // Restore marble worlds
        const restoredMarble = assets
          .filter((a: any) => a.type === 'marble_world')
          .map((a: any, i: number) => ({
            id: `marble-${Date.now()}-${i}`,
            name: a.metadata?.name || 'AI World',
            splatUrl: a.url,
            radUrl: a.metadata?.radUrl || null,
            colliderUrl: a.metadata?.colliderUrl || null,
            position: a.metadata?.position || [0, 0, 0] as [number, number, number],
            rotation: a.metadata?.rotation || [0, 0, 0] as [number, number, number],
            scale: a.metadata?.scale || [1, 1, 1] as [number, number, number],
            groundPlaneOffset: a.metadata?.groundPlaneOffset || null,
            showCollider: false,
            isLocked: false,
          }));
        if (restoredMarble.length > 0) {
          setMarbleWorlds(restoredMarble);
          console.log(`[LoadWorld] Restored ${restoredMarble.length} marble worlds`);
        }
      }
      store.setLastSaved(world?.updated_at ? new Date(world.updated_at) : null);
      store.setLoadingMessage('Preparing scene');
      // Brief delay to let React render the loaded assets before hiding preloader
      setTimeout(() => { clearWorldHistory(); store.setWorldLoading(false); }, 600);
    } catch (error) {
      console.error('Error loading world:', error);
      store.setWorldLoading(false);
    }
  };

  // ---- Init ----
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      store.setUser(user);
      // Building requires a (free) account. Guests get the sign-in gate.
      if (!user) store.setPanel('showAuthPrompt', true);

      if (user) {
        try {
          const activeAvatar = await getActiveAvatar();
          if (activeAvatar?.model_url) {
            store.setAvatarFromDb(activeAvatar);
            return;
          }
        } catch (err) {
          console.warn('[Avatar] Could not load active wardrobe avatar:', err);
        }
      }

      const savedAvatar = localStorage.getItem('eb-avatar-url');
      // Clean up legacy/dead avatars (RPM shut down Jan 2026)
      if (savedAvatar && (
        savedAvatar.includes('d1a370nemizbjq.cloudfront.net') ||
        savedAvatar.includes('readyplayer.me') ||
        savedAvatar.includes('assets.meshy.ai') || // Meshy signed URLs are temporary
        savedAvatar.includes('64e91e8ce8c92e11dda01b07') ||
        savedAvatar.includes('66f85e4b3098f32ca0b2bce1') ||
        savedAvatar.includes('6740e4e6e95e3e1feecf3b0c')
      )) {
        localStorage.removeItem('eb-avatar-url');
        store.setAvatarUrl(null); // Force avatar selection
      } else if (savedAvatar) {
        store.setAvatarUrl(savedAvatar);
      } else {
        // No saved avatar — leave null to show avatar setup gate
        store.setAvatarUrl(null);
      }
    });
    store.setUserName(getStoredUserName());

    if (!isTemp && worldId && worldId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      store.setActualWorldId(worldId);
      loadWorld(worldId);
    } else {
      // New/temp world — no data to load, dismiss preloader quickly
      setTimeout(() => store.setWorldLoading(false), 400);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      store.setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Timer for temp session
  useEffect(() => {
    if (!user && isTemp) {
      const timer = setTimeout(() => {
        store.setPanel('showTimeWarning', true);
        store.setPanel('showAuthPrompt', true);
      }, 45 * 60 * 1000);
      return () => clearTimeout(timer);
    }
  }, [user, isTemp]);

  // All world data (environments, skyboxes, content) is loaded per-world from world_assets in loadWorld()

  // Fetch username from profiles
  useEffect(() => {
    if (user) {
      supabase.from('profiles').select('username').eq('id', user.id).single()
        .then(({ data }) => { if (data?.username) store.setUserName(data.username); });
    } else {
      store.setUserName(getStoredUserName());
    }
  }, [user]);

  // Build scene objects list
  const remoteObjectIds = useWorldStore((s) => s.remoteObjectIds);
  const sceneObjects = useMemo<SceneObject[]>(() => {
    const objects: SceneObject[] = [];
    if (avatarUrl) objects.push({ id: 'avatar-1', name: 'Player Avatar', type: 'avatar', visible: true, selected: selectedObjectId === 'avatar-1' });
    Object.entries(environmentModels).forEach(([id, model]) => {
      if (remoteObjectIds.has(id)) return;
      objects.push({ id, name: model.name || `Environment ${id.split('-')[1]}`, type: 'environment', visible: model.visible, selected: selectedObjectId === id, locked: !!model.transform?.isLocked });
    });
    Object.entries(contentItems).forEach(([id, item]) => {
      if (remoteObjectIds.has(id)) return;
      const label = item.type === 'audio' ? '🎵' : item.type === 'model' ? '📦' : item.type === 'splat' ? '☁️' : item.type === 'ply' ? '🔮' : '🖼️';
      objects.push({ id, name: `${label} ${item.filename || `Content ${id.split('-').pop()}`}`, type: 'object', visible: item.visible, selected: selectedObjectId === id, multiSelected: selectedObjectIds.includes(id), groupId: item.groupId, locked: !!item.isLocked });
    });
    Object.entries(screenProps).forEach(([id, prop]) => {
      if (remoteObjectIds.has(id)) return;
      const label = prop.type === 'browser' ? '🌐' : prop.type === 'video' ? '🎬' : '🖥️';
      objects.push({ id, name: `${label} ${prop.type === 'browser' ? 'Browser' : prop.type === 'video' ? 'Video' : 'Screen'} ${id.split('-').pop()}`, type: 'object', visible: prop.visible, selected: selectedObjectId === id, locked: !!prop.isLocked });
    });
    Object.entries(primitives).forEach(([id, prim]) => {
      if (remoteObjectIds.has(id)) return;
      objects.push({ id, name: prim.name || prim.shape, type: 'object', visible: prim.visible, selected: selectedObjectId === id, multiSelected: selectedObjectIds.includes(id), groupId: prim.groupId, locked: !!prim.isLocked });
    });
    Object.entries(lights).forEach(([id, light]) => {
      if (remoteObjectIds.has(id)) return;
      objects.push({ id, name: `💡 ${light.name}`, type: 'object', visible: light.visible, selected: selectedObjectId === id, locked: !!light.isLocked });
    });
    marbleWorlds.forEach((mw) => {
      objects.push({ id: mw.id, name: `✨ ${mw.name}`, type: 'object', visible: true, selected: selectedObjectId === mw.id });
    });
    // Spawn Point — always visible in the objects list
    objects.push({ id: 'spawn-point', name: '📍 Spawn Point', type: 'object', visible: true, selected: selectedObjectId === 'spawn-point' });
    // Apply custom scene order if set
    const order = store.sceneOrder;
    if (order.length > 0) {
      const idxMap = new Map(order.map((id, i) => [id, i]));
      objects.sort((a, b) => {
        const ai = idxMap.get(a.id) ?? 9999;
        const bi = idxMap.get(b.id) ?? 9999;
        return ai - bi;
      });
    }
    return objects;
  }, [avatarUrl, environmentModels, contentItems, screenProps, primitives, lights, selectedObjectId, selectedObjectIds, remoteObjectIds, marbleWorlds, store.sceneOrder]);

  // ---- Mobile detection ----
  const isQuestBrowser = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /Quest|OculusBrowser|GathrXR/i.test(navigator.userAgent);
  }, []);

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    // Quest runs Android but should use the desktop Canvas (with VR support)
    if (isQuestBrowser) return false;
    return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent) || window.innerWidth < 768;
  }, [isQuestBrowser]);

  const [mobileTab, setMobileTab] = useState<'none' | 'chat'>('none');
  const [mobileAudioActive, setMobileAudioActive] = useState(false);
  const mobileOrbitRef = useRef<any>(null);
  const mobileContainerRef = useRef<HTMLDivElement>(null);

  // Prevent pull-to-refresh and swipe-navigation on mobile via JS
  useEffect(() => {
    if (!isMobile) return;
    const el = mobileContainerRef.current || document.body;
    const prevent = (e: TouchEvent) => {
      // Allow scrolling inside chat overlay
      if ((e.target as HTMLElement)?.closest?.('.chat-overlay')) return;
      e.preventDefault();
    };
    el.addEventListener('touchmove', prevent, { passive: false });
    // Also prevent on document to catch edge swipes
    document.addEventListener('touchmove', prevent, { passive: false });
    // Prevent overscroll on the html/body level
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      el.removeEventListener('touchmove', prevent);
      document.removeEventListener('touchmove', prevent);
      document.documentElement.style.overscrollBehavior = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isMobile]);

  // ---- Mobile Render ----
  if (isMobile) {
    return (
      <>
        <WorldPreloader />
        <Suspense fallback={null}>
          <MusicRuntime worldId={worldId || null} />
          <MusicOverlay worldId={worldId || null} builder />
        </Suspense>
        <div ref={mobileContainerRef} className="world-builder-container">
          {/* 3D Canvas — full screen, no XR on mobile, wrapped in error boundary */}
          <CanvasErrorBoundary>
            <Canvas
              className="world-canvas"
              shadows
              gl={{
                antialias: false,
                preserveDrawingBuffer: true,
                powerPreference: 'low-power',
                failIfMajorPerformanceCaveat: false,
                alpha: false,
                // Mobile-specific: limit pixel ratio and context attributes
                depth: true,
                stencil: false,
              }}
              camera={{ position: [0, 1.6, 0], fov: 70, near: 0.1, far: 1000 }}
              dpr={[1, 1]}
              frameloop="always"
              onCreated={({ gl }) => {
                // Force clear color so canvas is never transparent/black
                gl.setClearColor('#0a0e1a', 1);
                // Force an initial render
                gl.clear();
                console.log('[Mobile Canvas] WebGL context created successfully');
              }}
            >
              <NoXRScene.Provider value={true}>
              {/* === LAYER 1: Absolute basics that never fail === */}
              <KTX2Initializer />
              <WaterDepthPass />
              <DebugInspector />
              <color attach="background" args={['#0a0e1a']} />
              <ambientLight intensity={0.6} />
              <directionalLight position={[10, 10, 5]} intensity={0.8} />
              <hemisphereLight args={['#87CEEB', '#1a1a2e', 0.3]} />

              {/* Ground — use the same Ground component as desktop for full textures */}
              <R3FErrorBoundary name="ground">
                <Ground
                  visible={groundVisible}
                  size={groundSettings?.size || 100}
                  color={groundSettings?.color || '#1a1a2e'}
                  textureUrl={groundSettings?.textureUrl || null}
                  texturePreset={groundSettings?.texturePreset || null}
                  pbrMaps={groundSettings?.pbrMaps || null}
                  textureScale={groundSettings?.textureScale || 4}
                  opacity={groundSettings?.opacity ?? 1}
                />
              </R3FErrorBoundary>

              {/* Fallback ground only when nothing is configured. This used to
                  also fire on !groundVisible, so turning the ground off swapped
                  it for a hardcoded plane instead of removing it. */}
              {groundVisible && !groundSettings && (
                <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} name="ground">
                  <planeGeometry args={[100, 100]} />
                  <meshStandardMaterial color="#1a1a2e" />
                </mesh>
              )}

              {/* === LAYER 2: Environment models (GLB) — wrapped in error boundaries === */}
              {/* Uses MobileSafeEnvironmentModel which avoids useXR() crash */}
              {Object.entries(environmentModels).map(([id, model]) => (
                <R3FErrorBoundary key={id} name={`env-model-${id}`}>
                  <Suspense fallback={null}>
                    <MobileSafeEnvironmentModel
                      url={model.url}
                      position={model.transform?.position || [0, 0, 0]}
                      scale={model.transform?.scale || 1}
                      rotation={model.transform?.rotation || [0, 0, 0]}
                    />
                  </Suspense>
                </R3FErrorBoundary>
              ))}

              {/* === LAYER 3: Content items — mobile-safe version, no splats === */}
              {Object.entries(contentItems).map(([id, item]) => (
                <R3FErrorBoundary key={id} name={`content-${id}`}>
                  <Suspense fallback={null}>
                    <MobileContentDisplay
                      content={{ id, ...item }}
                      isSelected={false}
                      onSelect={() => {}}
                    />
                  </Suspense>
                </R3FErrorBoundary>
              ))}

              {/* === LAYER 3b: Marble worlds — show collider mesh only, skip gaussian splats === */}
              {marbleWorlds.map((mw) => (
                <R3FErrorBoundary key={mw.id} name={`marble-${mw.id}`}>
                  <Suspense fallback={null}>
                    {mw.colliderUrl ? (
                      <MobileSafeEnvironmentModel
                        url={mw.colliderUrl}
                        position={mw.position || [0, 0, 0]}
                        scale={typeof mw.scale === 'number' ? mw.scale : 1}
                        rotation={mw.rotation || [0, 0, 0]}
                      />
                    ) : (
                      <group position={mw.position || [0, 0, 0]}>
                        <mesh>
                          <boxGeometry args={[2, 2, 2]} />
                          <meshBasicMaterial color="#00c2b3" wireframe transparent opacity={0.3} />
                        </mesh>
                      </group>
                    )}
                  </Suspense>
                </R3FErrorBoundary>
              ))}

              {/* === LAYER 3c: Skybox — prefer mobile-specific image, fall back to desktop skybox === */}
              {mobileSkyboxUrl ? (
                <R3FErrorBoundary name="skybox-mobile">
                  <MobileSkybox url={mobileSkyboxUrl} />
                </R3FErrorBoundary>
              ) : currentSkyboxId && skyboxes[currentSkyboxId] ? (
                <R3FErrorBoundary name="skybox">
                  <MobileSkybox url={skyboxes[currentSkyboxId].url} />
                </R3FErrorBoundary>
              ) : (
                <Sky distance={450000} sunPosition={[100, 20, 100]} inclination={0} azimuth={0.25} />
              )}

              {/* === LAYER 4: Primitives === */}
              {Object.entries(primitives).map(([id, prim]) => (
                prim.visible !== false && (
                  <R3FErrorBoundary key={id} name={`prim-${id}`}>
                    <ScenePrimitive
                      id={id}
                      primitive={prim}
                      isSelected={false}
                      transformMode="translate"
                      onSelect={() => {}}
                      onTransformChange={() => {}}
                      orbitRef={mobileOrbitRef}
                    />
                  </R3FErrorBoundary>
                )
              ))}

              {/* === LAYER 5: Lights === */}
              {Object.entries(lights).map(([id, light]) => (
                light.visible !== false && (
                  <R3FErrorBoundary key={id} name={`light-${id}`}>
                    <SceneLight
                      id={id}
                      light={light}
                      isSelected={false}
                      onSelect={() => {}}
                      onTransformChange={() => {}}
                      orbitRef={mobileOrbitRef}
                    />
                  </R3FErrorBoundary>
                )
              ))}

              {/* Screen and browser use controls are available on mobile too. */}
              {Object.entries(screenProps).map(([id, prop]) => prop.visible && (
                <R3FErrorBoundary key={id} name={`screen-${id}`}>
                  <group position={prop.position} rotation={prop.rotation} scale={prop.scale}>
                    {prop.type === 'browser' ? <BrowserProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} url={prop.browserUrl || ''} /> :
                      <ScreenProp position={[0, 0, 0]} rotation={[0, 0, 0]} scale={1} screenType={prop.type} videoUrl={prop.videoUrl} streamData={prop.streamData || (prop.type === 'screen-share' ? activeScreenShare : null)} />}
                    <MediaPanelActions id={id} item={prop} />
                  </group>
                </R3FErrorBoundary>
              ))}

              {/* === LAYER 7: Atmosphere (particles, fog, lighting) — no bloom/useXR on mobile === */}
              <R3FErrorBoundary name="atmosphere">
                <MobileAtmosphere />
              </R3FErrorBoundary>

              {/* No avatar on mobile — first-person spectator mode */}
              <OrbitControls ref={mobileOrbitRef} enablePan={false} enableZoom={false} enableRotate maxPolarAngle={Math.PI * 0.85} minPolarAngle={Math.PI * 0.15} target={[0, 1.6, -5]} />
              <MobileFirstPersonCamera orbitRef={mobileOrbitRef} />
              <Suspense fallback={null}><MusicListenerFrame /></Suspense>
              </NoXRScene.Provider>
            </Canvas>
          </CanvasErrorBoundary>

          {/* Presence tracking on mobile (no avatar sync, just shows in online users) */}
          {worldId && <PresenceTracker worldId={worldId} />}

          {/* Voice chat — listen only on mobile (no mic), delayed until world
              loads. Same rule as desktop: the Join button must be visible or
              voice cannot start at all. */}
          {userName && worldId && user && !store.worldLoading && (
            <VoiceChatWrapper roomName={worldId} userName={userName} minimal listenOnly />
          )}

          {/* Chat overlay */}
          {mobileTab === 'chat' && worldId && (
            <ChatOverlay worldId={worldId} onClose={() => setMobileTab('none')} />
          )}

          {/* Online users bar */}
          <OnlineUsersBar />
          <MediaPanelDialogs worldId={worldId || ''} />

          {/* Auth prompt */}
          {(showAuthPrompt || showVoiceChatPrompt) && (
            <div className="auth-modal-overlay" onClick={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); }}>
              <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="auth-modal-close" onClick={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); }}>×</button>
                <AuthComponent onSuccess={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); }} />
              </div>
            </div>
          )}

          {/* Mobile joystick for movement */}
          <MobileJoystick />

          {/* No camera controls — always first person on mobile */}

          {/* Bottom tab bar */}
          <div className="mobile-tab-bar">
            <button className="mobile-tab" onClick={() => { if (window.confirm('Leave this world?')) navigate(user ? '/dashboard' : '/'); }}>
              <LogOut size={20} />
              <span>Leave</span>
            </button>
            <button className={`mobile-tab ${mobileTab === 'chat' ? 'active' : ''}`} onClick={() => setMobileTab(mobileTab === 'chat' ? 'none' : 'chat')}>
              <MessageSquare size={20} />
              <span>Chat</span>
            </button>
            <button className={`mobile-tab ${mobileAudioActive ? 'active' : ''}`} onClick={() => {
              // Mobile browsers require a user gesture to resume AudioContext
              const ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
              if (ctx) {
                const ac = new ctx();
                ac.resume().then(() => { ac.close(); });
              }
              // Also resume any existing suspended contexts
              if (typeof document !== 'undefined') {
                document.querySelectorAll('audio').forEach((a) => { try { a.play(); } catch {} });
              }
              setMobileAudioActive(true);
            }}>
              <Volume2 size={20} />
              <span>{mobileAudioActive ? 'Listening' : 'Listen'}</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  // ---- Desktop Render ----
  return (
    <>
      <WorldPreloader />
      <MediaPanelDialogs worldId={worldId || ''} />
      <Suspense fallback={null}>
        <MusicRuntime worldId={worldId || null} />
        {!isInVR && <MusicOverlay worldId={worldId || null} builder />}
      </Suspense>
      <div className="world-builder-container">
        {/* Header — hidden during VR so HTML overlays don't bleed into Quest recording */}
        <div className="world-header glassmorphism-dark" style={isInVR ? { display: 'none' } : undefined}>
          <div className="header-left">
            <h1>{worldName}</h1>
            {userName && <div className="user-info"><User size={12} /><span>{userName}</span></div>}
          </div>
          <div className="world-controls">
            {/* Social buttons - always visible */}
            <button className={`btn-glass ${showChat ? 'active' : ''}`} onClick={() => store.togglePanel('showChat')} title="Chat"><MessageSquare size={14} /><span>Chat</span></button>
            {/* Signed in, VoiceChatWrapper renders Join Voice / the mic toggle
                into this slot; signed out, the button is the sign-in prompt. */}
            {!user && <VoiceButtonOverlay user={user} onAuthPrompt={() => store.setPanel('showVoiceChatPrompt', true)} />}
            <div id="gx-voice-slot" className="gx-voice-slot" />
            <button className={`btn-glass ${showScreenShare ? 'active' : ''}`} onClick={() => store.togglePanel('showScreenShare')} title="Share your screen, or place a screen / video panel"><Share2 size={14} /><span>Share Screen</span></button>
            <BuilderMusicButton />

            <div className="controls-divider" />

            {/* Add dropdown */}
            <div className="menu-group" onClick={(e) => e.stopPropagation()}>
              <button className={`menu-trigger ${openMenu === 'add' ? 'open' : ''}`} onClick={() => setOpenMenu(openMenu === 'add' ? null : 'add')}>
                <Plus size={14} /> Add <ChevronDown size={10} />
              </button>
              {openMenu === 'add' && (
                <div className="menu-dropdown">
                  <button className={`menu-item ${showObjectLibrary ? 'active' : ''}`} onClick={() => { setShowObjectLibrary(true); setOpenMenu(null); }}><FolderOpen size={14} /> Asset Library</button>
                  {canBuild && (MARBLE_ENABLED
                    ? <button className="menu-item" onClick={() => { setShowMarblePanel(true); setOpenMenu(null); }}><Sparkles size={14} /> Marble World</button>
                    : <button className="menu-item menu-item-soon" aria-disabled="true" title="Coming soon: AI-generated walk-in worlds" onClick={() => { setMarbleSoonAt(Date.now()); setOpenMenu(null); }}><Sparkles size={14} /> Marble World <span className="menu-soon-badge">Soon</span></button>)}
                  {canBuild && <button className="menu-item" onClick={() => { setShowObjectGen(true); setOpenMenu(null); }}><Box size={14} /> AI Object</button>}
                  {isAppAdmin && <button className="menu-item" onClick={() => { setOpenMenu(null); window.open('/studio', '_blank', 'noopener'); }}><DraftingCompass size={14} /> Studio</button>}
                  {canBuild && <div className="menu-divider" />}
                  {canBuild && <button className="menu-item" onClick={() => { store.setPanel('showEnvironmentPicker', true); setOpenMenu(null); }}><Globe size={14} /> Import 3D Environment</button>}
                  <button className="menu-item" onClick={() => { store.setPanel('showSkyboxManager', true); setOpenMenu(null); }}><Image size={14} /> Skyboxes</button>
                  <button className="menu-item" onClick={() => { store.setPanel('showContentBrowser', true); setOpenMenu(null); }}><Upload size={14} /> My Uploads</button>
                  {canBuild && <div className="menu-divider" />}
                  <button className="menu-item" onClick={() => { store.setPanel('showBrowserPropPicker', true); setOpenMenu(null); }}><GlobeIcon size={14} /> Browser Panel</button>
                  {canBuild && (<>
                    <div className="menu-divider" />
                    <div className="menu-label">Characters</div>
                    <button className="menu-item" title="A character that lives in your world" onClick={() => { setShowNpcCreator(true); setOpenMenu(null); }}><UserRound size={14} /> NPC</button>
                    <div className="menu-divider" />
                    <div className="menu-label">Interior design</div>
                    <button className="menu-item" title="Select a wall, describe a look, and AI designs three textures for it" onClick={() => { setShowWallDesigner(true); setOpenMenu(null); }}><Wand2 size={14} /> Design a wall (AI)</button>
                    <div className="menu-divider" />
                    <div className="menu-label">Portals</div>
                    <button className="menu-item" onClick={() => { startPlacePrimitive('portal'); setOpenMenu(null); }}><CircleDot size={14} /> Portal entrance</button>
                    {(() => {
                      const waiting = portalNeedingExit();
                      return (
                        <button className="menu-item" disabled={!waiting} style={waiting ? undefined : { opacity: 0.4, cursor: 'default' }}
                          title={waiting ? 'Place where the portal leads' : 'Place a portal entrance first'}
                          onClick={() => { if (waiting) { startPlacePortalExit(waiting); setOpenMenu(null); } }}><CircleDot size={14} /> Portal exit</button>
                      );
                    })()}
                  </>)}
                  {canBuild && (<>
                    <div className="menu-divider" />
                    <div className="menu-label">Audio</div>
                    <button className="menu-item" title="A speaker that plays music around it: louder up close" onClick={() => { startPlaceSpeaker(); setOpenMenu(null); }}><Speaker size={14} /> Speaker</button>
                    <button className="menu-item" title="A soundtrack everyone hears everywhere in the world" onClick={() => { setShowMusicPanel(true); setOpenMenu(null); }}><Music size={14} /> World music</button>
                  </>)}
                  <div className="menu-divider" />
                  <div className="menu-label">Utility</div>
                  <button className="menu-item" onClick={() => { store.setSelectedObjectId('spawn-point'); store.setIsEditingEnvironment(true); setOpenMenu(null); }}><MapPin size={14} /> Spawn Point</button>
                </div>
              )}
            </div>

            {/* Games dropdown: table games and arcade cabinets */}
            {canBuild && (
              <div className="menu-group" onClick={(e) => e.stopPropagation()}>
                <button className={`menu-trigger ${openMenu === 'games' ? 'open' : ''}`} onClick={() => setOpenMenu(openMenu === 'games' ? null : 'games')}>
                  <Gamepad2 size={14} /> Games <ChevronDown size={10} />
                </button>
                {openMenu === 'games' && (
                  <div className="menu-dropdown menu-dropdown--games">
                    <div className="menu-label">Table games</div>
                    {gameList().map((g) => (
                      <button key={g.kind} className="menu-item" title={g.blurb} onClick={() => { startPlacePrimitive('gametable', g.kind); setOpenMenu(null); }}><g.icon size={14} /> {g.label}</button>
                    ))}
                    <div className="menu-divider" />
                    <div className="menu-label">Arcade</div>
                    {!arcadeMenu.ready ? (
                      <div className="menu-note">Loading arcade games…</div>
                    ) : arcadeMenu.games.length === 0 ? (
                      <div className="menu-note">No arcade games in this build yet</div>
                    ) : (
                      <div className="menu-arcade-grid">
                        <button className="menu-arcade-item menu-arcade-item--board" title="A scoreboard for the arcade games: pick the game, period, look and size once it's placed"
                          onClick={() => { startPlaceLeaderboard(); setOpenMenu(null); }}>
                          <span className="menu-arcade-board" aria-hidden="true">
                            <span className="menu-arcade-board-title">HIGH SCORES</span>
                            {[1, 2, 3].map((n) => <span key={n} className="menu-arcade-board-row"><i className={`m${n}`}>{n}</i><b /><em /></span>)}
                          </span>
                          <span className="menu-arcade-board-label"><strong>Leaderboard</strong><small>Top scores, live</small></span>
                        </button>
                        {arcadeMenu.games.map((g) => (
                          <button key={g.id} className="menu-arcade-item" title={g.blurb}
                            style={{ '--arc-c1': g.colors.primary, '--arc-c2': g.colors.secondary } as React.CSSProperties}
                            onClick={() => { startPlaceArcade(g.id); setOpenMenu(null); }}>
                            <img src={artDataUrl(g.artSvg)} alt="" draggable={false} />
                            <span>{g.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="menu-divider" />
                    <div className="menu-label">World game</div>
                    {WORLD_GAME_MENU.map(({ role, label, Icon }) => (
                      <button key={role} className="menu-item" title={ROLE_HINT[role]} data-testid={`menu-worldgame-${role}`}
                        onClick={() => { startPlaceWorldGame(role); setOpenMenu(null); }}><Icon size={14} /> {label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Manage dropdown */}
            <div className="menu-group" onClick={(e) => e.stopPropagation()}>
              <button className={`menu-trigger ${openMenu === 'manage' ? 'open' : ''}`} onClick={() => setOpenMenu(openMenu === 'manage' ? null : 'manage')}>
                <LayoutGrid size={14} /> View <ChevronDown size={10} />
              </button>
              {openMenu === 'manage' && (
                <div className="menu-dropdown">
                  <button className={`menu-item ${showObjectsList ? 'active' : ''}`} onClick={() => { store.togglePanel('showObjectsList'); setOpenMenu(null); }}><Layers size={14} /> Scene Objects</button>
                  <div className="menu-divider" />
                  <button className={`menu-item ${showGroundControls ? 'active' : ''}`} onClick={() => { store.togglePanel('showGroundControls'); setOpenMenu(null); }}><Layers size={14} /> Ground</button>
                  <button className={`menu-item ${showAtmosphereControls ? 'active' : ''}`} onClick={() => { store.togglePanel('showAtmosphereControls'); setOpenMenu(null); }}><Cloud size={14} /> Atmosphere</button>
                  <button className={`menu-item ${showEnvironmentControls && selectedObjectId ? 'active' : ''}`}
                    onClick={() => { if (selectedObjectId && (environmentModels[selectedObjectId] || contentItems[selectedObjectId] || screenProps[selectedObjectId])) { store.togglePanel('showEnvironmentControls'); setOpenMenu(null); } }}
                    style={!selectedObjectId ? { opacity: 0.4, cursor: 'default' } : undefined}
                  ><ZoomIn size={14} /> Object Controls</button>
                </div>
              )}
            </div>

            <VRButtonComponent onEnterVR={() => xrStore.enterVR()} />

            <div className="controls-divider" />

            {/* World settings dropdown (owner or host) */}
            {(isOwner || role === 'host') && (
              <div className="menu-group" onClick={(e) => e.stopPropagation()}>
                <button className={`menu-trigger ${openMenu === 'world' ? 'open' : ''}`} onClick={() => setOpenMenu(openMenu === 'world' ? null : 'world')}>
                  <Settings size={14} /> World <ChevronDown size={10} />
                </button>
                {openMenu === 'world' && (
                  <div className="menu-dropdown">
                    {isOwner && user && <button className={`menu-item ${isSaving ? 'active' : ''}`} onClick={() => { saveWorld(); setOpenMenu(null); }}><Save size={14} /> {isSaving ? 'Saving...' : 'Save'}</button>}
                    {isOwner && user && <button className="menu-item" disabled={capturingThumb} onClick={() => { captureThumbnailNow(); setOpenMenu(null); }} title="Frame the shot, then capture it as this world's card image"><Camera size={14} /> {capturingThumb ? 'Capturing…' : 'Set World Cover'}</button>}
                    {isOwner && user && <button className={`menu-item ${!isPublic ? 'active' : ''}`} onClick={() => { store.setIsPublic(!isPublic); if (isPublic && !accessCode) store.generateAccessCode(); setOpenMenu(null); }}>
                      {isPublic ? <Globe size={14} /> : <Lock size={14} />} {isPublic ? 'Public' : accessCode ? `Private (${accessCode})` : 'Private'}
                    </button>}
                    {isOwner && actualWorldId && <button className="menu-item" onClick={async () => { store.setIsLoadingShortURL(true); store.setPanel('showShareModal', true); const url = `${window.location.origin}/world/${actualWorldId}?name=${encodeURIComponent(worldName)}`; const shortURL = await URLShortenerService.getShortenedURL(url, worldName); store.setShortenedURL(shortURL); store.setIsLoadingShortURL(false); setOpenMenu(null); }}><Share2 size={14} /> Share Link</button>}
                    {isOwner && <div className="menu-divider" />}
                    <button className="menu-item" onClick={() => { setShowMusicPanel(true); setOpenMenu(null); }}><Music size={14} /> Music</button>
                    <button className="menu-item" onClick={() => { setShowWorldSettings(true); setOpenMenu(null); }}><Users size={14} /> Multiplayer Settings</button>
                  </div>
                )}
              </div>
            )}

            <button className="btn-glass btn-leave" onClick={() => { if (window.confirm('Are you sure you want to leave this world?')) navigate(user ? '/dashboard' : '/'); }} title="Leave World"><LogOut size={14} /><span>Leave</span></button>
          </div>
        </div>

        {/* 3D Canvas */}
        <Canvas
          className="world-canvas"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          gl={{ antialias: !isQuestBrowser, powerPreference: 'high-performance', alpha: false, preserveDrawingBuffer: true, failIfMajorPerformanceCaveat: false, localClippingEnabled: true }}
          // dpr cap: 2 on Retina = 4x the pixels of dpr 1, and splat worlds
          // are fill-rate-bound (overlapping transparent quads) — dpr 2 was
          // pinning an M3 Pro at single-digit FPS. AdaptiveDpr (below) drops
          // to 1 during camera motion. Override for testing with ?dpr=1 etc.
          dpr={isQuestBrowser ? 1 : (URL_DPR || [1, 1.5])}
          camera={{ fov: 60, near: 0.1, far: 1000 }}
          onCreated={({ gl, scene, camera }) => {
            gl.shadowMap.enabled = false;
            // Dev-only handle for the headless VR (hand tracking) tests.
            if (import.meta.env.DEV) (window as any).__gathrxrThree = { gl, scene, camera, store: useWorldStore, xr: xrStore };
          }}
        >
          <KTX2Initializer />
              <WaterDepthPass />
              <DebugInspector />
          {!isQuestBrowser && !URL_DPR && <AdaptiveDpr idle={1.5} moving={1} />}
          {SHOW_PERF && (
            <Suspense fallback={null}>
              <PerfOverlay position="bottom-left" deepAnalyze />
            </Suspense>
          )}
          <XR store={xrStore}>
            <VRBackgroundEnforcer />
            <VRPreloader />
            <VRFrameOptimizer />
            <VRPerformanceManager>
              <VROptimizer />
              {/* Main scene content - Sky/Environment may suspend for asset loading */}
              <Suspense fallback={null}>
                <ResourceLogger />
                {currentSkyboxId && skyboxes[currentSkyboxId] ? (
                  <SafeR3F name="skybox"><Environment files={skyboxes[currentSkyboxId].url} background /></SafeR3F>
                ) : (
                  <>
                    <Sky distance={450000} sunPosition={[100, 20, 100]} inclination={0} azimuth={0.25} />
                    <SafeR3F name="env-hdri"><Environment files="/hdri/venice_sunset_1k.hdr" /></SafeR3F>
                  </>
                )}
                <AtmosphereScene />
                <Ground visible={groundVisible} size={groundSettings.size} color={groundSettings.color} textureUrl={groundSettings.textureUrl} texturePreset={groundSettings.texturePreset} pbrMaps={groundSettings.pbrMaps || null} textureScale={groundSettings.textureScale} opacity={groundSettings.opacity ?? 1} />
                <Suspense fallback={null}><PaintedFoliage /></Suspense>
                <Suspense fallback={null}><FoliagePreview /></Suspense>
                {groundVisible && groundSettings.showGrid && <gridHelper args={[groundSettings.size, groundSettings.size, 0x444444, 0x222222]} position={[0, -0.49, 0]} />}
                {/* Build Mode v2: brighter snap-sized grid while the architect camera is on */}
                {topDownView && (
                  <gridHelper
                    args={[
                      groundSettings.size,
                      Math.max(4, Math.min(400, Math.round(groundSettings.size / (snapSettings.gridSize || 1)))),
                      0x00c2b3,
                      0x2a6f6a,
                    ]}
                    position={[0, 0.015, 0]}
                  />
                )}
                <AvatarManager avatarUrl={avatarUrl} isEditingEnvironment={isEditingEnvironment || topDownView} cameraMode={cameraMode} userName={userName} spawnPosition={store.spawnPosition} onGroupRef={(ref) => { localAvatarRef.current = ref; if (import.meta.env.DEV) (window as any).__gxAvatar = ref; }} />
                {Object.entries(environmentModels).map(([id, model]) => (
                  model.visible && (
                    <SimpleEnvironmentModel key={id} url={model.url} position={model.transform.position} scale={model.transform.scale} rotation={model.transform.rotation} collision={model.collision} isSelected={selectedObjectId === id} />
                  )
                ))}
                {Object.entries(contentItems).map(([id, item]) => (
                  item.visible && (
                    <ContentDisplay key={id} content={{ id, ...item }} isSelected={selectedObjectId === id}
                      transformMode={transformMode}
                      orbitRef={orbitControlsRef}
                      showSeatMarker
                      onMaterialSlots={(slots) => setModelSlots((prev) => (
                        prev[id]?.length === slots.length ? prev : { ...prev, [id]: slots }
                      ))}
                      onTransformChange={(updates) => {
                        const mapped: any = {};
                        if (updates.position) mapped.position = updates.position;
                        if (updates.rotation) mapped.rotation = updates.rotation;
                        if (updates.scale) mapped.scale = updates.scale[0];
                        store.updateContentItem(id, mapped);
                        broadcastObjectUpdate('content', id, mapped);
                        // Dragging a grouped model moves the whole group.
                        if (item.groupId && updates.position) {
                          moveGroupSiblings(
                            worldId, item.groupId, id,
                            updates.position[0] - item.position[0],
                            updates.position[1] - item.position[1],
                            updates.position[2] - item.position[2],
                          );
                        }
                      }}
                      onSelect={() => {
                        if (useWorldStore.getState().buildTool !== 'none') return;
                        if (useWorldStore.getState().contentItems[id]?.isLocked) { store.flashLocked(id); return; }
                        store.setSelectedObjectId(id); store.setIsEditingEnvironment(true); store.setPanel('showEnvironmentControls', true);
                      }} />
                  )
                ))}
                {/* Marble AI Worlds */}
                {marbleWorlds.map((mw) => (
                  <MarbleWorldWithControls
                    key={mw.id}
                    mw={mw}
                    isSelected={selectedObjectId === mw.id}
                    transformMode={transformMode}
                    orbitRef={orbitControlsRef}
                    onSelect={() => { store.setSelectedObjectId(mw.id); store.setIsEditingEnvironment(true); store.setPanel('showEnvironmentControls', true); }}
                    onTransformChange={(updates) => {
                      setMarbleWorlds((prev) => prev.map((w) =>
                        w.id === mw.id ? { ...w, ...updates } : w
                      ));
                    }}
                  />
                ))}
                {Object.entries(screenProps).map(([id, prop]) => prop.visible && (
                  <SceneMediaPanel key={id} id={id} item={prop} selected={selectedObjectId === id}
                    mode={transformMode} orbitRef={orbitControlsRef}
                    onDone={() => { store.setSelectedObjectId(null); store.setPanel('showEnvironmentControls', false); store.setIsEditingEnvironment(false); }}
                    onSelect={() => { if (prop.isLocked) { store.flashLocked(id); return; } store.setSelectedObjectId(id); store.setIsEditingEnvironment(true); store.setPanel('showEnvironmentControls', true); }}
                    onChange={(updates) => { store.updateScreenProp(id, updates); broadcastObjectUpdate('screenprop', id, updates); }} />
                ))}
                {pendingPanel && canBuild && <PanelPlacement browser={pendingPanel.type === 'browser'}
                  onCancel={() => setPendingPanel(null)}
                  onPlace={(position, rotation) => {
                    const id = `screen-${crypto.randomUUID()}`;
                    const item: ScreenPropItem = { ...pendingPanel, position, rotation, scale: 1, visible: true, isLocked: false, uploadedAt: Date.now() };
                    store.addScreenProp(id, item);
                    broadcastObjectAdd('screenprop', id, item);
                    store.setSelectedObjectId(null);
                    store.setIsEditingEnvironment(false);
                    store.setPanel('showEnvironmentControls', false);
                    setPendingPanel(null);
                  }} />}
                {Object.entries(primitives).map(([id, prim]) => (
                  <ScenePrimitive
                    key={id}
                    id={id}
                    primitive={prim}
                    isSelected={selectedObjectId === id}
                    transformMode={transformMode}
                    onSelect={() => {
                      // Build Mode v2: while a tool is armed, clicks belong to the
                      // tool (paint / place / draw), not to selection.
                      const bs = useWorldStore.getState();
                      if (bs.buildTool === 'paint') {
                        if (!canBuild) return;
                        const color = bs.paintColor;
                        if (primitives[id]?.color === color) return;
                        store.updatePrimitive(id, { color });
                        broadcastObjectUpdate('primitive', id, { color });
                        return;
                      }
                      if (bs.buildTool !== 'none') return;
                      store.setSelectedObjectId(id); store.setIsEditingEnvironment(true);
                    }}
                    onTransformChange={(updates) => {
                      store.updatePrimitive(id, updates);
                      broadcastObjectUpdate('primitive', id, updates);
                      // Apply delta to group members (shapes + content models)
                      const gid = prim.groupId;
                      if (gid && updates.position) {
                        moveGroupSiblings(
                          worldId, gid, id,
                          updates.position[0] - prim.position[0],
                          updates.position[1] - prim.position[1],
                          updates.position[2] - prim.position[2],
                        );
                      }
                      // Apply scale to group members as per-axis RATIOS (not a
                      // copy — group members like room walls have different
                      // sizes). Y positions scale by the same ratio so walls
                      // stay grounded (a wall's center sits at height/2):
                      // shrink a wall's height and the whole room gets shorter.
                      if (gid && updates.scale) {
                        const rx = prim.scale[0] ? updates.scale[0] / prim.scale[0] : 1;
                        const ry = prim.scale[1] ? updates.scale[1] / prim.scale[1] : 1;
                        const rz = prim.scale[2] ? updates.scale[2] / prim.scale[2] : 1;
                        // The gizmo reports position+rotation+scale together on
                        // every mouse-up, so this block also fires after pure
                        // MOVES with unchanged scale — and must not touch the
                        // members then (it would clobber the group move above
                        // with stale positions).
                        if (Math.abs(rx - 1) < 0.001 && Math.abs(ry - 1) < 0.001 && Math.abs(rz - 1) < 0.001) return;
                        const members = store.getGroupMembers(gid);
                        // Member positions scale about the grabbed object (XZ,
                        // in its local frame) and about the ground (Y) — so
                        // contiguous groups like stair runs stay contiguous
                        // instead of opening gaps between members.
                        const pvx = prim.position[0], pvz = prim.position[2];
                        const th = prim.rotation[1];
                        const tc = Math.cos(th), ts = Math.sin(th);
                        for (const mid of members) {
                          if (mid === id) continue;
                          const mp = primitives[mid];
                          if (!mp) continue;
                          const wx = mp.position[0] - pvx, wz = mp.position[2] - pvz;
                          const lx = (wx * tc - wz * ts) * rx;
                          const lz = (wx * ts + wz * tc) * rz;
                          const mUpd = {
                            scale: [mp.scale[0] * rx, mp.scale[1] * ry, mp.scale[2] * rz] as [number, number, number],
                            position: [pvx + lx * tc + lz * ts, mp.position[1] * ry, pvz - lx * ts + lz * tc] as [number, number, number],
                          };
                          store.updatePrimitive(mid, mUpd);
                          broadcastObjectUpdate('primitive', mid, mUpd);
                        }
                        // Re-anchor the edited member too (the gizmo scales
                        // about its center, which would leave it floating).
                        if (Math.abs(ry - 1) > 0.001) {
                          const selfPos: [number, number, number] = [
                            updates.position?.[0] ?? prim.position[0],
                            prim.position[1] * ry,
                            updates.position?.[2] ?? prim.position[2],
                          ];
                          store.updatePrimitive(id, { position: selfPos });
                          broadcastObjectUpdate('primitive', id, { position: selfPos });
                        }
                      }
                    }}
                    orbitRef={orbitControlsRef}
                  />
                ))}
                {Object.entries(lights).map(([id, light]) => (
                  <SceneLight
                    key={id}
                    id={id}
                    light={light}
                    isSelected={selectedObjectId === id}
                    onSelect={() => { store.setSelectedObjectId(id); store.setIsEditingEnvironment(true); }}
                    onTransformChange={(updates) => { store.updateLight(id, updates); broadcastObjectUpdate('light', id, updates); }}
                    orbitRef={orbitControlsRef}
                  />
                ))}
                <SpawnPointMarker
                  position={store.spawnPosition}
                  isSelected={selectedObjectId === 'spawn-point'}
                  onSelect={() => { store.setSelectedObjectId('spawn-point'); store.setIsEditingEnvironment(true); }}
                  onTransformChange={(pos) => store.setSpawnPosition(pos)}
                  orbitRef={orbitControlsRef}
                  canBuild={canBuild}
                />
                <NonVRControls avatarUrl={avatarUrl} environmentModels={environmentModels} orbitRef={orbitControlsRef} />
                <CameraFocuser orbitRef={orbitControlsRef} />
                {/* Build Mode v2 */}
                <BuildToolsLayer worldId={worldId} canBuild={canBuild} />
                <WallDesignBridge />
                <LockedFlash />
                <TopDownCamera orbitRef={orbitControlsRef} />
                <RoofCutaway />
              </Suspense>
              {/* Own boundary so a loading VR avatar can't suspend MultiplayerSync. */}
              <Suspense fallback={null}>
                <VRScene avatarUrl={avatarUrl && avatarUrl !== 'skip' ? avatarUrl : undefined} worldId={worldId} />
              </Suspense>
              {/* VRBloom removed — causes VR black screen by toggling gl.xr.enabled during render */}
              <MultiplayerSync worldId={worldId || 'default'} localAvatarRef={localAvatarRef} />
              <PortalTraveler avatarRef={localAvatarRef} worldTrips={false} />
              <Suspense fallback={null}><MusicListenerFrame /></Suspense>
            </VRPerformanceManager>
          </XR>
        </Canvas>

        {/* Hide all HTML overlays during VR — Quest recording composites them as black rectangles */}
        {!isInVR && <>
        {/* Connecting indicator — non-blocking, auto-dismisses */}
        {!multiplayerInfo.connected && (
          <div style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(5, 14, 24, 0.88)', padding: '10px 20px',
            borderRadius: 12, border: '1px solid rgba(0, 194, 179, 0.2)',
            color: '#fff', fontSize: 13, backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}>
            <div style={{
              width: 16, height: 16, border: '2px solid rgba(0, 194, 179, 0.3)',
              borderTopColor: '#00C2B3', borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            Connecting to multiplayer...
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Build Mode v2 — armed-tool hint strip */}
        {canBuild && buildTool !== 'none' && (
          <div style={{
            position: 'fixed', bottom: 72, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1001, display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(5, 14, 24, 0.9)', padding: '8px 16px',
            borderRadius: 12, border: '1px solid rgba(0, 194, 179, 0.35)',
            color: '#fff', fontSize: 12, backdropFilter: 'blur(10px)',
            pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#00C2B3', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {buildTool === 'place' ? 'Placing' : buildTool === 'wall' ? 'Wall' : buildTool === 'room' ? 'Room' : buildTool === 'door' ? 'Doorway' : buildTool === 'window' ? 'Window' : buildTool === 'arch' ? 'Arch' : buildTool === 'arch-wide' ? 'Wide Arch' : buildTool === 'stairs' ? 'Stairs' : buildTool === 'box' ? 'Box' : buildTool === 'foliage' ? 'Foliage' : buildTool === 'select' ? 'Select' : 'Paint'}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.75)' }}>
              {buildTool === 'select' && `Drag a box to select · drag inside it to move${selectedObjectIds.length ? ` · ${selectedObjectIds.length} selected — G groups · ⌘D duplicates` : ''} · Esc clears`}
              {buildTool === 'place' && (placingShape === 'portal'
                ? (placingPortalExit ? 'Click where the portal leads (its exit) · R rotate 90° · Esc cancels' : 'Click to place the portal entrance · R rotate 90° · Esc cancels')
                : 'Click to place · R rotate 90° · Esc / right-click exit')}
              {buildTool === 'wall' && 'Drag on the ground to draw · walls chain from the last end · Esc exits'}
              {buildTool === 'room' && 'Drag a rectangle → 4 walls + floor · Esc exits'}
              {buildTool === 'door' && 'Hover a wall · click to cut a doorway · Esc exits'}
              {buildTool === 'box' && 'Drag a footprint on the grid · then move to pull the height · click to place'}
              {buildTool === 'foliage' && 'Drag to paint · E erase · [ ] size · T trees · Z undo · Esc exits'}
              {buildTool === 'arch' && 'Hover a wall · click to cut an arched doorway · Esc exits'}
              {buildTool === 'arch-wide' && 'Hover a wall · click to cut a wide arch · Esc exits'}
              {buildTool === 'window' && 'Hover a wall · click to cut a window · Esc exits'}
              {buildTool === 'stairs' && (stairSweep > 0
                ? `Curved ${stairSweep}° · drag: start = pivot, end = first step · R cycles curve · Esc exits`
                : 'Drag on the ground · steps rise along the drag · R curves the run · Esc exits')}
              {buildTool === 'paint' && 'Click a shape to recolor it · Esc exits'}
            </span>
          </div>
        )}

        <FoliageBrushPanel />

        {/* Overlays */}
        {showChat && <ChatOverlay worldId={worldId || ''} onClose={() => store.setPanel('showChat', false)} />}
        {(showScreenShare || showScreenPropPicker) && <ScreenShare
          onClose={() => { store.setPanel('showScreenShare', false); store.setPanel('showScreenPropPicker', false); }}
          onPlace={canBuild ? () => beginPanelPlacement({ type: 'screen-share' }) : undefined}
          onVideoPlace={canBuild ? (videoUrl) => beginPanelPlacement({ type: 'video', videoUrl }) : undefined} />}
        {pendingPanel && <div className="panel-placement-hint" role="status">Click to place · R rotates · Esc cancels<button onClick={() => setPendingPanel(null)}>Cancel</button></div>}

        {showContentBrowser && (
          <ContentBrowser
            contentItems={contentItems}
            user={user}
            onClose={() => store.setPanel('showContentBrowser', false)}
            onDelete={(id) => { store.deleteContentItem(id); broadcastObjectDelete('content', id); if (selectedObjectId === id) store.setSelectedObjectId(null); }}
            onToggleVisibility={(id) => { store.updateContentItem(id, { visible: !contentItems[id].visible }); broadcastObjectUpdate('content', id, { visible: !contentItems[id].visible }); }}
            onSelect={(id) => { store.setSelectedObjectId(id); store.setIsEditingEnvironment(true); store.setPanel('showEnvironmentControls', true); store.setPanel('showContentBrowser', false); if (contentItems[id] && !contentItems[id].visible) store.updateContentItem(id, { visible: true }); }}
            onAdd={(url, filename, fileType) => {
              let type: 'image' | 'audio' | 'model' | 'splat' | 'ply' = fileType as any;
              // Build Mode v2: models get the ghost-placement flow (click to
              // stamp on the ground, repeat). Everything else keeps the classic
              // fixed-spawn behavior.
              if (type === 'model' && canBuild) {
                startPlacement({ kind: 'content', url, contentType: 'model', filename, label: filename });
                store.setPanel('showContentBrowser', false);
                return;
              }
              const id = `content-${Date.now()}`;
              const defaultScale = type === 'image' ? 3 : 1;
              // autoFit models only — measured + normalized on load by ContentDisplay.
              const item = { url, type, position: [0, 2, -2] as [number,number,number], scale: defaultScale, rotation: [0,0,0] as [number,number,number], visible: true, isLocked: false, ...(type === 'model' ? { autoFit: true } : {}), filename, uploadedAt: Date.now() };
              store.addContentItem(id, item);
              broadcastObjectAdd('content', id, item);
              store.setSelectedObjectId(id);
              store.setPanel('showContentBrowser', false);
              store.setPanel('showObjectsList', true);
            }}
            onUpload={() => { store.setPanel('showContentBrowser', false); store.setPanel('showContentPicker', true); }}
            selectedId={selectedObjectId} />
        )}

        {showCopilot && (
          <CopilotPanel worldId={worldId || ''} onClose={() => setShowCopilot(false)} />
        )}

        {showObjectLibrary && (
          <ObjectLibraryBrowser
            onClose={() => setShowObjectLibrary(false)}
            onAddVenue={(venueId) => {
              if (canBuild) {
                startPlacement({ kind: 'venue', venueId, label: VENUES.find((v) => v.id === venueId)?.title });
              } else {
                commitVenue(worldId, venueId, [0, -20], 0);
              }
              setShowObjectLibrary(false);
            }}
            onAddEnvironment={canBuild ? (asset) => {
              // A whole room from the library: added as an environment at its
              // authored size, floor at y = 0 (see environmentFit).
              const fit = environmentFit(asset);
              const id = `env-${Date.now()}`;
              store.addEnvironmentModel(id, { url: asset.url, transform: { position: fit.position, scale: fit.scale, rotation: [0, 0, 0], isLocked: false }, visible: true });
              store.setSelectedObjectId(id);
              store.setPanel('showEnvironmentControls', true);
              store.setPanel('showObjectsList', true);
              setShowObjectLibrary(false);
            } : undefined}
            onAddBuilding={(kind) => {
              const gen = generateBuilding(kind as BuildingKind);
              if (canBuild) {
                startPlacement({ kind: 'generated', buildingKind: kind, parts: gen.parts, label: gen.title });
              } else {
                spawnPartsGroup(worldId, gen.title, gen.parts, [0, -4], 0);
              }
              setShowObjectLibrary(false);
            }}
            onAddPrefab={(prefabId) => {
              if (canBuild) {
                // Ghost placement: click the ground to stamp, R to rotate.
                startPlacement({ kind: 'prefab', prefabId, label: PREFABS[prefabId]?.title });
              } else {
                commitPrefab(worldId, prefabId, [0, -2], 0);
              }
              setShowObjectLibrary(false);
            }}
            onAdd={(url, filename, fileType, opts) => {
              // Build Mode v2: arm the ghost instead of dropping the model at a
              // fixed spot — click on the ground to stamp it, R to spin, Esc to stop.
              if (canBuild) {
                startPlacement({ kind: 'content', url, contentType: (fileType as any) || 'model', filename, label: filename, ...opts });
                setShowObjectLibrary(false);
                return;
              }
              const id = `content-${Date.now()}`;
              // autoFit: library models come in arbitrary native units — let
              // ContentDisplay measure + normalize to a human-usable size on load.
              // Built-in props are authored in meters and may be seats/solid.
              const item = { url, type: fileType as 'model', position: [0, 1, -2] as [number, number, number], scale: 1, rotation: [0, 0, 0] as [number, number, number], visible: true, isLocked: false, autoFit: opts?.autoFit ?? true, ...(opts?.seat ? { seat: true } : {}), ...(opts?.collidable ? { collidable: true } : {}), filename, uploadedAt: Date.now() };
              store.addContentItem(id, item);
              broadcastObjectAdd('content', id, item);
              store.setSelectedObjectId(id);
              setShowObjectLibrary(false);
              store.setPanel('showObjectsList', true);
            }}
          />
        )}

        {!isInVR && <LockedNotice />}
        {marbleSoonAt > 0 && !isInVR && (
          <div key={marbleSoonAt} className="gx-soon-notice" role="status"><Sparkles size={14} /> <span>{MARBLE_COMING_SOON_MESSAGE}</span></div>
        )}
        {showWallDesigner && canBuild && !isInVR && (
          <WallDesigner worldId={worldId} onClose={() => setShowWallDesigner(false)} />
        )}
        {showMusicPanel && !isInVR && (
          <Suspense fallback={null}><WorldMusicPanel onClose={() => setShowMusicPanel(false)} /></Suspense>
        )}
        {showNpcCreator && (
          <NpcCreator
            onClose={() => setShowNpcCreator(false)}
            onPlace={(npc: NpcConfig) => {
              setShowNpcCreator(false);
              const st = useWorldStore.getState();
              st.setPlacementSpec({ kind: 'primitive', shape: 'npc', npc });
              st.setPlacementRotY(0);
              st.setBuildTool('place');
            }}
          />
        )}

        {showObjectGen && (
          <ObjectGenerator
            onClose={() => setShowObjectGen(false)}
            onObjectCreated={(glbUrl, name, thumbnailUrl) => {
              const id = `content-${Date.now()}`;
              const item = {
                url: glbUrl,
                type: 'model' as const,
                position: [0, 1.2, -2] as [number, number, number],
                rotation: [0, 0, 0] as [number, number, number],
                scale: 1,
                visible: true,
                isLocked: false,
                filename: name,
                thumbnailUrl,
                uploadedAt: Date.now(),
              };
              store.addContentItem(id, item);
              broadcastObjectAdd('content', id, item);
              store.setSelectedObjectId(id);
              store.setIsEditingEnvironment(true);
              store.setPanel('showEnvironmentControls', true);
              setShowObjectGen(false);
            }}
          />
        )}

        {MARBLE_ENABLED && showMarblePanel && (
          <MarbleWorldPanel
            onClose={() => setShowMarblePanel(false)}
            onWorldReady={(world, assets) => {
              const id = `marble-${Date.now()}`;
              setMarbleWorlds((prev) => [...prev, {
                id,
                name: world.display_name || 'AI World',
                splatUrl: assets.splatUrl,
                colliderUrl: assets.colliderUrl,
                position: [0, 0, 0],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
                groundPlaneOffset: assets.groundPlaneOffset,
                showCollider: false,
            isLocked: false,
              }]);
            }}
          />
        )}

        {showSkyboxManager && (
          <SkyboxManager skyboxes={skyboxes} currentSkyboxId={currentSkyboxId} canHq={isAppAdmin}
            mobileSkyboxUrl={store.mobileSkyboxUrl}
            onClose={() => store.setPanel('showSkyboxManager', false)}
            onSelect={(id) => { store.setCurrentSkyboxId(id); store.setPanel('showSkyboxManager', false); }}
            onDelete={(id) => store.deleteSkybox(id)}
            onUpload={() => { store.setPanel('showSkyboxManager', false); store.setPanel('showSkyboxPicker', true); }}
            onClearSkybox={() => store.setCurrentSkyboxId(null)}
            onMobileSkyboxChange={(url) => store.setMobileSkyboxUrl(url)}
            onSkyMeshUpload={(url, filename, mobileUrl) => {
              // A 3D sky is a content model flagged as environmentMesh, not a
              // skybox image — autoFit sizes it to enclose the world on load.
              const id = `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
              const item = {
                url, type: 'model' as const,
                position: [0, 0, 0] as [number, number, number],
                scale: 1, rotation: [0, 0, 0] as [number, number, number],
                visible: true, isLocked: false,
                collidable: false, environmentMesh: true, autoFit: true,
                mobileUrl,
                filename, uploadedAt: Date.now(),
              };
              store.addContentItem(id, item);
              broadcastObjectAdd('content', id, item);
              store.setPanel('showSkyboxManager', false);
            }} />
        )}

        {/* Environment Controls for selected environment model */}
        {showEnvironmentControls && !selectedObjectId && canBuild && (
          <div className="environment-controls">
            <div className="controls-header">
              <h3>Environment Controls</h3>
              <button className="icon-btn" onClick={() => store.setPanel('showEnvironmentControls', false)} title="Close controls"><X size={18} /></button>
            </div>
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              <p>Select an object or image to adjust its position, scale, and rotation.</p>
            </div>
          </div>
        )}

        {selectedObjectId && environmentModels[selectedObjectId] && showEnvironmentControls && canBuild && (
          <ObjectControls
            name={`Environment`}
            position={environmentModels[selectedObjectId].transform.position}
            scale={environmentModels[selectedObjectId].transform.scale}
            rotation={environmentModels[selectedObjectId].transform.rotation}
            isLocked={environmentModels[selectedObjectId].transform.isLocked}
            transformMode={transformMode}
            onTransformModeChange={setTransformMode}
            onPositionChange={(position) => store.updateEnvironmentTransform(selectedObjectId, { position })}
            onScaleChange={(scale) => store.updateEnvironmentTransform(selectedObjectId, { scale })}
            onRotationChange={(rotation) => store.updateEnvironmentTransform(selectedObjectId, { rotation })}
            onLockToggle={() => { const newLocked = !environmentModels[selectedObjectId].transform.isLocked; store.updateEnvironmentTransform(selectedObjectId, { isLocked: newLocked }); store.setIsEditingEnvironment(!newLocked); }}
            onReset={() => store.updateEnvironmentTransform(selectedObjectId, { position: [0, -0.5, 0], scale: 1, rotation: [0, 0, 0], isLocked: false })}
            onClose={() => { store.setPanel('showEnvironmentControls', false); store.setIsEditingEnvironment(false); }}
            onDelete={() => { store.deleteEnvironmentModel(selectedObjectId); store.setSelectedObjectId(null); store.setPanel('showEnvironmentControls', false); }}
          />
        )}

        {selectedObjectId && contentItems[selectedObjectId] && showEnvironmentControls && canBuild && (
          <ObjectControls
            name={contentItems[selectedObjectId].filename || 'Object'}
            position={contentItems[selectedObjectId].position}
            scale={contentItems[selectedObjectId].scale}
            rotation={contentItems[selectedObjectId].rotation}
            isLocked={contentItems[selectedObjectId].isLocked || false}
            transformMode={transformMode}
            onTransformModeChange={setTransformMode}
            onPositionChange={(position) => { if (!contentItems[selectedObjectId].isLocked) store.updateContentItem(selectedObjectId, { position }); }}
            onScaleChange={(scale) => { if (!contentItems[selectedObjectId].isLocked) store.updateContentItem(selectedObjectId, { scale }); }}
            onRotationChange={(rotation) => { if (!contentItems[selectedObjectId].isLocked) store.updateContentItem(selectedObjectId, { rotation }); }}
            onLockToggle={() => { const newLocked = !contentItems[selectedObjectId].isLocked; store.updateContentItem(selectedObjectId, { isLocked: newLocked }); broadcastObjectUpdate('content', selectedObjectId, { isLocked: newLocked }); store.setIsEditingEnvironment(!newLocked); }}
            collidable={contentItems[selectedObjectId].collidable || false}
            onCollidableChange={(collidable) => { store.updateContentItem(selectedObjectId, { collidable }); broadcastObjectUpdate('content', selectedObjectId, { collidable }); }}
            seat={contentItems[selectedObjectId].seat || false}
            onSeatChange={(seat) => { store.updateContentItem(selectedObjectId, { seat }); broadcastObjectUpdate('content', selectedObjectId, { seat }); }}
            environmentMesh={contentItems[selectedObjectId].environmentMesh || false}
            onEnvironmentMeshChange={(on) => {
              // Re-fit when switching modes: a sky wraps the world, a prop sits in it.
              const patch = { environmentMesh: on, autoFit: true } as any;
              store.updateContentItem(selectedObjectId, patch);
              broadcastObjectUpdate('content', selectedObjectId, patch);
            }}
            materialSlots={modelSlots[selectedObjectId]}
            materialColors={contentItems[selectedObjectId].materialColors}
            materialTextures={contentItems[selectedObjectId].materialTextures}
            onMaterialTextureChange={(slot, value) => {
              const cur = contentItems[selectedObjectId].materialTextures || {};
              const next = { ...cur };
              if (value) next[slot] = value; else delete next[slot];
              const patch = { materialTextures: Object.keys(next).length ? next : undefined };
              store.updateContentItem(selectedObjectId, patch);
              broadcastObjectUpdate('content', selectedObjectId, patch);
            }}
            onMaterialColorChange={(slot, color) => {
              const cur = contentItems[selectedObjectId].materialColors || {};
              const next = { ...cur };
              if (color) next[slot] = color; else delete next[slot];
              const patch = { materialColors: Object.keys(next).length ? next : undefined };
              store.updateContentItem(selectedObjectId, patch);
              broadcastObjectUpdate('content', selectedObjectId, patch);
            }}
            onReset={() => store.updateContentItem(selectedObjectId, { position: [0, 2, 0], scale: contentItems[selectedObjectId].type === 'image' ? 3 : 1, rotation: [0, 0, 0] })}
            onClose={() => { store.setPanel('showEnvironmentControls', false); store.setIsEditingEnvironment(false); }}
            onDelete={() => { store.deleteContentItem(selectedObjectId); broadcastObjectDelete('content', selectedObjectId); store.setSelectedObjectId(null); store.setPanel('showEnvironmentControls', false); }}
            onDuplicate={() => {
              const src = contentItems[selectedObjectId];
              const newId = `content-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
              const dupItem = { ...src, position: [src.position[0] + 1.5, src.position[1], src.position[2]] as [number,number,number] };
              store.addContentItem(newId, dupItem);
              broadcastObjectAdd('content', newId, dupItem);
              store.setSelectedObjectId(newId);
            }}
          />
        )}

        {selectedObjectId && screenProps[selectedObjectId] && showEnvironmentControls && canBuild && (
          <ObjectControls
            name={screenProps[selectedObjectId].type === 'browser' ? 'Browser' : screenProps[selectedObjectId].type === 'video' ? 'Video' : 'Screen'}
            position={screenProps[selectedObjectId].position}
            scale={screenProps[selectedObjectId].scale}
            rotation={screenProps[selectedObjectId].rotation}
            isLocked={screenProps[selectedObjectId].isLocked || false}
            transformMode={transformMode}
            onTransformModeChange={setTransformMode}
            onPositionChange={(position) => { if (!screenProps[selectedObjectId].isLocked) { store.updateScreenProp(selectedObjectId, { position }); broadcastObjectUpdate('screenprop', selectedObjectId, { position }); } }}
            onScaleChange={(scale) => { if (!screenProps[selectedObjectId].isLocked) { store.updateScreenProp(selectedObjectId, { scale }); broadcastObjectUpdate('screenprop', selectedObjectId, { scale }); } }}
            onRotationChange={(rotation) => { if (!screenProps[selectedObjectId].isLocked) { store.updateScreenProp(selectedObjectId, { rotation }); broadcastObjectUpdate('screenprop', selectedObjectId, { rotation }); } }}
            onLockToggle={() => { const newLocked = !screenProps[selectedObjectId].isLocked; store.updateScreenProp(selectedObjectId, { isLocked: newLocked }); broadcastObjectUpdate('screenprop', selectedObjectId, { isLocked: newLocked }); store.setIsEditingEnvironment(!newLocked); }}
            onReset={() => { const updates = { scale: 1, rotation: [0, 0, 0] as [number, number, number] }; store.updateScreenProp(selectedObjectId, updates); broadcastObjectUpdate('screenprop', selectedObjectId, updates); }}
            onClose={() => { store.setPanel('showEnvironmentControls', false); store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
            onDelete={() => { const prop = screenProps[selectedObjectId]; if (prop?.streamData) prop.streamData.getTracks().forEach((t: any) => t.stop()); store.deleteScreenProp(selectedObjectId); broadcastObjectDelete('screenprop', selectedObjectId); store.setSelectedObjectId(null); store.setPanel('showEnvironmentControls', false); }}
          />
        )}

        {(() => {
          if (!selectedObjectId?.startsWith('marble-')) return null;
          const mw = marbleWorlds.find((w) => w.id === selectedObjectId);
          if (!mw) return null;
          return (
            <ObjectControls
              name={`AI World: ${mw.name}`}
              position={mw.position}
              scale={mw.scale}
              rotation={mw.rotation}
              isLocked={mw.isLocked}
              transformMode={transformMode}
              onTransformModeChange={setTransformMode}
              onPositionChange={(position) => { if (!mw.isLocked) setMarbleWorlds((prev) => prev.map((w) => w.id === mw.id ? { ...w, position } : w)); }}
              onScaleChange={(scale) => { if (!mw.isLocked) setMarbleWorlds((prev) => prev.map((w) => w.id === mw.id ? { ...w, scale: [scale, scale, scale] as [number, number, number] } : w)); }}
              onRotationChange={(rotation) => { if (!mw.isLocked) setMarbleWorlds((prev) => prev.map((w) => w.id === mw.id ? { ...w, rotation } : w)); }}
              onLockToggle={() => setMarbleWorlds((prev) => prev.map((w) => w.id === mw.id ? { ...w, isLocked: !w.isLocked } : w))}
              onReset={() => setMarbleWorlds((prev) => prev.map((w) => w.id === mw.id ? { ...w, position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] } : w))}
              onClose={() => { store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
              onDelete={() => { setMarbleWorlds((prev) => prev.filter((w) => w.id !== mw.id)); store.setSelectedObjectId(null); store.setPanel('showEnvironmentControls', false); }}
            />
          );
        })()}

        {showObjectsList && (
          <SceneObjectsList objects={sceneObjects}
            multiSelectedCount={selectedObjectIds.length}
            onSelectObject={(id) => { store.clearMultiSelect(); store.setSelectedObjectId(id); store.setIsEditingEnvironment(true); if (id === 'spawn-point' || id.startsWith('env-') || id.startsWith('content-') || id.startsWith('screen-') || id.startsWith('light-') || id.startsWith('marble-')) store.setPanel('showEnvironmentControls', true); }}
            onMultiSelect={(id) => store.toggleMultiSelect(id)}
            onGroup={() => store.groupSelected()}
            onUngroup={() => store.ungroupSelected()}
            onReorder={(orderedIds) => store.setSceneOrder(orderedIds)}
            flashId={lockFlash?.id} flashAt={lockFlash?.at}
            onToggleLock={(id) => {
              const st = useWorldStore.getState();
              if (st.primitives[id]) { const v = !st.primitives[id].isLocked; st.updatePrimitive(id, { isLocked: v }); broadcastObjectUpdate('primitive', id, { isLocked: v }); }
              else if (st.contentItems[id]) { const v = !st.contentItems[id].isLocked; st.updateContentItem(id, { isLocked: v }); broadcastObjectUpdate('content', id, { isLocked: v }); }
              else if (st.lights[id]) { const v = !st.lights[id].isLocked; st.updateLight(id, { isLocked: v }); broadcastObjectUpdate('light', id, { isLocked: v }); }
              else if (st.screenProps[id]) { const v = !st.screenProps[id].isLocked; st.updateScreenProp(id, { isLocked: v }); broadcastObjectUpdate('screenprop', id, { isLocked: v }); }
              else if (st.environmentModels[id]) { st.updateEnvironmentTransform(id, { isLocked: !st.environmentModels[id].transform.isLocked }); }
            }}
            onDeleteObject={(id) => {
              if (id === 'spawn-point') return; // Spawn point cannot be deleted
              if (id === 'avatar-1') { if (window.confirm('Delete your avatar?')) { store.setAvatarUrl(null); store.setIsEditingEnvironment(false); } return; }
              const clearSel = () => { if (selectedObjectId === id) store.setSelectedObjectId(null); };
              // Look the object up in its actual collection — ids may be UUIDs
              // (worlds created via save_world, e.g. experiences) or prefixed
              // (builder-native), so don't rely on the id prefix.
              if (primitives[id]) { store.deletePrimitive(id); broadcastObjectDelete('primitive', id); clearSel(); }
              else if (contentItems[id]) { store.deleteContentItem(id); broadcastObjectDelete('content', id); clearSel(); }
              else if (lights[id]) { store.deleteLight(id); broadcastObjectDelete('light', id); clearSel(); }
              else if (screenProps[id]) { const prop = screenProps[id]; if (prop?.streamData) prop.streamData.getTracks().forEach((t) => t.stop()); store.deleteScreenProp(id); broadcastObjectDelete('screenprop', id); clearSel(); store.setPanel('showEnvironmentControls', false); }
              else if (environmentModels[id] || id.startsWith('env-')) { store.deleteEnvironmentModel(id); clearSel(); store.setPanel('showEnvironmentControls', false); }
              else if (id.startsWith('marble-')) { setMarbleWorlds((prev) => prev.filter((w) => w.id !== id)); clearSel(); store.setPanel('showEnvironmentControls', false); }
            }}
            onToggleVisibility={(id) => {
              if (primitives[id]) store.updatePrimitive(id, { visible: !primitives[id].visible });
              else if (contentItems[id]) store.updateContentItem(id, { visible: !contentItems[id].visible });
              else if (lights[id]) store.updateLight(id, { visible: !lights[id].visible });
              else if (screenProps[id]) { const updates = { visible: !screenProps[id].visible }; store.updateScreenProp(id, updates); broadcastObjectUpdate('screenprop', id, updates); }
              else if (environmentModels[id]) store.updateEnvironmentModel(id, { visible: !environmentModels[id].visible });
            }}
          />
        )}

        {showGroundControls && (
          <GroundControls
            size={groundSettings.size} color={groundSettings.color} textureUrl={groundSettings.textureUrl}
            texturePreset={groundSettings.texturePreset} textureScale={groundSettings.textureScale}
            showGrid={groundSettings.showGrid} isLocked={groundSettings.isLocked}
            onSizeChange={(size) => store.setGroundSettings({ size })}
            onColorChange={(color) => store.setGroundSettings({ color })}
            onTextureChange={(url) => store.setGroundSettings({ textureUrl: url, texturePreset: null, pbrMaps: null })}
            onPresetChange={(preset) => store.setGroundSettings({ texturePreset: preset, textureUrl: null, pbrMaps: null })}
            pbrMaps={groundSettings.pbrMaps || null}
            onPbrMapsChange={(maps) => store.setGroundSettings({ pbrMaps: maps, texturePreset: null, textureUrl: null })}
            onTextureScaleChange={(scale) => store.setGroundSettings({ textureScale: scale })}
            opacity={groundSettings.opacity ?? 1}
            onOpacityChange={(opacity) => store.setGroundSettings({ opacity })}
            groundVisible={groundVisible}
            onGroundVisibleToggle={() => store.setGroundVisible(!groundVisible)}
            onGridToggle={() => store.setGroundSettings({ showGrid: !groundSettings.showGrid })}
            onLockToggle={() => { const newLocked = !groundSettings.isLocked; store.setGroundSettings({ isLocked: newLocked }); store.setIsEditingEnvironment(!newLocked); }}
            onReset={() => { store.setGroundSettings({ size: 100, color: '#1a1a1a', textureUrl: null, texturePreset: null, textureScale: 10, showGrid: true, isLocked: true }); store.setIsEditingEnvironment(false); }}
            onClose={() => { store.setPanel('showGroundControls', false); store.setIsEditingEnvironment(false); store.setGroundSettings({ isLocked: true }); }}
            onUploadTexture={() => { sessionStorage.setItem('uploadingGroundTexture', 'true'); store.setPanel('showContentPicker', true); }}
          />
        )}

        {/* Primitive Controls */}
        {selectedObjectId && primitives[selectedObjectId] && (
          <PrimitiveControls
            primitive={primitives[selectedObjectId]}
            transformMode={transformMode}
            onTransformModeChange={setTransformMode}
            onUpdate={(updates) => { store.updatePrimitive(selectedObjectId, updates); broadcastObjectUpdate('primitive', selectedObjectId, updates); }}
            onDelete={() => { store.deletePrimitive(selectedObjectId); broadcastObjectDelete('primitive', selectedObjectId); store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
            onDuplicate={() => duplicateSelectedRef.current()}
            onClose={() => { store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
          />
        )}

        {/* Light Controls */}
        {selectedObjectId && lights[selectedObjectId] && (
          <LightControls
            light={lights[selectedObjectId]}
            onUpdate={(updates) => { store.updateLight(selectedObjectId, updates); broadcastObjectUpdate('light', selectedObjectId, updates); }}
            onDelete={() => { store.deleteLight(selectedObjectId); broadcastObjectDelete('light', selectedObjectId); store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
            onDuplicate={() => {
              const src = lights[selectedObjectId];
              const newId = `light-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
              const dupItem = { ...src, position: [src.position[0] + 2, src.position[1], src.position[2]] as [number,number,number], name: `${src.name} Copy` };
              store.addLight(newId, dupItem);
              broadcastObjectAdd('light', newId, dupItem);
              store.setSelectedObjectId(newId);
            }}
            onClose={() => { store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }}
          />
        )}

        {/* Spawn Point Controls */}
        {selectedObjectId === 'spawn-point' && canBuild && (
          <div className="light-controls" style={{ zIndex: 50 }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onWheel={(e) => e.stopPropagation()}
          >
            <div className="controls-header">
              <h3><MapPin size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Spawn Point</h3>
              <button className="icon-btn" onClick={() => { store.setSelectedObjectId(null); store.setIsEditingEnvironment(false); }} title="Close"><X size={18} /></button>
            </div>
            <div className="controls-scroll">
              <div className="control-group">
                <label>Position</label>
                {(['X', 'Y', 'Z'] as const).map((axis, i) => (
                  <div className="control-row" key={axis}>
                    <span>{axis}</span>
                    <input type="range" min={-50} max={50} step={0.5}
                      value={store.spawnPosition[i]}
                      onChange={(e) => {
                        const pos = [...store.spawnPosition] as [number, number, number];
                        pos[i] = +e.target.value;
                        store.setSpawnPosition(pos);
                      }}
                    />
                    <input type="number" className="value-input" step={0.5}
                      value={store.spawnPosition[i]}
                      onChange={(e) => {
                        const pos = [...store.spawnPosition] as [number, number, number];
                        pos[i] = +e.target.value;
                        store.setSpawnPosition(pos);
                      }}
                      style={{ width: 52, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: 'white', padding: '2px 6px', fontSize: 12, textAlign: 'right' }}
                    />
                  </div>
                ))}
              </div>
              <div className="control-group">
                <button
                  className="toggle-btn"
                  style={{ width: '100%', justifyContent: 'center', padding: '8px 0' }}
                  onClick={() => store.setSpawnPosition([0, 0, 0])}
                >Reset to Origin</button>
              </div>
              {store.worldBounds && (
                <div className="control-group">
                  <label>Walkable area</label>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
                    Players are kept inside X {store.worldBounds.min[0]}…{store.worldBounds.max[0]}, Z {store.worldBounds.min[1]}…{store.worldBounds.max[1]}.
                  </div>
                  <button
                    className="toggle-btn"
                    style={{ width: '100%', justifyContent: 'center', padding: '8px 0' }}
                    onClick={() => store.setWorldBounds(null)}
                    title="Remove the walkable area (takes effect when you save)"
                  >Let players roam anywhere</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Atmosphere Controls */}
        {showAtmosphereControls && (
          <AtmosphereControls onClose={() => store.setPanel('showAtmosphereControls', false)} />
        )}

        {/* World Settings Panel */}
        {showWorldSettings && (isOwner || role === 'host') && (
          <div className="world-settings-panel">
            <div className="settings-header">
              <h3>Multiplayer Settings</h3>
              <button className="btn-glass" onClick={() => setShowWorldSettings(false)} style={{ width: 24, height: 24, padding: 2 }}><X size={14} /></button>
            </div>
            <div className="settings-body">
              {isOwner && (
                <div className="settings-field">
                  <label>Max Players per Shard</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="number" min={1} max={50} value={maxPlayersInput} onChange={(e) => setMaxPlayersInput(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} />
                    <button className="settings-btn" onClick={updateMaxPlayers}>Apply</button>
                  </div>
                </div>
              )}

              {isOwner && (
                <div className="settings-field">
                  <label>Host Designation</label>
                  {user ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div className="settings-value" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                        {shardInfo?.hostUserId === user.id ? 'You are the host' : shardInfo?.hostUserId ? 'Another user is host' : 'No host set'}
                      </div>
                      <button className="settings-btn" onClick={designateAsHost}>
                        {shardInfo?.hostUserId === user.id ? 'Re-designate as Host' : 'Become Host'}
                      </button>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Host appears in all shards and can broadcast chat to everyone</div>
                    </div>
                  ) : (
                    <div className="settings-value" style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Sign in to become host</div>
                  )}
                </div>
              )}

              <div className="settings-field">
                <label>Shard Status</label>
                <div className="shard-status">
                  {shardInfo?.shards?.length ? shardInfo.shards.map((s: any) => (
                    <div key={s.shardIndex} className={`shard-badge ${s.shardIndex === 0 ? 'current' : ''}`}>
                      <span className="shard-name">Shard {s.shardIndex}</span>
                      <span className="shard-count">{s.players}/{s.maxPlayers}</span>
                    </div>
                  )) : (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>No active shards</div>
                  )}
                  {shardInfo && (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                      Total: {shardInfo.totalPlayers} player{shardInfo.totalPlayers !== 1 ? 's' : ''} across {shardInfo.shards?.length || 0} shard{(shardInfo.shards?.length || 0) !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Player Permissions */}
              <div className="settings-field">
                <label>Player Permissions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {connectedPlayers.filter(p => p.userId && p.userId !== user?.id).length === 0 ? (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>No other players connected</div>
                  ) : (
                    connectedPlayers.filter(p => p.userId && p.userId !== user?.id).map((p) => (
                      <div key={p.socketId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid var(--gx-border)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{p.userName}</span>
                          <span style={{ fontSize: 10, color: p.role === 'host' ? '#f59e0b' : p.role === 'builder' ? 'var(--gx-accent)' : 'rgba(255,255,255,0.4)' }}>
                            {p.role === 'host' ? 'Host' : p.role === 'builder' ? 'Builder' : 'Viewer'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            className="settings-btn"
                            style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => {
                              const newRole = p.role === 'builder' || p.role === 'host' ? 'viewer' : 'builder';
                              const channel = getChannel(worldId || '');
                              channel.send({
                                type: 'broadcast',
                                event: 'role:update',
                                payload: { targetUserId: p.userId, role: newRole },
                              });
                              // Update local player list
                              setConnectedPlayers(prev => prev.map(cp => cp.userId === p.userId ? { ...cp, role: newRole } : cp));
                              // Persist so the grant survives reload (loadWorld reads world_access)
                              const wid = useWorldStore.getState().actualWorldId;
                              if (wid && p.userId) {
                                const persist = newRole === 'builder'
                                  ? supabase.from('world_access').upsert({ world_id: wid, user_id: p.userId, role: 'invited' })
                                  : supabase.from('world_access').delete().eq('world_id', wid).eq('user_id', p.userId);
                                persist.then(({ error }) => {
                                  if (error) console.error('Failed to persist role grant:', error);
                                });
                              }
                            }}
                          >
                            {p.role === 'builder' || p.role === 'host' ? 'Revoke' : 'Grant Build'}
                          </button>
                          <button
                            className="settings-btn"
                            style={{ fontSize: 11, padding: '4px 10px', background: p.role === 'host' ? 'rgba(245, 158, 11, 0.15)' : undefined, borderColor: p.role === 'host' ? 'rgba(245, 158, 11, 0.3)' : undefined }}
                            onClick={() => {
                              const newRole = p.role === 'host' ? 'builder' : 'host';
                              const channel = getChannel(worldId || '');
                              channel.send({
                                type: 'broadcast',
                                event: 'role:update',
                                payload: { targetUserId: p.userId, role: newRole },
                              });
                              setConnectedPlayers(prev => prev.map(cp => cp.userId === p.userId ? { ...cp, role: newRole } : cp));
                            }}
                          >
                            {p.role === 'host' ? 'Remove Host' : 'Make Host'}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Builders can add/edit objects. Hosts get full edit access including settings.</div>
                </div>
              </div>

              {/* Lock World (owner only) */}
              {isOwner && <div className="settings-field">
                <label>Lock World</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: store.worldLocked ? '#ff3b30' : 'rgba(255,255,255,0.6)' }}>
                      {store.worldLocked ? 'World is locked — no one can join' : 'World is open'}
                    </span>
                    <button
                      className="settings-btn"
                      style={store.worldLocked ? { background: 'rgba(255, 59, 48, 0.15)', borderColor: 'rgba(255, 59, 48, 0.3)', color: '#ff3b30' } : {}}
                      onClick={() => {
                        const newLocked = !store.worldLocked;
                        store.setWorldLocked(newLocked);
                        // Broadcast lock state to all connected users
                        const channel = getChannel(worldId || '');
                        channel.send({
                          type: 'broadcast',
                          event: 'world:lock',
                          payload: { locked: newLocked },
                        });
                      }}
                    >
                      {store.worldLocked ? 'Unlock' : 'Lock Everyone Out'}
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>When locked, all non-owner users are removed and no one can join</div>
                </div>
              </div>}
            </div>
          </div>
        )}

        {/* Multiplayer + Resource HUD (bottom-left) */}
        <ResourceMeter multiplayerInfo={multiplayerInfo} primitiveCount={Object.keys(primitives).length} contentCount={Object.keys(contentItems).length} lightCount={Object.keys(lights).length} />

        {/* Online Users Bar — merged into BuilderToolbar */}

        {/* Knock notifications (owner sees when someone knocks on locked world) */}
        <KnockNotification worldId={worldId} />

        {/* Builder toolbar (shown for anyone with canBuild) */}
        {worldId && <BuilderToolbar worldId={worldId} />}

        {/* In-world emote wheel (play affordance for everyone; hidden in VR) */}
        {!isInVR && (
          <EmoteWheel
            onEmote={(name) => {
              store.playLocalEmote(name);
              const wid = useWorldStore.getState().actualWorldId;
              if (wid) broadcastEmote(wid, name);
            }}
          />
        )}

        {/* Controls panel (bottom-right) */}
        {avatarUrl && showControlsPanel && (
          <div style={{ position: 'absolute', bottom: '20px', right: '20px', padding: '16px', background: 'color-mix(in srgb, var(--gx-surface) 70%, transparent)', borderRadius: '16px', backdropFilter: 'blur(20px)', border: '1px solid var(--gx-border)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)', color: 'var(--gx-text)', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '240px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '-8px' }}>
              <div style={{ fontWeight: '600', color: 'color-mix(in srgb, var(--gx-accent) 70%, var(--gx-text) 30%)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '1px' }}>Controls</div>
              <button style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--gx-border)', borderRadius: '6px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.6)' }} onClick={() => store.setPanel('showControlsPanel', false)} title="Hide controls"><X size={16} /></button>
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>W/A/S/D - Move</div>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Shift - Run</div>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Mouse - Look around</div>
            <div style={{ fontWeight: '600', color: 'color-mix(in srgb, var(--gx-accent) 70%, var(--gx-text) 30%)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '1px', marginTop: '16px', marginBottom: '8px' }}>Camera</div>
            <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
              {(['normal', 'zoom', 'birdseye'] as const).map((mode) => (
                <button key={mode} style={{ padding: '8px 12px', background: cameraMode === mode ? 'rgba(0, 194, 179, 0.14)' : 'rgba(255, 255, 255, 0.05)', border: `1px solid ${cameraMode === mode ? 'rgba(0, 194, 179, 0.35)' : 'var(--gx-border)'}`, borderRadius: '8px', color: cameraMode === mode ? 'white' : 'rgba(255, 255, 255, 0.8)', cursor: 'pointer', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s' }}
                  onClick={() => store.setCameraMode(mode)}>
                  {mode === 'normal' ? <ZoomOut size={14} /> : mode === 'zoom' ? <ZoomIn size={14} /> : <Eye size={14} />}
                  {mode === 'normal' ? 'Normal' : mode === 'zoom' ? 'Zoom' : 'First Person'}
                </button>
              ))}
            </div>
            <button style={{ marginTop: '16px', width: '100%', padding: '10px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--gx-border)', borderRadius: '10px', color: 'rgba(255, 255, 255, 0.9)', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }} onClick={() => { if (window.confirm('Delete your avatar?')) { store.setAvatarUrl(null); store.setIsEditingEnvironment(false); } }}>Delete Avatar</button>
            <button style={{ marginTop: '4px', width: '100%', padding: '10px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--gx-border)', borderRadius: '10px', color: 'rgba(255, 255, 255, 0.9)', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }} onClick={() => store.setPanel('showAvatarSelector', true)}>Change Avatar</button>

            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--gx-border)' }}>

              {/* Animation menu — per-avatar clip library stored in the
                  animations record (__library) and synced to the wardrobe */}
              {(() => {
                let anims: any = {}; let lib: Record<string, Record<string, string>> = {};
                try { anims = JSON.parse(localStorage.getItem('eb-avatar-animations') || '{}') || {}; } catch {}
                lib = anims.__library || {};
                const rows: [string, string][] = [['Idle', 'idle'], ['Walk', 'walking']];
                return rows.map(([label, key]) => {
                  const options = Object.entries(lib[key] || {});
                  if (options.length === 0) return null;
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', width: 118 }}>{label} style</span>
                      <select
                        defaultValue={anims[key] || ''}
                        style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--gx-border)', borderRadius: 6, color: 'var(--gx-text, #fff)', fontSize: 12, padding: '4px 6px' }}
                        onChange={(e) => {
                          try {
                            const a = JSON.parse(localStorage.getItem('eb-avatar-animations') || '{}') || {};
                            a[key] = e.target.value;
                            localStorage.setItem('eb-avatar-animations', JSON.stringify(a));
                          } catch {}
                          savePoseAndAnimations().catch(() => {}).finally(() => window.location.reload());
                        }}>
                        {options.map(([name, url]) => (
                          <option key={name} value={url}>{name}</option>
                        ))}
                      </select>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {avatarUrl && !showControlsPanel && (
          <button style={{ position: 'absolute', bottom: '20px', right: '20px', width: '48px', height: '48px', background: 'rgba(15, 15, 25, 0.7)', borderRadius: '12px', backdropFilter: 'blur(20px)', border: '1px solid var(--gx-border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.8)' }} onClick={() => store.setPanel('showControlsPanel', true)} title="Show controls"><ChevronUp size={20} /></button>
        )}

        {!avatarUrl && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              maxWidth: 440, width: '90%', padding: '40px 36px',
              background: 'rgba(12, 22, 38, 0.95)',
              border: '1px solid rgba(0, 194, 179, 0.2)',
              borderRadius: 20, textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            }}>
              <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: '#e0e6ed' }}>Choose Your Avatar</h2>
              <p style={{ margin: '0 0 28px', color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Create or select an avatar to enter the world</p>
              <button className="btn-primary" onClick={() => store.setPanel('showAvatarSelector', true)} style={{
                width: '100%', padding: '14px', fontSize: 15, fontWeight: 600,
                background: 'var(--gx-gradient)', border: 'none', borderRadius: 12,
                color: 'white', cursor: 'pointer', marginBottom: 16,
              }}>Create Avatar with AI</button>
              <button onClick={() => { store.setAvatarUrl('skip'); localStorage.setItem('eb-avatar-url', 'skip'); }} style={{
                width: '100%', padding: '14px', fontSize: 14, fontWeight: 500,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', marginBottom: 20,
              }}>Enter Without Avatar</button>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Or paste an avatar GLB URL:</p>
                <input type="text" placeholder="https://example.com/my-avatar.glb" style={{
                  width: '100%', padding: '10px 14px', backgroundColor: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: 'white', fontSize: 13,
                }}
                  onKeyPress={(e) => { if (e.key === 'Enter') { const url = e.currentTarget.value.trim(); if (url && url.endsWith('.glb')) { store.setAvatarUrl(url); localStorage.setItem('eb-avatar-url', url); e.currentTarget.value = ''; } } }} />
                <small style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>Paste any .glb avatar URL and press Enter</small>
              </div>
            </div>
          </div>
        )}

        {showAvatarSelector && (
          <AvatarCreateModal
            variant="more"
            equip="always"
            returnPath="/avatars/new"
            finishLabel="Back to the editor"
            onFinish={() => store.setPanel('showAvatarSelector', false)}
            onClose={() => store.setPanel('showAvatarSelector', false)}
          />
        )}

        {/* Upload Modals — S3Upload renders its own full-screen overlay */}
        {showEnvironmentPicker && (
          <S3Upload
            show={showEnvironmentPicker}
            uploadType="environment"
            onSuccess={(result) => {
              if (result.filesUploaded?.length > 0) {
                const file = result.filesUploaded[0];
                const id = `env-${Date.now()}`;
                store.addEnvironmentModel(id, { url: file.url, transform: { position: [0, -0.5, 0], scale: 1, rotation: [0, 0, 0], isLocked: false }, visible: true });
                store.setSelectedObjectId(id);
                store.setPanel('showEnvironmentControls', true);
                store.setPanel('showObjectsList', true);
              }
              store.setPanel('showEnvironmentPicker', false);
            }}
            onClose={() => store.setPanel('showEnvironmentPicker', false)}
            onError={(error) => { console.error('Environment upload error:', error); alert('Failed to upload environment file.'); }}
          />
        )}

        {showSkyboxPicker && (
          <S3Upload
            show={showSkyboxPicker}
            uploadType="skybox"
            onSuccess={(result) => {
              if (result.filesUploaded?.length > 0) {
                const file = result.filesUploaded[0];
                const id = `skybox-${Date.now()}`;
                store.addSkybox(id, { url: file.url, filename: file.filename, uploadedAt: Date.now() });
                store.setCurrentSkyboxId(id);
                store.setPanel('showSkyboxManager', true);
              }
              store.setPanel('showSkyboxPicker', false);
            }}
            onClose={() => store.setPanel('showSkyboxPicker', false)}
            onError={(error) => { console.error('Skybox upload error:', error); alert('Failed to upload skybox.'); }}
          />
        )}

        {showContentPicker && (
          <S3Upload
            show={showContentPicker}
            uploadType="content"
            onSuccess={(result) => {
              if (result.filesUploaded?.length > 0) {
                const file = result.filesUploaded[0];
                const isGroundTexture = sessionStorage.getItem('uploadingGroundTexture') === 'true';
                sessionStorage.removeItem('uploadingGroundTexture');
                if (isGroundTexture) { store.setGroundSettings({ textureUrl: file.url }); store.setPanel('showContentPicker', false); return; }
                const id = `content-${Date.now()}`;
                let type: 'image' | 'audio' | 'model' | 'splat' | 'ply' = 'image';
                if (file.filename.match(/\.(mp3|wav|ogg|m4a)$/i)) type = 'audio';
                else if (file.filename.match(/\.(glb|gltf)$/i)) type = 'model';
                else if (file.filename.match(/\.(splat|spz|ply)$/i)) type = 'splat';
                const defaultScale = type === 'image' ? 3 : 1;
                const contentItem = { url: file.url, type, position: [0, 2, -2] as [number,number,number], scale: defaultScale, rotation: [0, 0, 0] as [number,number,number], visible: true, isLocked: false, filename: file.filename, uploadedAt: Date.now() };
                store.addContentItem(id, contentItem);
                broadcastObjectAdd('content', id, contentItem);
                store.setPanel('showObjectsList', true);
              }
              store.setPanel('showContentPicker', false);
            }}
            onClose={() => store.setPanel('showContentPicker', false)}
            onError={(error) => { console.error('Content upload error:', error); alert('Failed to upload content.'); }}
          />
        )}

        {showBrowserPropPicker && <PanelUrlPicker
          onClose={() => store.setPanel('showBrowserPropPicker', false)}
          onPlace={(browserUrl) => beginPanelPlacement({ type: 'browser', browserUrl })} />}

        {/* Voice Chat — delayed until world loads. NOT display:none: joining is
            a user gesture (browser audio policy), so hiding this hid the only
            Join Voice button — making voice, and therefore screen share,
            unreachable on desktop. `minimal` keeps the post-join UI to a
            floating mic toggle instead of the full panel. */}
        {userName && worldId && user && !store.worldLoading && (
          <VoiceChatWrapper roomName={worldId} userName={userName} minimal slotId={isInVR ? undefined : 'gx-voice-slot'} />
        )}
        {!isInVR && <RoomScreenDock />}

        {/* Auth Prompt */}
        {(showAuthPrompt || showVoiceChatPrompt) && (
          <div className="auth-modal-overlay" onClick={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); }}>
            <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="auth-modal-close" onClick={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); }}>×</button>
              {showVoiceChatPrompt && (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <AlertCircle size={48} color="#00c2b3" style={{ marginBottom: '16px' }} />
                  <h2>Sign in to use voice chat</h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '20px' }}>Voice chat requires an account for security and moderation.</p>
                </div>
              )}
              {showTimeWarning && (
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                  <AlertCircle size={48} color="#00c2b3" style={{ marginBottom: '16px' }} />
                  <h2>Your session is about to expire</h2>
                  <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '20px' }}>Sign in now to save your world and continue building!</p>
                </div>
              )}
              <AuthComponent onSuccess={() => { store.setPanel('showAuthPrompt', false); store.setPanel('showVoiceChatPrompt', false); store.setPanel('showTimeWarning', false); }} />
            </div>
          </div>
        )}

        {/* Temp User Banner */}
        {isTemp && !user && (
          <div style={{ position: 'fixed', top: '60px', left: '50%', transform: 'translateX(-50%)', padding: '8px 24px', background: 'rgba(0, 194, 179, 0.1)', border: '1px solid rgba(0, 194, 179, 0.3)', borderRadius: '20px', color: '#00c2b3', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', zIndex: 100 }}>
            <AlertCircle size={16} /> Temporary session - <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => store.setPanel('showAuthPrompt', true)}>Sign in to save</span>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div className="modal-overlay" onClick={() => store.setPanel('showShareModal', false)}>
            <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header"><h2>Share World</h2><button className="close-button" onClick={() => store.setPanel('showShareModal', false)}>×</button></div>
              <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>World Link {isLoadingShortURL && <span style={{ fontSize: '12px', color: 'rgba(0, 194, 179, 0.8)' }}>(Generating short link...)</span>}</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" value={isLoadingShortURL ? 'Generating short link...' : (shortenedURL || `${window.location.origin}/world/${actualWorldId}?name=${encodeURIComponent(worldName)}`)} readOnly style={{ flex: 1, padding: '10px', background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '8px', color: isLoadingShortURL ? 'rgba(255, 255, 255, 0.5)' : 'white', fontSize: '14px', fontFamily: 'monospace' }} onClick={(e) => !isLoadingShortURL && e.currentTarget.select()} />
                    <button className="btn-primary" disabled={isLoadingShortURL} onClick={(e) => { if (isLoadingShortURL) return; const urlToCopy = shortenedURL || `${window.location.origin}/world/${actualWorldId}?name=${encodeURIComponent(worldName)}`; navigator.clipboard.writeText(urlToCopy); const btn = e.currentTarget; btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy'; }, 2000); }} style={{ padding: '10px 20px', opacity: isLoadingShortURL ? 0.5 : 1 }}>Copy</button>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>Access Settings</label>
                  <div style={{ padding: '12px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px', fontSize: '14px' }}>
                    {isPublic ? (
                      <div style={{ color: '#4caf50' }}><Globe size={16} style={{ display: 'inline-block', marginRight: '8px' }} />Public - Anyone with the link can join</div>
                    ) : (
                      <div><div style={{ color: '#00c2b3', marginBottom: '8px' }}><Lock size={16} style={{ display: 'inline-block', marginRight: '8px' }} />Private - Access code required</div><div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Access Code: <strong>{accessCode}</strong></div></div>
                    )}
                  </div>
                </div>
                <div style={{ padding: '16px', background: 'rgba(0, 194, 179, 0.1)', border: '1px solid rgba(0, 194, 179, 0.3)', borderRadius: '8px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  <strong>What visitors can do:</strong>
                  <ul style={{ margin: '8px 0 0 20px', padding: 0 }}><li>View and explore the world</li><li>Use chat and voice chat</li><li>See all placed objects</li></ul>
                  <strong style={{ display: 'block', marginTop: '12px' }}>What visitors cannot do:</strong>
                  <ul style={{ margin: '8px 0 0 20px', padding: 0 }}><li>Edit or move objects</li><li>Upload new content</li><li>Change world settings</li><li>Save changes</li></ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Notification */}
        {saveNotification.show && (
          <div style={{ position: 'fixed', top: '80px', left: '50%', transform: 'translateX(-50%)', padding: '16px 24px', background: saveNotification.type === 'success' ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)', backdropFilter: 'blur(10px)', borderRadius: '12px', border: `1px solid ${saveNotification.type === 'success' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', color: 'white', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 1000, animation: 'slideDown 0.3s ease-out' }}>
            {saveNotification.type === 'success' ? (
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Save size={16} /></div>
            ) : <AlertCircle size={24} />}
            {saveNotification.message}
          </div>
        )}
        </>}
      </div>
    </>
  );
};

export default WorldBuilder;
